import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import RecruiterDashboard from './RecruiterDashboard';
import AgentDashboard from './AgentDashboard';
import FieldAgentDashboard from './FieldAgentDashboard';
import TelecallerDashboard from './TelecallerDashboard';
import AccountantDashboard from './AccountantDashboard';
import MarketingDashboard from './MarketingDashboard';
import HRExecutiveDashboard from './HRExecutiveDashboard';
import TargetWidget from '../components/TargetWidget';
import StaffChat from '../components/StaffChat';
import { fmtDate, fmtDateTime } from '../utils/dates';

const roleSummaries = {
  'HR Assistant': '',
  'Data Analyst': 'Build and maintain a clean company database for outreach and follow-up.',
  'HR Manager': 'Oversee hiring strategy, approvals, and team operations.',
  Recruiter: 'Manage sourcing, shortlisting, and interview pipelines.',
  'Assistant Recruiter': 'Support recruiter follow-up, sourcing coordination, and candidate pipeline updates.',
  Accountant: 'Support payroll, billing, and finance-related recruitment records.',
  Agent: 'Capture and manage member and company leads for the placement pipeline.',
  Telecaller: 'Call leads forwarded by agents, update call status and drive conversions.',
  Marketing: 'Collect and manage leads from social media, institutions, and other channels.',
  'HR Executive': 'Follow up on marketing leads, update status and complete registrations.',
  Staff: 'Access your assigned staff workspace and tasks.'
};

const getTodayEntryDate = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const year = today.getFullYear();
  return `${month}-${day}-${year}`;
};

const createInitialCompanyForm = () => ({
  id: null,
  entry_date: getTodayEntryDate(),
  company_name: '',
  category: '',
  location: '',
  district: '',
  contact_numbers: [''],
  whatsapp_number: '',
  email_id: '',
  website: '',
  google_map: '',
  photos: [],
  existing_photos: []
});

const createInitialFilters = () => ({
  category: '',
  location: '',
  district: '',
  contact_number: '',
  whatsapp_number: ''
});

const normalizeDigits = (value) => String(value || '').replace(/\D/g, '');

const getWhatsAppDigits = (value) => {
  const digits = normalizeDigits(value);
  if (!digits) return '';
  return digits.length === 10 ? `91${digits}` : digits;
};

const ContactLink = ({ value, type = 'call' }) => {
  const label = String(value || '').trim();
  if (!label) return '-';

  const digits = type === 'whatsapp' ? getWhatsAppDigits(label) : normalizeDigits(label);
  if (!digits) return label;

  const href = type === 'whatsapp' ? `https://wa.me/${digits}` : `tel:${digits}`;

  return (
    <a
      href={href}
      target={type === 'whatsapp' ? '_blank' : undefined}
      rel={type === 'whatsapp' ? 'noreferrer' : undefined}
      className="font-semibold text-blue-600 underline underline-offset-2"
    >
      {label}
    </a>
  );
};

const ContactList = ({ value, type = 'call' }) => {
  const items = String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
  if (items.length === 0) return '-';

  return (
    <div className="flex flex-col gap-1">
      {items.map((item) => (
        <ContactLink key={`${type}-${item}`} value={item} type={type} />
      ))}
    </div>
  );
};

const assistantRecruiterStatusOptions = ['Pending', 'No Vacancies', 'Rejected', 'Found Vacancies', 'Number Does Not Exist', 'Call Later'];
const availabilityStatusOptions = ['Pending', 'Available', 'Not Available'];
const interviewConfirmationOptions = ['Pending', 'Accepted', 'Rejected'];

const createInitialVacancyForm = () => ({
  vacancy_id: null,
  job_id: null,
  company_id: null,
  date_of_communication: getTodayEntryDate(),
  company_firm_organisation: '',
  job_title: '',
  department: '',
  section: '',
  salary: '',
  gender: '',
  age_limit: '',
  qualification: '',
  experience: '',
  place: '',
  district: '',
  location_map: '',
  total_vacancies: '',
  contact_person: '',
  requirement: ''
});

const createVacancyFormFromCompany = (company = {}) => ({
  ...createInitialVacancyForm(),
  company_id: company.id || null,
  date_of_communication: company.date_of_communication || getTodayEntryDate(),
  company_firm_organisation: '',
  place: company.location || '',
  district: company.district || '',
  location_map: company.google_map || ''
});

const createVacancyFormFromVacancy = (company = {}, vacancy = {}) => ({
  ...createVacancyFormFromCompany(company),
  vacancy_id: vacancy.id || null,
  job_id: vacancy.job_id || null,
  date_of_communication: vacancy.date_of_communication || company.date_of_communication || getTodayEntryDate(),
  company_firm_organisation: vacancy.company_firm_organisation || '',
  job_title: vacancy.job_title || '',
  department: vacancy.department || '',
  section: vacancy.section || '',
  salary: vacancy.salary || '',
  gender: vacancy.gender || '',
  age_limit: vacancy.age_limit || '',
  qualification: vacancy.qualification || '',
  experience: vacancy.experience || '',
  place: vacancy.place || company.location || '',
  district: vacancy.district || company.district || '',
  location_map: vacancy.location_map || company.google_map || '',
  total_vacancies: vacancy.total_vacancies || '',
  contact_person: vacancy.contact_person || '',
  requirement: vacancy.requirement || ''
});

const createInterviewCoordinationForm = (registration = {}) => ({
  candidate_availability: registration.candidate_availability || 'Pending',
  candidate_availability_note: registration.candidate_availability_note || '',
  hr_assistant_confirmation: registration.hr_assistant_confirmation || 'Pending'
});

const createCompanyCoordinationForm = (item = {}) => ({
  vacancy_availability: item.vacancy_availability || 'Pending',
  vacancy_availability_note: item.vacancy_availability_note || '',
  assistant_recruiter_confirmation: item.assistant_recruiter_confirmation || 'Pending',
  interview_date: item.interview_date || '',
  interview_time: item.interview_time || '',
  interview_location: item.interview_location || '',
  interview_mode: item.interview_mode || '',
  reporting_officer: item.reporting_officer || '',
  ar_interview_confirmed: Number(item.ar_interview_confirmed || 0)
});

const createInitialForwardFilters = () => ({
  name: '',
  place: '',
  contact_number: '',
  communication_status: 'all'
});

const createEmptyExperienceDetail = () => ({
  exp_department: '',
  title_post: '',
  company_name: '',
  salary: '',
  total_years: '',
  location: ''
});

const QUALIFICATION_OPTIONS = [
  'Certificate','Diploma','ITI','Plus Two / 12th','SSLC / 10th',
  'BA','B.Com','BBA','BCA','B.Ed','B.Sc','B.Sc Maths','B.Sc Physics','B.Sc Chemistry','B.Sc Computer Science','B.Sc Nursing','B.Sc Agriculture','B.Tech / B.E','MBBS','B.Pharm','BHM','BDS','LLB',
  'MA','M.Com','MBA','MCA','M.Ed','M.Sc','M.Sc Maths','M.Sc Physics','M.Sc Computer Science','M.Tech','MHM',
  'Ph.D','Post Doctoral','Other'
];

const EXAM_STATUS_OPTIONS = ['Passed','Failed','Pursuing','Waiting for the Examination','Discontinued'];

const KERALA_DISTRICTS_HR = ['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam','Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode','Wayanad','Kannur','Kasaragod'];

const HIGHLIGHT_LANGUAGE_OPTIONS = ['English','Hindi','Malayalam','Tamil','Kannada','Gujarati','Telugu','Marathi','Arabic','Urdu','Bengali','Punjabi','Other'];

const createTalentDetailForm = (registration = {}) => {
  const experienceDetails = Array.isArray(registration.experience_details) && registration.experience_details.length > 0
    ? registration.experience_details.map((item) => ({
        exp_department: item?.exp_department || '',
        title_post: item?.title_post || '',
        company_name: item?.company_name || '',
        salary: item?.salary || '',
        total_years: item?.total_years || '',
        location: item?.location || ''
      }))
    : [createEmptyExperienceDetail()];

  return {
    registration_number: registration.registration_number || '',
    date_of_communication: registration.date_of_communication || '',
    age: registration.age || '',
    google_location: registration.google_location || '',
    guardian_number: registration.guardian_number || '',
    contact_through: registration.contact_through || '',
    qualification_department: registration.qualification_department || '',
    completed_year: registration.completed_year || '',
    exam_status: registration.exam_status || '',
    preferred_district: Array.isArray(registration.preferred_district)
      ? registration.preferred_district
      : (registration.preferred_district ? registration.preferred_district.split(',').map(s => s.trim()).filter(Boolean) : []),
    preferred_location: registration.preferred_location || '',
    driving_license: registration.driving_license || '',
    license_category: registration.license_category || '',
    marital_status: registration.marital_status || '',
    highlight_language: Array.isArray(registration.highlight_language)
      ? registration.highlight_language
      : (registration.highlight_language ? registration.highlight_language.split(',').map(s => s.trim()).filter(Boolean) : []),
    job_priority: registration.job_priority || '',
    rating: registration.rating || '',
    how_you_heard: registration.how_you_heard || '',
    remarks: registration.remarks || '',
    additional_qualification: Array.isArray(registration.additional_qualification)
      ? registration.additional_qualification
      : (registration.additional_qualification ? registration.additional_qualification.split(',').map(s => s.trim()).filter(Boolean) : []),
    gender: registration.gender || '',
    experience_details: experienceDetails
  };
};

const recruiterEligibleStatuses = ['Registration Fee Paid', 'Prime Membership Fee Paid', 'Partner Member'];
const rejectedCommunicationStatuses = ['Black Listed', 'Job Not Required', 'Not Interested'];
const returnedCommunicationStatus = 'Registration Fee Returned';
const FORWARDED_SECTION_PAGE_SIZE = 10;

const getForwardedSectionKey = (registration) => {
  if (Number(registration.forwarded_to_recruiter_count || 0) > 0) {
    return 'forwarded';
  }

  if (rejectedCommunicationStatuses.includes(registration.communication_status)) {
    return 'rejected';
  }

  if (registration.communication_status === returnedCommunicationStatus) {
    return 'returned';
  }

  return 'pending';
};

const escapeExcelValue = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const SAVED_PAGE_SIZE = 25;

// ─── Toast Notification ────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [message, onClose]);

  if (!message) return null;

  const isError = type === 'error' || message.toLowerCase().includes('fail') || message.toLowerCase().includes('error');

  return (
    <div className={`fixed bottom-6 left-1/2 z-[9999] -translate-x-1/2 flex items-center gap-3 rounded-2xl px-5 py-3.5 shadow-2xl
      transition-all duration-300 animate-fadeInUp min-w-[260px] max-w-[400px]
      ${isError ? 'bg-red-600 text-white' : 'bg-black text-yellow-400'}`}
    >
      <span className="text-lg flex-shrink-0">{isError ? '✕' : '✓'}</span>
      <p className="text-sm font-bold flex-1">{message}</p>
      <button type="button" onClick={onClose} className="flex-shrink-0 text-xs opacity-60 hover:opacity-100 font-black">✕</button>
    </div>
  );
};

const KERALA_DISTRICTS_PL = ['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam','Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode','Wayanad','Kannur','Kasaragod'];
const PARTNER_LEAD_TYPES = ['Akshaya','CNC Center','Institute','Member','Councelor','Driving Agent','School','College','Job Consultancy','Exam Centers','Arts & Sports Club','Other'];

const createInitialPLForm = () => ({
  id: null,
  entry_date: getTodayEntryDate(),
  company_name: '',
  type_of_company: '',
  place: '',
  district: '',
  contact_numbers: [''],
  whatsapp_number: '',
  email_id: '',
  website: '',
});

const DataAnalystDashboard = ({ name, summary, API_URL }) => {
  const [activeTab, setActiveTab] = useState('entry');
  const [companyForm, setCompanyForm] = useState(createInitialCompanyForm);
  const [filters, setFilters] = useState(createInitialFilters);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [toast, setToast] = useState('');
  const [savedPage, setSavedPage] = useState(1);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Partner Leads state
  const [partnerLeads, setPartnerLeads] = useState([]);
  const [plLoading, setPlLoading] = useState(false);
  const [plSaving, setPlSaving] = useState(false);
  const [plDeletingId, setPlDeletingId] = useState(null);
  const [plFeedback, setPlFeedback] = useState('');
  const [plError, setPlError] = useState('');
  const [plForm, setPlForm] = useState(createInitialPLForm);
  const [plSubTab, setPlSubTab] = useState('entry');
  const [plPage, setPlPage] = useState(1);
  const [partnershipExecutives, setPartnershipExecutives] = useState([]);
  const [forwardModalId, setForwardModalId] = useState(null);
  const [forwardingToId, setForwardingToId] = useState('');
  const [forwardingLoading, setForwardingLoading] = useState(false);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data || []);
    } catch (err) {
      setCompanies([]);
      setFeedback(err?.response?.data?.error || 'Failed to fetch saved companies.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // ── Partner Leads handlers ──────────────────────────────────────────────────

  const fetchPartnerLeads = async () => {
    setPlLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/partner-leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerLeads(res.data || []);
    } catch (err) {
      setPartnerLeads([]);
    } finally {
      setPlLoading(false);
    }
  };

  const fetchPartnershipExecutives = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/partnership-executives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnershipExecutives(res.data || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (activeTab === 'partner-leads') {
      fetchPartnerLeads();
      fetchPartnershipExecutives();
    }
  }, [activeTab]); // eslint-disable-line

  const resetPLForm = () => setPlForm(createInitialPLForm());

  const handlePLFormChange = (e) => {
    const { name, value } = e.target;
    setPlForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePLContactChange = (idx, value) => {
    setPlForm((prev) => {
      const arr = [...prev.contact_numbers];
      arr[idx] = value;
      return { ...prev, contact_numbers: arr };
    });
  };

  const handleAddPLContact = () => {
    setPlForm((prev) => ({ ...prev, contact_numbers: [...prev.contact_numbers, ''] }));
  };

  const handleRemovePLContact = (idx) => {
    setPlForm((prev) => {
      const arr = prev.contact_numbers.filter((_, i) => i !== idx);
      return { ...prev, contact_numbers: arr.length > 0 ? arr : [''] };
    });
  };

  const handlePLSubmit = async (e) => {
    e.preventDefault();
    setPlSaving(true);
    setPlFeedback('');
    setPlError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...plForm,
        contact_numbers: plForm.contact_numbers.filter(Boolean),
      };
      let res;
      if (plForm.id) {
        res = await axios.put(`${API_URL}/api/staff/partner-leads/${plForm.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPartnerLeads((prev) => prev.map((l) => l.id === plForm.id ? res.data.lead : l));
        setPlFeedback('Partner lead updated successfully.');
      } else {
        res = await axios.post(`${API_URL}/api/staff/partner-leads`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPartnerLeads((prev) => [res.data.lead, ...prev]);
        setPlFeedback('Partner lead saved successfully.');
      }
      resetPLForm();
      setPlSubTab('saved');
    } catch (err) {
      setPlError(err?.response?.data?.error || 'Failed to save partner lead.');
    } finally {
      setPlSaving(false);
    }
  };

  const handleEditPL = (lead) => {
    setPlForm({
      id: lead.id,
      entry_date: lead.entry_date || '',
      company_name: lead.company_name || '',
      type_of_company: lead.type_of_company || '',
      place: lead.place || '',
      district: lead.district || '',
      contact_numbers: Array.isArray(lead.contact_numbers) && lead.contact_numbers.length > 0 ? lead.contact_numbers : [''],
      whatsapp_number: lead.whatsapp_number || '',
      email_id: lead.email_id || '',
      website: lead.website || '',
    });
    setPlFeedback('Editing partner lead.');
    setPlError('');
    setPlSubTab('entry');
  };

  const handleDeletePL = async (id) => {
    if (!window.confirm('Delete this partner lead?')) return;
    setPlDeletingId(id);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/staff/partner-leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerLeads((prev) => prev.filter((l) => l.id !== id));
      if (plForm.id === id) resetPLForm();
      setPlFeedback('Partner lead deleted.');
    } catch (err) {
      setPlFeedback(err?.response?.data?.error || 'Failed to delete partner lead.');
    } finally {
      setPlDeletingId(null);
    }
  };

  const handleForwardPL = async () => {
    if (!forwardingToId) return;
    setForwardingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API_URL}/api/staff/partner-leads/${forwardModalId}/forward`, { forwarded_to: forwardingToId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartnerLeads((prev) => prev.map((l) => l.id === forwardModalId ? res.data.lead : l));
      setPlFeedback(`Forwarded to ${res.data.lead.forwarded_to_name}.`);
      setForwardModalId(null);
      setForwardingToId('');
    } catch (err) {
      setPlFeedback(err?.response?.data?.error || 'Failed to forward lead.');
    } finally {
      setForwardingLoading(false);
    }
  };

  const PL_PAGE_SIZE = 10;
  const plTotalPages = Math.max(1, Math.ceil(partnerLeads.length / PL_PAGE_SIZE));
  const paginatedLeads = partnerLeads.slice((plPage - 1) * PL_PAGE_SIZE, plPage * PL_PAGE_SIZE);

  // ── End Partner Leads handlers ──────────────────────────────────────────────

  const filteredCompanies = useMemo(() => {
    return companies.filter((company) => {
      const categoryMatch = String(company.category || '').toLowerCase().includes(filters.category.toLowerCase());
      const locationMatch = String(company.location || '').toLowerCase().includes(filters.location.toLowerCase());
      const districtMatch = String(company.district || '').toLowerCase().includes(filters.district.toLowerCase());
      const contactMatch = String(company.contact_numbers_display || '').toLowerCase().includes(filters.contact_number.toLowerCase());
      const whatsappMatch = String(company.whatsapp_number || '').toLowerCase().includes(filters.whatsapp_number.toLowerCase());

      return categoryMatch && locationMatch && districtMatch && contactMatch && whatsappMatch;
    });
  }, [companies, filters]);

  const totalContacts = useMemo(
    () => companies.reduce((sum, company) => sum + (Array.isArray(company.contact_numbers) ? company.contact_numbers.length : 0), 0),
    [companies]
  );

  const distinctDistricts = useMemo(() => new Set(companies.map((company) => company.district).filter(Boolean)).size, [companies]);

  const existingCompanyNumbers = useMemo(() => new Set(
    companies
      .filter((c) => c.id !== companyForm.id)
      .flatMap((c) => Array.isArray(c.contact_numbers) ? c.contact_numbers : [])
      .concat(companies.filter((c) => c.id !== companyForm.id).map((c) => normalizeDigits(c.whatsapp_number)).filter(Boolean))
      .map((n) => normalizeDigits(n))
      .filter(Boolean)
  ), [companies, companyForm.id]);

  const savedTotalPages = Math.max(1, Math.ceil(filteredCompanies.length / SAVED_PAGE_SIZE));
  const paginatedCompanies = useMemo(
    () => filteredCompanies.slice((savedPage - 1) * SAVED_PAGE_SIZE, savedPage * SAVED_PAGE_SIZE),
    [filteredCompanies, savedPage]
  );

  const handleDownloadTemplate = () => {
    const headers = [['Company Name', 'Category', 'Location', 'District', 'Contact Numbers', 'Whatsapp Number', 'Email ID', 'Website', 'Google Map']];
    const workbook = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charSet="utf-8" /></head>
        <body>
          <table>
            <thead>
              <tr>${headers[0].map(h => `<th>${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              <tr><td>Example Company Ltd</td><td>IT</td><td>Kochi</td><td>Ernakulam</td><td>9876543210</td><td>9876543210</td><td>hr@example.com</td><td>https://example.com</td><td>https://maps.google.com/...</td></tr>
            </tbody>
          </table>
        </body>
      </html>`;
    const blob = new Blob([workbook], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'company-import-template.xls';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API_URL}/api/staff/companies/import`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImportResult(res.data);
      if (res.data.inserted > 0) {
        await fetchCompanies();
      }
    } catch (err) {
      setImportResult({ error: err?.response?.data?.error || 'Import failed.' });
    } finally {
      setImportLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'whatsapp_number') {
      setCompanyForm((prev) => ({ ...prev, whatsapp_number: normalizeDigits(value).slice(0, 13) }));
      return;
    }
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactNumberChange = (index, value) => {
    setCompanyForm((prev) => ({
      ...prev,
      contact_numbers: prev.contact_numbers.map((contact, contactIndex) => (
        contactIndex === index ? normalizeDigits(value).slice(0, 13) : contact
      ))
    }));
  };

  const handleAddContactNumber = () => {
    setCompanyForm((prev) => ({ ...prev, contact_numbers: [...prev.contact_numbers, ''] }));
  };

  const handleRemoveContactNumber = (index) => {
    setCompanyForm((prev) => {
      const nextContactNumbers = prev.contact_numbers.filter((_, contactIndex) => contactIndex !== index);
      return {
        ...prev,
        contact_numbers: nextContactNumbers.length > 0 ? nextContactNumbers : ['']
      };
    });
  };

  const resetCompanyForm = () => {
    setCompanyForm(createInitialCompanyForm());
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();

    const cleanedContactNumbers = companyForm.contact_numbers
      .map((value) => normalizeDigits(value))
      .filter(Boolean);

    if (cleanedContactNumbers.length === 0) {
      alert('Add at least one contact number.');
      return;
    }

    if (cleanedContactNumbers.some((value) => value.length < 10 || value.length > 13)) {
      alert('Each contact number must be between 10 and 13 digits.');
      return;
    }

    if (companyForm.whatsapp_number && (normalizeDigits(companyForm.whatsapp_number).length < 10 || normalizeDigits(companyForm.whatsapp_number).length > 13)) {
      alert('WhatsApp number must be between 10 and 13 digits.');
      return;
    }

    const uniqueContactNumbers = new Set(cleanedContactNumbers);
    if (uniqueContactNumbers.size !== cleanedContactNumbers.length) {
      alert('Duplicate contact numbers are not allowed.');
      return;
    }

    const cleanedWhatsapp = normalizeDigits(companyForm.whatsapp_number);

    const crossDuplicate = cleanedContactNumbers.find((n) => existingCompanyNumbers.has(n));
    if (crossDuplicate) {
      alert(`Contact number ${crossDuplicate} is already used by another company.`);
      return;
    }
    if (cleanedWhatsapp && existingCompanyNumbers.has(cleanedWhatsapp)) {
      alert(`WhatsApp number ${cleanedWhatsapp} is already used by another company.`);
      return;
    }

    const fd = new FormData();
    fd.append('company_name', companyForm.company_name);
    fd.append('category', companyForm.category);
    fd.append('location', companyForm.location);
    fd.append('district', companyForm.district);
    fd.append('contact_numbers', JSON.stringify(cleanedContactNumbers));
    fd.append('whatsapp_number', normalizeDigits(companyForm.whatsapp_number));
    fd.append('email_id', companyForm.email_id);
    fd.append('website', companyForm.website);
    fd.append('google_map', companyForm.google_map);
    fd.append('existing_photos', JSON.stringify(companyForm.existing_photos || []));
    (companyForm.photos || []).forEach((file) => fd.append('photos', file));

    setSaving(true);
    setFeedback('');

    try {
      const token = localStorage.getItem('token');
      if (companyForm.id) {
        const res = await axios.put(`${API_URL}/api/staff/companies/${companyForm.id}`, fd, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies((prev) => prev.map((company) => (company.id === companyForm.id ? res.data.company : company)));
        setToast('Company updated successfully.');
      } else {
        const res = await axios.post(`${API_URL}/api/staff/companies`, fd, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies((prev) => [res.data.company, ...prev]);
        setToast('Company entry saved successfully.');
      }

      resetCompanyForm();
      setActiveTab('saved');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to save company details.');
    } finally {
      setSaving(false);
    }
  };

  const handleEditCompany = (company) => {
    setCompanyForm({
      id: company.id,
      entry_date: company.entry_date || getTodayEntryDate(),
      company_name: company.company_name || '',
      category: company.category || '',
      location: company.location || '',
      district: company.district || '',
      contact_numbers: Array.isArray(company.contact_numbers) && company.contact_numbers.length > 0 ? company.contact_numbers : [''],
      whatsapp_number: company.whatsapp_number || '',
      email_id: company.email_id || '',
      website: company.website || '',
      google_map: company.google_map || '',
      photos: [],
      existing_photos: Array.isArray(company.photos) ? company.photos : []
    });
    setFeedback('Editing saved company details.');
    setActiveTab('entry');
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Delete this saved company?')) return;

    setDeletingId(companyId);
    setFeedback('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/staff/companies/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies((prev) => prev.filter((company) => company.id !== companyId));
      if (companyForm.id === companyId) {
        resetCompanyForm();
      }
      setFeedback('Company deleted successfully.');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to delete company.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setSavedPage(1);
  };

  const handleClearFilters = () => {
    setFilters(createInitialFilters());
    setSavedPage(1);
  };

  const handleExportToExcel = () => {
    if (filteredCompanies.length === 0) {
      alert('No saved companies available for export.');
      return;
    }

    const rows = filteredCompanies.map((company) => `
      <tr>
        <td>${escapeExcelValue(company.entry_date)}</td>
        <td>${escapeExcelValue(company.company_name)}</td>
        <td>${escapeExcelValue(company.category)}</td>
        <td>${escapeExcelValue(company.location)}</td>
        <td>${escapeExcelValue(company.district)}</td>
        <td>${escapeExcelValue(company.contact_numbers_display)}</td>
        <td>${escapeExcelValue(company.whatsapp_number)}</td>
        <td>${escapeExcelValue(company.email_id)}</td>
        <td>${escapeExcelValue(company.website)}</td>
        <td>${escapeExcelValue(company.google_map)}</td>
      </tr>
    `).join('');

    const workbook = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charSet="utf-8" />
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th>Date of Entry</th>
                <th>Company Name</th>
                <th>Category</th>
                <th>Location</th>
                <th>District</th>
                <th>Contact Numbers</th>
                <th>Whatsapp Number</th>
                <th>Email ID</th>
                <th>Website</th>
                <th>Google Map</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([workbook], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `saved-companies-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-accent px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1920px] rounded-[28px] border border-yellow-200 bg-white shadow-[0_25px_80px_rgba(0,0,0,0.08)]">
        <div className="rounded-t-[28px] bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.32),transparent_28%),linear-gradient(135deg,#070707_0%,#1e1e1e_54%,#5b4600_100%)] px-5 py-6 text-white md:px-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.34em] text-yellow-300">Staff Dashboard</p>
          <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl">Data Analyst Company Tracker</h1>
              <p className="mt-2 text-sm leading-7 text-white/80 md:text-base">{summary}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">Saved Companies</p>
                <p className="mt-2 text-3xl font-black">{companies.length}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">Contact Points</p>
                <p className="mt-2 text-3xl font-black">{totalContacts}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">Districts</p>
                <p className="mt-2 text-3xl font-black">{distinctDistricts}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-200 px-5 py-4 md:px-8">
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => setActiveTab('entry')}
              className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                activeTab === 'entry' ? 'bg-black text-yellow-300' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
              }`}
            >
              Company Entry
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('saved')}
              className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                activeTab === 'saved' ? 'bg-black text-yellow-300' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
              }`}
            >
              Saved Companies
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('partner-leads'); setPlFeedback(''); }}
              className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                activeTab === 'partner-leads' ? 'bg-yellow-500 text-black' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
              }`}
            >
              Partners Lead
            </button>
          </div>
        </div>

        {feedback && (
          <div className="mx-5 mt-5 rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm font-semibold text-gray-800 md:mx-8">
            {feedback}
          </div>
        )}

        <div className="px-5 pt-5 md:px-8">
          <TargetWidget API_URL={API_URL} />
        </div>

        {activeTab === 'entry' && (
          <div className="px-5 py-6 md:px-8">
            <form onSubmit={handleCompanySubmit} className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:p-6">
              <div className="flex flex-col gap-2 border-b border-gray-200 pb-5">
                <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-600">Entry Form</p>
                <h2 className="text-2xl font-black text-black">{companyForm.id ? 'Edit Company Details' : 'Add New Company'}</h2>
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <label className="block md:col-span-2 xl:col-span-3">
                  <span className="text-sm font-bold text-black">Date of Entry</span>
                  <input
                    value={companyForm.entry_date}
                    readOnly
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-100 px-4 py-3 text-black outline-none"
                  />
                </label>

                <label className="block md:col-span-2 xl:col-span-3">
                  <span className="text-sm font-bold text-black">Company Name</span>
                  <input
                    name="company_name"
                    value={companyForm.company_name}
                    onChange={handleFormChange}
                    placeholder="Enter company name"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-bold text-black">Category</span>
                  <input
                    name="category"
                    value={companyForm.category}
                    onChange={handleFormChange}
                    placeholder="IT, Retail, Healthcare..."
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-bold text-black">Location</span>
                  <input
                    name="location"
                    value={companyForm.location}
                    onChange={handleFormChange}
                    placeholder="Town or city"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-bold text-black">District</span>
                  <select
                    name="district"
                    value={companyForm.district}
                    onChange={handleFormChange}
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    required
                  >
                    <option value="">Select district...</option>
                    {KERALA_DISTRICTS_PL.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>

                <div className="block">
                  <span className="text-sm font-bold text-black">Whatsapp Number</span>
                  {(() => {
                    const waVal = companyForm.whatsapp_number;
                    const isWaDuplicate = waVal.length >= 10 && existingCompanyNumbers.has(waVal);
                    return (
                      <>
                        <input
                          name="whatsapp_number"
                          value={waVal}
                          onChange={handleFormChange}
                          placeholder="10-13 digit whatsapp number"
                          maxLength={13}
                          className={`mt-1.5 w-full rounded-2xl border px-4 py-3 text-black outline-none focus:ring-2 ${isWaDuplicate ? 'border-red-400 bg-red-50 focus:border-red-400 focus:ring-red-200' : 'border-gray-300 bg-gray-50 focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200'}`}
                        />
                        {isWaDuplicate && (
                          <p className="mt-1 ml-1 text-xs font-semibold text-red-500">⚠ Number already exists</p>
                        )}
                      </>
                    );
                  })()}
                </div>

                <label className="block">
                  <span className="text-sm font-bold text-black">Email ID</span>
                  <input
                    type="email"
                    name="email_id"
                    value={companyForm.email_id}
                    onChange={handleFormChange}
                    placeholder="company@example.com"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-bold text-black">Website</span>
                  <input
                    name="website"
                    value={companyForm.website}
                    onChange={handleFormChange}
                    placeholder="https://company.com"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                  />
                </label>

                <label className="block md:col-span-2 xl:col-span-3">
                  <span className="text-sm font-bold text-black">Google Map</span>
                  <input
                    name="google_map"
                    value={companyForm.google_map}
                    onChange={handleFormChange}
                    placeholder="Paste Google Maps URL"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                  />
                </label>

                <div className="block md:col-span-2 xl:col-span-3">
                  <span className="text-sm font-bold text-black">Photos <span className="text-xs font-normal text-gray-400">(optional · max 3 · JPG only)</span></span>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {(companyForm.existing_photos || []).map((photo, idx) => (
                      <div key={`existing-${idx}`} className="relative h-20 w-20 overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
                        <img
                          src={`${API_URL}/${photo}`}
                          alt={`Photo ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setCompanyForm((prev) => ({ ...prev, existing_photos: prev.existing_photos.filter((_, i) => i !== idx) }))}
                          className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {(companyForm.photos || []).map((file, idx) => (
                      <div key={`new-${idx}`} className="relative h-20 w-20 overflow-hidden rounded-xl border border-yellow-300 bg-yellow-50">
                        <img
                          src={URL.createObjectURL(file)}
                          alt={`New ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => setCompanyForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))}
                          className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {((companyForm.existing_photos || []).length + (companyForm.photos || []).length) < 3 && (
                      <label className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 transition hover:border-yellow-400 hover:text-yellow-600">
                        <span className="text-2xl leading-none">+</span>
                        <span className="mt-1 text-[10px] font-semibold">Add Photo</span>
                        <input
                          type="file"
                          accept=".jpg,.jpeg"
                          multiple
                          className="hidden"
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            setCompanyForm((prev) => {
                              const remaining = 3 - (prev.existing_photos || []).length - (prev.photos || []).length;
                              return { ...prev, photos: [...(prev.photos || []), ...files.slice(0, remaining)] };
                            });
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-[28px] border border-dashed border-yellow-300 bg-yellow-50/70 p-4 md:p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-extrabold uppercase tracking-[0.22em] text-yellow-700">Contact Numbers</p>
                    <p className="mt-1 text-sm text-gray-600">Add one or more company contact numbers.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddContactNumber}
                    className="rounded-xl bg-black px-4 py-2 text-sm font-extrabold text-yellow-300 transition hover:bg-yellow-400 hover:text-black"
                  >
                    Add Number
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {companyForm.contact_numbers.map((contactNumber, index) => {
                    const isDuplicateInForm = contactNumber.length >= 10 && companyForm.contact_numbers.some((n, i) => i !== index && n === contactNumber);
                    const isDuplicateInDB = contactNumber.length >= 10 && existingCompanyNumbers.has(contactNumber);
                    const isDuplicate = isDuplicateInForm || isDuplicateInDB;
                    return (
                      <div key={`${index}-${companyForm.id || 'new'}`} className="flex flex-col gap-1">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                          <input
                            value={contactNumber}
                            onChange={(e) => handleContactNumberChange(index, e.target.value)}
                            placeholder={`Contact Number ${index + 1}`}
                            maxLength={13}
                            className={`w-full rounded-2xl border px-4 py-3 text-black outline-none focus:ring-2 ${isDuplicate ? 'border-red-400 bg-red-50 focus:border-red-400 focus:ring-red-200' : 'border-gray-300 bg-white focus:border-yellow-400 focus:ring-yellow-200'}`}
                            required={index === 0}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveContactNumber(index)}
                            className="rounded-xl border border-red-200 px-4 py-3 text-sm font-bold text-red-600 transition hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                        {isDuplicate && (
                          <p className="ml-1 text-xs font-semibold text-red-500">⚠ Number already exists in another company</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-gray-600">Required fields: Company Name, Category, Location, District, and at least one contact number.</p>
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetCompanyForm}
                    className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-xl bg-yellow-400 px-6 py-3 text-sm font-extrabold text-black transition hover:bg-yellow-500 disabled:opacity-60"
                  >
                    {saving ? 'Saving...' : companyForm.id ? 'Update Company' : 'Save Company'}
                  </button>
                </div>
              </div>
            </form>

          </div>
        )}

        {activeTab === 'saved' && (
          <div className="px-5 py-6 md:px-8">
            <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:p-6">
              <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-600">Saved Section</p>
                  <h2 className="mt-2 text-2xl font-black text-black">Saved Company Details</h2>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={fetchCompanies}
                    className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
                  >
                    Refresh
                  </button>
                  <button
                    type="button"
                    onClick={() => { setImportFile(null); setImportResult(null); setImportModalOpen(true); }}
                    className="rounded-xl border border-yellow-400 bg-yellow-50 px-5 py-3 text-sm font-extrabold text-yellow-700 transition hover:bg-yellow-400 hover:text-black"
                  >
                    Import from Excel
                  </button>
                  <button
                    type="button"
                    onClick={handleExportToExcel}
                    className="rounded-xl bg-black px-5 py-3 text-sm font-extrabold text-yellow-300 transition hover:bg-yellow-400 hover:text-black"
                  >
                    Export To Excel
                  </button>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
                <input
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  placeholder="Filter by Category"
                  className="rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                />
                <input
                  name="location"
                  value={filters.location}
                  onChange={handleFilterChange}
                  placeholder="Filter by Location"
                  className="rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                />
                <input
                  name="district"
                  value={filters.district}
                  onChange={handleFilterChange}
                  placeholder="Filter by District"
                  className="rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                />
                <input
                  name="contact_number"
                  value={filters.contact_number}
                  onChange={handleFilterChange}
                  placeholder="Filter by Contact Number"
                  className="rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                />
                <div className="flex gap-3">
                  <input
                    name="whatsapp_number"
                    value={filters.whatsapp_number}
                    onChange={handleFilterChange}
                    placeholder="Filter by Whatsapp"
                    className="w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                  />
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="rounded-2xl border border-gray-300 px-4 py-3 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-600">
                  Showing {filteredCompanies.length === 0 ? 0 : (savedPage - 1) * SAVED_PAGE_SIZE + 1}–{Math.min(savedPage * SAVED_PAGE_SIZE, filteredCompanies.length)} of {filteredCompanies.length} saved companies.
                </p>
              </div>

              {loading ? (
                <div className="mt-6 text-sm text-gray-600">Loading saved companies...</div>
              ) : (
                <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="min-w-full bg-white text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company Name</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Category</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Location</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">District</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contact Numbers</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Whatsapp</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Email</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Website</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Google Map</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Photos</th>
                        <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCompanies.length === 0 && (
                        <tr>
                          <td colSpan="12" className="px-3 py-6 text-center text-gray-500">No saved companies matched the current filters.</td>
                        </tr>
                      )}
                      {paginatedCompanies.map((company) => (
                        <tr key={company.id} className="align-top">
                          <td className="border-b px-3 py-3 text-gray-700">{company.entry_date || '-'}</td>
                          <td className="border-b px-3 py-3 font-semibold text-black">{company.company_name || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.category || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.location || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.district || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.contact_numbers_display || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.whatsapp_number || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">{company.email_id || '-'}</td>
                          <td className="border-b px-3 py-3 text-gray-700">
                            {company.website ? (
                              <a href={company.website} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                                Open
                              </a>
                            ) : '-'}
                          </td>
                          <td className="border-b px-3 py-3 text-gray-700">
                            {company.google_map ? (
                              <a href={company.google_map} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                                Open Map
                              </a>
                            ) : '-'}
                          </td>
                          <td className="border-b px-3 py-3">
                            {Array.isArray(company.photos) && company.photos.length > 0 ? (
                              <div className="flex gap-1">
                                {company.photos.map((photo, idx) => (
                                  <a key={idx} href={`${API_URL}/${photo}`} target="_blank" rel="noreferrer">
                                    <img
                                      src={`${API_URL}/${photo}`}
                                      alt={`Photo ${idx + 1}`}
                                      className="h-10 w-10 rounded object-cover border border-gray-200 hover:opacity-80 transition"
                                    />
                                  </a>
                                ))}
                              </div>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="border-b px-3 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditCompany(company)}
                                className="rounded-lg border border-blue-500 px-3 py-1.5 text-xs font-bold text-blue-600 transition hover:bg-blue-50"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteCompany(company.id)}
                                disabled={deletingId === company.id}
                                className="rounded-lg border border-red-500 px-3 py-1.5 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-60"
                              >
                                {deletingId === company.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {savedTotalPages > 1 && (
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSavedPage(1)}
                    disabled={savedPage === 1}
                    className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    onClick={() => setSavedPage((p) => Math.max(1, p - 1))}
                    disabled={savedPage === 1}
                    className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40"
                  >
                    ‹ Prev
                  </button>
                  {Array.from({ length: savedTotalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === savedTotalPages || Math.abs(p - savedPage) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '...'
                        ? <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">…</span>
                        : (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setSavedPage(item)}
                            className={`rounded-xl px-3 py-2 text-xs font-bold transition ${savedPage === item ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-yellow-400'}`}
                          >
                            {item}
                          </button>
                        )
                    )}
                  <button
                    type="button"
                    onClick={() => setSavedPage((p) => Math.min(savedTotalPages, p + 1))}
                    disabled={savedPage === savedTotalPages}
                    className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40"
                  >
                    Next ›
                  </button>
                  <button
                    type="button"
                    onClick={() => setSavedPage(savedTotalPages)}
                    disabled={savedPage === savedTotalPages}
                    className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40"
                  >
                    »
                  </button>
                  <span className="text-xs text-gray-500">Page {savedPage} of {savedTotalPages}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Partners Lead Tab ─────────────────────────────────────────── */}
        {activeTab === 'partner-leads' && (
          <div className="px-5 py-6 md:px-8">
            {plError && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
                ⚠ {plError}
              </div>
            )}
            {plFeedback && (
              <div className="mb-5 rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm font-semibold text-gray-800">
                {plFeedback}
              </div>
            )}

            {/* Sub-tabs */}
            <div className="mb-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => { setPlSubTab('entry'); setPlFeedback(''); setPlError(''); }}
                className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                  plSubTab === 'entry' ? 'bg-black text-yellow-300' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
                }`}
              >
                {plForm.id ? 'Edit Lead' : 'Add New Lead'}
              </button>
              <button
                type="button"
                onClick={() => { setPlSubTab('saved'); fetchPartnerLeads(); setPlFeedback(''); setPlError(''); }}
                className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                  plSubTab === 'saved' ? 'bg-black text-yellow-300' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
                }`}
              >
                Saved Leads ({partnerLeads.length})
              </button>
            </div>

            {/* Entry form */}
            {plSubTab === 'entry' && (
              <form onSubmit={handlePLSubmit} className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                <div className="flex flex-col gap-2 border-b border-gray-200 pb-5">
                  <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-600">Partners Lead Form</p>
                  <h2 className="text-2xl font-black text-black">{plForm.id ? 'Edit Partner Lead' : 'Add New Partner Lead'}</h2>
                  <p className="text-sm text-gray-600">Record partner companies and organisations for outreach.</p>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <label className="block">
                    <span className="text-sm font-bold text-black">Date of Entry</span>
                    <input
                      value={plForm.entry_date}
                      readOnly
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-100 px-4 py-3 text-black outline-none"
                    />
                  </label>

                  <label className="block md:col-span-2">
                    <span className="text-sm font-bold text-black">Company Name *</span>
                    <input
                      name="company_name"
                      value={plForm.company_name}
                      onChange={handlePLFormChange}
                      placeholder="Enter company / organisation name"
                      required
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-black">Type of Company *</span>
                    <select
                      name="type_of_company"
                      value={plForm.type_of_company}
                      onChange={handlePLFormChange}
                      required
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    >
                      <option value="">Select type...</option>
                      {PARTNER_LEAD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-black">Place *</span>
                    <input
                      name="place"
                      value={plForm.place}
                      onChange={handlePLFormChange}
                      placeholder="Town / city"
                      required
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-black">District *</span>
                    <select
                      name="district"
                      value={plForm.district}
                      onChange={handlePLFormChange}
                      required
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    >
                      <option value="">Select district...</option>
                      {KERALA_DISTRICTS_PL.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </label>

                  {/* Contact Numbers */}
                  <div className="block md:col-span-2 xl:col-span-3">
                    <span className="text-sm font-bold text-black">Contact Numbers *</span>
                    <div className="mt-2 flex flex-col gap-2">
                      {plForm.contact_numbers.map((num, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="tel"
                            value={num}
                            onChange={(e) => handlePLContactChange(idx, e.target.value)}
                            placeholder={`Contact number ${idx + 1}`}
                            className="w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                          />
                          {plForm.contact_numbers.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemovePLContact(idx)}
                              className="rounded-2xl border border-red-300 px-4 py-3 text-sm font-bold text-red-600 hover:bg-red-50"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={handleAddPLContact}
                        className="self-start rounded-xl border border-yellow-400 bg-yellow-50 px-4 py-2 text-sm font-bold text-yellow-700 hover:bg-yellow-100"
                      >
                        + Add Number
                      </button>
                    </div>
                  </div>

                  <label className="block">
                    <span className="text-sm font-bold text-black">WhatsApp Number *</span>
                    <input
                      type="tel"
                      name="whatsapp_number"
                      value={plForm.whatsapp_number}
                      onChange={handlePLFormChange}
                      placeholder="WhatsApp number"
                      required
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-black">Email ID <span className="text-gray-400 font-normal">(optional)</span></span>
                    <input
                      type="email"
                      name="email_id"
                      value={plForm.email_id}
                      onChange={handlePLFormChange}
                      placeholder="email@example.com"
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-bold text-black">Website <span className="text-gray-400 font-normal">(optional)</span></span>
                    <input
                      type="url"
                      name="website"
                      value={plForm.website}
                      onChange={handlePLFormChange}
                      placeholder="https://..."
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  {plForm.id && (
                    <button
                      type="button"
                      onClick={() => { resetPLForm(); setPlFeedback(''); }}
                      className="rounded-2xl border border-gray-300 px-6 py-3 text-sm font-bold text-gray-700 transition hover:border-yellow-400 hover:text-black"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={plSaving}
                    className="rounded-2xl bg-black px-8 py-3 text-sm font-extrabold text-yellow-300 transition hover:bg-yellow-400 hover:text-black disabled:opacity-60"
                  >
                    {plSaving ? 'Saving...' : plForm.id ? 'Update Lead' : 'Save Lead'}
                  </button>
                </div>
              </form>
            )}

            {/* Saved Leads list */}
            {plSubTab === 'saved' && (
              <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm md:p-6">
                <div className="mb-5 flex flex-col gap-2 border-b border-gray-200 pb-5 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-600">Saved Section</p>
                    <h2 className="mt-1 text-2xl font-black text-black">Partner Leads History</h2>
                    <p className="mt-1 text-sm text-gray-600">{partnerLeads.length} leads saved. Edit, delete, or forward to a Partnership Executive.</p>
                  </div>
                  <button
                    type="button"
                    onClick={fetchPartnerLeads}
                    className="self-start rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
                  >
                    Refresh
                  </button>
                </div>

                {plLoading ? (
                  <div className="py-6 text-sm text-gray-600">Loading partner leads...</div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-gray-200">
                    <table className="min-w-full bg-white text-sm">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company Name</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Type</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Place</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">District</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contact Numbers</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">WhatsApp</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Email</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Website</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Forwarded To</th>
                          <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLeads.length === 0 && (
                          <tr>
                            <td colSpan="11" className="px-3 py-8 text-center text-gray-500">No partner leads saved yet. Click "Add New Lead" to create the first one.</td>
                          </tr>
                        )}
                        {paginatedLeads.map((lead) => (
                          <tr key={lead.id} className="align-top hover:bg-gray-50">
                            <td className="border-b px-3 py-3 text-gray-700 whitespace-nowrap">{lead.entry_date || '-'}</td>
                            <td className="border-b px-3 py-3 font-semibold text-black">{lead.company_name}</td>
                            <td className="border-b px-3 py-3 text-gray-700">{lead.type_of_company}</td>
                            <td className="border-b px-3 py-3 text-gray-700">{lead.place}</td>
                            <td className="border-b px-3 py-3 text-gray-700">{lead.district}</td>
                            <td className="border-b px-3 py-3 text-gray-700">
                              {Array.isArray(lead.contact_numbers) ? lead.contact_numbers.join(', ') : lead.contact_numbers}
                            </td>
                            <td className="border-b px-3 py-3 text-gray-700">{lead.whatsapp_number}</td>
                            <td className="border-b px-3 py-3 text-gray-700">{lead.email_id || '-'}</td>
                            <td className="border-b px-3 py-3 text-gray-700">
                              {lead.website ? (
                                <a href={lead.website} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open</a>
                              ) : '-'}
                            </td>
                            <td className="border-b px-3 py-3 text-gray-700">
                              {lead.forwarded_to_name ? (
                                <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-bold text-green-700">{lead.forwarded_to_name}</span>
                              ) : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                            <td className="border-b px-3 py-3">
                              <div className="flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditPL(lead)}
                                  className="rounded-lg border border-blue-500 px-3 py-1.5 text-xs font-bold text-blue-600 transition hover:bg-blue-50"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeletePL(lead.id)}
                                  disabled={plDeletingId === lead.id}
                                  className="rounded-lg border border-red-500 px-3 py-1.5 text-xs font-bold text-red-600 transition hover:bg-red-50 disabled:opacity-60"
                                >
                                  {plDeletingId === lead.id ? 'Deleting...' : 'Delete'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => { setForwardModalId(lead.id); setForwardingToId(lead.forwarded_to || ''); fetchPartnershipExecutives(); }}
                                  className="rounded-lg border border-yellow-500 px-3 py-1.5 text-xs font-bold text-yellow-700 transition hover:bg-yellow-50"
                                >
                                  Forward
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {plTotalPages > 1 && (
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <button type="button" onClick={() => setPlPage(1)} disabled={plPage === 1} className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40">«</button>
                    <button type="button" onClick={() => setPlPage((p) => Math.max(1, p - 1))} disabled={plPage === 1} className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40">‹ Prev</button>
                    {Array.from({ length: plTotalPages }, (_, i) => i + 1).map((p) => (
                      <button key={p} type="button" onClick={() => setPlPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${plPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-yellow-400'}`}>{p}</button>
                    ))}
                    <button type="button" onClick={() => setPlPage((p) => Math.min(plTotalPages, p + 1))} disabled={plPage === plTotalPages} className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40">Next ›</button>
                    <button type="button" onClick={() => setPlPage(plTotalPages)} disabled={plPage === plTotalPages} className="rounded-xl border border-gray-300 px-3 py-2 text-xs font-bold text-gray-700 transition hover:border-yellow-400 disabled:opacity-40">»</button>
                    <span className="text-xs text-gray-500">Page {plPage} of {plTotalPages}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Forward Modal */}
        {forwardModalId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-[28px] border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between rounded-t-[28px] bg-gradient-to-r from-black to-gray-800 px-6 py-5">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-300">Forward Lead</p>
                  <h3 className="mt-1 text-xl font-black text-white">Select Partnership Executive</h3>
                </div>
                <button type="button" onClick={() => { setForwardModalId(null); setForwardingToId(''); }} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">✕</button>
              </div>
              <div className="p-6">
                {partnershipExecutives.length === 0 ? (
                  <p className="text-sm text-gray-600">No active Partnership Executives found. Please add staff with the Partnership Executive role.</p>
                ) : (
                  <div className="flex flex-col gap-3">
                    <label className="block">
                      <span className="text-sm font-bold text-black">Partnership Executive *</span>
                      <select
                        value={forwardingToId}
                        onChange={(e) => setForwardingToId(e.target.value)}
                        className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:bg-white focus:ring-2 focus:ring-yellow-200"
                      >
                        <option value="">Select executive...</option>
                        {partnershipExecutives.map((exec) => (
                          <option key={exec.user_id} value={exec.user_id}>{exec.name}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                )}
                <div className="mt-6 flex justify-end gap-3">
                  <button type="button" onClick={() => { setForwardModalId(null); setForwardingToId(''); }} className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400">Cancel</button>
                  {partnershipExecutives.length > 0 && (
                    <button type="button" onClick={handleForwardPL} disabled={!forwardingToId || forwardingLoading} className="rounded-xl bg-black px-6 py-3 text-sm font-extrabold text-yellow-300 transition hover:bg-yellow-400 hover:text-black disabled:opacity-50">
                      {forwardingLoading ? 'Forwarding...' : 'Forward Now'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {importModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8 backdrop-blur-sm">
            <div className="w-full max-w-lg rounded-[28px] border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between rounded-t-[28px] bg-gradient-to-r from-black to-gray-800 px-6 py-5">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-300">Data Import</p>
                  <h3 className="mt-1 text-xl font-black text-white">Import Companies from Excel</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setImportModalOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  ✕
                </button>
              </div>

              <div className="p-6">
                <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4">
                  <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-yellow-700">Step 1 — Download Template</p>
                  <p className="mt-2 text-sm text-gray-700">Download the template, fill in your company data, then upload it below. Required columns: <strong>Company Name, Category, Location, District, Contact Numbers</strong> (comma-separated 10-digit numbers).</p>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="mt-3 rounded-xl bg-yellow-400 px-5 py-2.5 text-sm font-extrabold text-black transition hover:bg-yellow-500"
                  >
                    Download Template (.xls)
                  </button>
                </div>

                <div className="mt-5">
                  <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-700">Step 2 — Upload Filled File</p>
                  <label className="mt-3 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center transition hover:border-yellow-400 hover:bg-yellow-50">
                    {importFile ? (
                      <>
                        <span className="text-2xl">📄</span>
                        <span className="text-sm font-bold text-black">{importFile.name}</span>
                        <span className="text-xs text-gray-500">Click to change file</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl text-gray-300">⬆</span>
                        <span className="text-sm font-semibold text-gray-600">Click to select .xls or .xlsx file</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept=".xls,.xlsx"
                      className="hidden"
                      onChange={(e) => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                    />
                  </label>
                </div>

                {importResult && (
                  <div className={`mt-4 rounded-2xl border p-4 text-sm ${importResult.error ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50'}`}>
                    {importResult.error ? (
                      <p className="font-semibold">{importResult.error}</p>
                    ) : (
                      <>
                        <p className="font-extrabold text-green-700">Import complete</p>
                        <p className="mt-1 text-gray-700">✔ {importResult.inserted} companies imported · {importResult.skipped} rows skipped</p>
                        {importResult.errors && importResult.errors.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-xs font-bold text-gray-600">Show skipped row details ({importResult.errors.length})</summary>
                            <ul className="mt-2 space-y-1">
                              {importResult.errors.map((e, i) => <li key={i} className="text-xs text-red-600">• {e}</li>)}
                            </ul>
                          </details>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setImportModalOpen(false)}
                    className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={!importFile || importLoading}
                    className="rounded-xl bg-black px-6 py-3 text-sm font-extrabold text-yellow-300 transition hover:bg-yellow-400 hover:text-black disabled:opacity-50"
                  >
                    {importLoading ? 'Importing...' : 'Import Now'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

// ─── Partnership Executive Dashboard ─────────────────────────────────────────

const EXEC_STATUSES = ['Pending', 'Agreed', 'Call Later', 'Not Available', 'Rejected'];
const EXEC_STATUS_COLORS = {
  Pending:       'bg-gray-100 text-gray-700',
  Agreed:        'bg-green-100 text-green-700',
  'Call Later':  'bg-blue-100 text-blue-700',
  'Not Available': 'bg-orange-100 text-orange-700',
  Rejected:      'bg-red-100 text-red-700',
};

const KERALA_DISTRICTS_EXEC = ['Thiruvananthapuram','Kollam','Pathanamthitta','Alappuzha','Kottayam','Idukki','Ernakulam','Thrissur','Palakkad','Malappuram','Kozhikode','Wayanad','Kannur','Kasaragod'];

const PartnershipExecutiveDashboard = ({ name, API_URL }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [feedback, setFeedback] = useState('');

  // Status update modal
  const [statusModal, setStatusModal] = useState(null); // { lead }
  const [statusChoice, setStatusChoice] = useState('');
  const [callLaterDatetime, setCallLaterDatetime] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusSaving, setStatusSaving] = useState(false);

  // Partnership form modal
  const [partnerFormLead, setPartnerFormLead] = useState(null);
  const [partnerForm, setPartnerForm] = useState({});
  const [partnerFormFiles, setPartnerFormFiles] = useState({});
  const [partnerFormSaving, setPartnerFormSaving] = useState(false);

  // Call Later alerts
  const [callAlerts, setCallAlerts] = useState([]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/executive/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(res.data || []);
    } catch (err) {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []); // eslint-disable-line

  // Check Call Later alerts every minute
  useEffect(() => {
    const check = () => {
      const now = new Date();
      const alerts = leads.filter(l => {
        if (l.executive_status !== 'Call Later' || !l.call_later_datetime) return false;
        const callTime = new Date(l.call_later_datetime);
        const diffMin = (callTime - now) / 60000;
        return diffMin <= 15 && diffMin >= -5; // alert if within 15 min or up to 5 min past
      });
      setCallAlerts(alerts);
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [leads]);

  const pendingLeads   = leads.filter(l => ['Pending','Not Available','Call Later'].includes(l.executive_status));
  const agreedLeads    = leads.filter(l => l.executive_status === 'Agreed');
  const rejectedLeads  = leads.filter(l => l.executive_status === 'Rejected');

  // Summary stats
  const totalLeads = leads.length;
  const converted  = agreedLeads.length;
  const convRate   = totalLeads > 0 ? Math.round((converted / totalLeads) * 100) : 0;
  const pendingCount = pendingLeads.length;
  const rejectedCount = rejectedLeads.length;
  const withForm = leads.filter(l => l.partnership_form_id).length;

  // District breakdown
  const districtMap = {};
  agreedLeads.forEach(l => { districtMap[l.district] = (districtMap[l.district] || 0) + 1; });
  const topDistricts = Object.entries(districtMap).sort((a,b) => b[1]-a[1]).slice(0, 6);

  // Type breakdown
  const typeMap = {};
  agreedLeads.forEach(l => { typeMap[l.type_of_company] = (typeMap[l.type_of_company] || 0) + 1; });
  const topTypes = Object.entries(typeMap).sort((a,b) => b[1]-a[1]).slice(0, 6);

  const openStatusModal = (lead) => {
    setStatusModal({ lead });
    setStatusChoice(lead.executive_status || 'Pending');
    setCallLaterDatetime(lead.call_later_datetime ? lead.call_later_datetime.slice(0, 16) : '');
    setRejectionReason(lead.rejection_reason || '');
    setFeedback('');
  };

  const handleStatusSave = async () => {
    if (!statusModal) return;
    setStatusSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        executive_status: statusChoice,
        call_later_datetime: statusChoice === 'Call Later' ? callLaterDatetime : undefined,
        rejection_reason: statusChoice === 'Rejected' ? rejectionReason : undefined,
      };
      const res = await axios.put(`${API_URL}/api/staff/executive/leads/${statusModal.lead.id}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(prev => prev.map(l => l.id === statusModal.lead.id ? res.data.lead : l));
      setFeedback(`Status updated to "${statusChoice}".`);
      setStatusModal(null);
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to update status.');
    } finally {
      setStatusSaving(false);
    }
  };

  const openPartnerForm = (lead) => {
    setPartnerFormLead(lead);
    setPartnerForm({
      name_of_organisation: lead.company_name || '',
      owner_of_organisation: '',
      place: lead.place || '',
      district: lead.district || '',
      location_map: '',
      years_of_experience: '',
      contact_number: Array.isArray(lead.contact_numbers) ? (lead.contact_numbers[0] || '') : '',
      whatsapp_number: lead.whatsapp_number || '',
      email_id: lead.email_id || '',
      website: lead.website || '',
    });
    setPartnerFormFiles({});
    setFeedback('');
  };

  const handlePartnerFormSubmit = async (e) => {
    e.preventDefault();
    if (!partnerFormLead) return;
    setPartnerFormSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      Object.entries(partnerForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (partnerFormFiles.organisation_license_file) fd.append('organisation_license_file', partnerFormFiles.organisation_license_file);
      if (partnerFormFiles.organisation_photo_file) fd.append('organisation_photo_file', partnerFormFiles.organisation_photo_file);
      if (partnerFormFiles.organisation_name_board_photo_file) fd.append('organisation_name_board_photo_file', partnerFormFiles.organisation_name_board_photo_file);

      const res = await axios.post(`${API_URL}/api/staff/executive/leads/${partnerFormLead.id}/partnership-form`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setLeads(prev => prev.map(l => l.id === partnerFormLead.id ? res.data.lead : l));
      setFeedback('Partnership form submitted successfully. Admin will review and issue credentials.');
      setPartnerFormLead(null);
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to submit partnership form.');
    } finally {
      setPartnerFormSaving(false);
    }
  };

  const fmt = (dt) => fmtDateTime(dt);

  const LeadRow = ({ lead }) => (
    <tr className="align-top hover:bg-gray-50 transition">
      <td className="border-b px-3 py-3 text-gray-600 whitespace-nowrap text-xs">{lead.entry_date || '—'}</td>
      <td className="border-b px-3 py-3">
        <p className="font-bold text-black">{lead.company_name}</p>
        <p className="text-xs text-gray-500">{lead.type_of_company}</p>
      </td>
      <td className="border-b px-3 py-3 text-gray-700 text-sm">{lead.place}, {lead.district}</td>
      <td className="border-b px-3 py-3 text-sm text-gray-700">
        {Array.isArray(lead.contact_numbers) ? lead.contact_numbers.map((n,i) => (
          <a key={i} href={`tel:${n}`} className="block text-blue-600 hover:underline font-semibold">{n}</a>
        )) : lead.contact_numbers}
      </td>
      <td className="border-b px-3 py-3">
        <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${EXEC_STATUS_COLORS[lead.executive_status] || EXEC_STATUS_COLORS.Pending}`}>
          {lead.executive_status || 'Pending'}
        </span>
        {lead.executive_status === 'Call Later' && lead.call_later_datetime && (
          <p className="mt-1 text-xs text-blue-600 font-semibold">📅 {fmt(lead.call_later_datetime)}</p>
        )}
      </td>
      <td className="border-b px-3 py-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => openStatusModal(lead)}
            className="rounded-lg border border-gray-400 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-100 transition"
          >
            Update Status
          </button>
          {lead.executive_status === 'Agreed' && !lead.partnership_form_id && (
            <button
              type="button"
              onClick={() => openPartnerForm(lead)}
              className="rounded-lg border border-green-500 bg-green-50 px-3 py-1.5 text-xs font-bold text-green-700 hover:bg-green-100 transition"
            >
              Fill Partnership Form
            </button>
          )}
          {lead.partnership_form_id && (
            <span className="rounded-lg border border-yellow-400 bg-yellow-50 px-3 py-1.5 text-xs font-bold text-yellow-700">Form Submitted ✓</span>
          )}
        </div>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-accent px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1920px] rounded-[28px] border border-yellow-200 bg-white shadow-[0_25px_80px_rgba(0,0,0,0.08)]">

        {/* Header */}
        <div className="rounded-t-[28px] bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.32),transparent_28%),linear-gradient(135deg,#070707_0%,#1e1e1e_54%,#5b4600_100%)] px-5 py-6 text-white md:px-8">
          <p className="text-xs font-extrabold uppercase tracking-[0.34em] text-yellow-300">Partnership Executive Dashboard</p>
          <div className="mt-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-black tracking-tight md:text-4xl">Partner Leads Pipeline</h1>
              <p className="mt-2 text-sm text-white/70">Welcome, {name}. Follow up on assigned leads and convert them to business partnerships.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { label: 'Total Leads', value: totalLeads },
                { label: 'Agreed', value: converted },
                { label: 'Pending', value: pendingCount },
                { label: 'Conversion', value: `${convRate}%` },
              ].map(kpi => (
                <div key={kpi.label} className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur-sm">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-yellow-300">{kpi.label}</p>
                  <p className="mt-2 text-3xl font-black">{kpi.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Call Later Alerts */}
        {callAlerts.length > 0 && (
          <div className="mx-5 mt-5 rounded-2xl border border-blue-300 bg-blue-50 px-5 py-4 md:mx-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-blue-700">⏰ Upcoming Call Reminders</p>
            <div className="mt-3 flex flex-col gap-2">
              {callAlerts.map(l => (
                <div key={l.id} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 border border-blue-200 shadow-sm">
                  <div>
                    <p className="font-bold text-black">{l.company_name}</p>
                    <p className="text-xs text-gray-600">{l.place}, {l.district}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-blue-700">{fmt(l.call_later_datetime)}</p>
                    <p className="text-xs text-blue-500">Scheduled Call</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {feedback && (
          <div className="mx-5 mt-5 rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm font-semibold text-gray-800 md:mx-8">
            {feedback}
          </div>
        )}

        <div className="px-5 pt-5 md:px-8">
          <TargetWidget API_URL={API_URL} />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-5 py-4 md:px-8">
          <div className="flex flex-wrap gap-3">
            {[
              { id: 'pending', label: `Pending (${pendingCount})` },
              { id: 'agreed', label: `Agreed (${agreedLeads.length})` },
              { id: 'rejected', label: `Rejected (${rejectedCount})` },
              { id: 'summary', label: 'Summary' },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-extrabold uppercase tracking-[0.16em] transition ${
                  activeTab === tab.id ? 'bg-black text-yellow-300' : 'border border-gray-300 bg-white text-gray-700 hover:border-yellow-400 hover:text-black'
                }`}
              >
                {tab.label}
              </button>
            ))}
            <button type="button" onClick={fetchLeads} className="ml-auto rounded-full border border-gray-300 px-4 py-2 text-xs font-bold text-gray-600 hover:border-yellow-400 transition">
              ↻ Refresh
            </button>
          </div>
        </div>

        {/* Pending Tab */}
        {activeTab === 'pending' && (
          <div className="px-5 py-6 md:px-8">
            <div className="mb-4">
              <h2 className="text-xl font-black text-black">Pending Leads</h2>
              <p className="text-sm text-gray-600 mt-1">All new, Not Available, and Call Later leads. Update status after each call attempt.</p>
            </div>
            {loading ? <div className="py-6 text-sm text-gray-500">Loading leads...</div> : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full bg-white text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Location</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contact</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Status</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingLeads.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400">No pending leads right now.</td></tr>
                    )}
                    {pendingLeads.map(lead => <LeadRow key={lead.id} lead={lead} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Agreed Tab */}
        {activeTab === 'agreed' && (
          <div className="px-5 py-6 md:px-8">
            <div className="mb-5">
              <h2 className="text-xl font-black text-black">Agreed Leads</h2>
              <p className="text-sm text-gray-600 mt-1">These leads have agreed to partner. Fill the partnership form to submit to admin for ID &amp; password issuance.</p>
            </div>
            {loading ? <div className="py-6 text-sm text-gray-500">Loading...</div> : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full bg-white text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Location</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contact</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Status</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreedLeads.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400">No agreed leads yet. Keep following up!</td></tr>
                    )}
                    {agreedLeads.map(lead => <LeadRow key={lead.id} lead={lead} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Rejected Tab */}
        {activeTab === 'rejected' && (
          <div className="px-5 py-6 md:px-8">
            <div className="mb-4">
              <h2 className="text-xl font-black text-black">Rejected Leads</h2>
              <p className="text-sm text-gray-600 mt-1">Leads that declined partnership. Kept for record.</p>
            </div>
            {loading ? <div className="py-6 text-sm text-gray-500">Loading...</div> : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200">
                <table className="min-w-full bg-white text-sm">
                  <thead className="bg-gray-50 text-left">
                    <tr>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Location</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contact</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Reason</th>
                      <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedLeads.length === 0 && (
                      <tr><td colSpan="6" className="px-3 py-8 text-center text-gray-400">No rejected leads.</td></tr>
                    )}
                    {rejectedLeads.map(lead => (
                      <tr key={lead.id} className="align-top hover:bg-gray-50">
                        <td className="border-b px-3 py-3 text-gray-600 text-xs">{lead.entry_date || '—'}</td>
                        <td className="border-b px-3 py-3"><p className="font-bold text-black">{lead.company_name}</p><p className="text-xs text-gray-500">{lead.type_of_company}</p></td>
                        <td className="border-b px-3 py-3 text-sm text-gray-700">{lead.place}, {lead.district}</td>
                        <td className="border-b px-3 py-3 text-sm">
                          {Array.isArray(lead.contact_numbers) ? lead.contact_numbers.map((n,i) => <a key={i} href={`tel:${n}`} className="block text-blue-600 hover:underline font-semibold">{n}</a>) : lead.contact_numbers}
                        </td>
                        <td className="border-b px-3 py-3 text-sm text-gray-600 italic">{lead.rejection_reason || '—'}</td>
                        <td className="border-b px-3 py-3">
                          <button type="button" onClick={() => openStatusModal(lead)} className="rounded-lg border border-gray-400 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-100">Reconsider</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="px-5 py-6 md:px-8 space-y-8">
            <div>
              <h2 className="text-2xl font-black text-black">Performance Summary</h2>
              <p className="text-sm text-gray-600 mt-1">Your lead conversion overview and breakdown.</p>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
              {[
                { label: 'Total Leads', value: totalLeads, color: 'border-gray-300 bg-gray-50', text: 'text-black' },
                { label: 'Agreed', value: converted, color: 'border-green-300 bg-green-50', text: 'text-green-700' },
                { label: 'Pending', value: pendingCount, color: 'border-blue-300 bg-blue-50', text: 'text-blue-700' },
                { label: 'Rejected', value: rejectedCount, color: 'border-red-300 bg-red-50', text: 'text-red-700' },
                { label: 'Forms Submitted', value: withForm, color: 'border-yellow-300 bg-yellow-50', text: 'text-yellow-700' },
              ].map(kpi => (
                <div key={kpi.label} className={`rounded-2xl border ${kpi.color} px-5 py-4`}>
                  <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gray-500">{kpi.label}</p>
                  <p className={`mt-3 text-4xl font-black ${kpi.text}`}>{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Conversion Funnel */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-yellow-600 mb-4">Conversion Funnel</p>
              {[
                { label: 'Leads Received', count: totalLeads, color: 'bg-gray-800' },
                { label: 'Called / In Progress', count: leads.filter(l => l.executive_status !== 'Pending').length, color: 'bg-blue-600' },
                { label: 'Agreed', count: converted, color: 'bg-green-600' },
                { label: 'Form Submitted', count: withForm, color: 'bg-yellow-500' },
              ].map(step => (
                <div key={step.label} className="mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-semibold text-gray-700">{step.label}</span>
                    <span className="text-sm font-black text-black">{step.count}</span>
                  </div>
                  <div className="h-3 w-full rounded-full bg-gray-100">
                    <div
                      className={`h-3 rounded-full transition-all ${step.color}`}
                      style={{ width: totalLeads > 0 ? `${Math.round((step.count / totalLeads) * 100)}%` : '0%' }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {totalLeads > 0 ? Math.round((step.count / totalLeads) * 100) : 0}%
                  </p>
                </div>
              ))}
            </div>

            {/* Agreed by District & Type */}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-yellow-600 mb-4">Agreed by District</p>
                {topDistricts.length === 0 ? (
                  <p className="text-sm text-gray-400">No agreed leads yet.</p>
                ) : (
                  topDistricts.map(([district, count]) => (
                    <div key={district} className="mb-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-700">{district}</span>
                        <span className="text-sm font-black">{count}</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-gray-100">
                        <div className="h-2.5 rounded-full bg-yellow-500" style={{ width: `${Math.round((count / converted) * 100)}%` }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-yellow-600 mb-4">Agreed by Company Type</p>
                {topTypes.length === 0 ? (
                  <p className="text-sm text-gray-400">No agreed leads yet.</p>
                ) : (
                  topTypes.map(([type, count]) => (
                    <div key={type} className="mb-3">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-700">{type}</span>
                        <span className="text-sm font-black">{count}</span>
                      </div>
                      <div className="h-2.5 w-full rounded-full bg-gray-100">
                        <div className="h-2.5 rounded-full bg-green-500" style={{ width: `${Math.round((count / converted) * 100)}%` }} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Status breakdown chips */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-yellow-600 mb-4">Status Breakdown</p>
              <div className="flex flex-wrap gap-3">
                {EXEC_STATUSES.map(s => {
                  const c = leads.filter(l => (l.executive_status || 'Pending') === s).length;
                  return (
                    <div key={s} className={`rounded-2xl px-4 py-3 border ${EXEC_STATUS_COLORS[s]}`}>
                      <p className="text-xs font-bold uppercase tracking-wide">{s}</p>
                      <p className="text-2xl font-black mt-1">{c}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Status Update Modal */}
      {statusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-[28px] border border-gray-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between rounded-t-[28px] bg-gradient-to-r from-black to-gray-800 px-6 py-5">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-300">Update Status</p>
                <h3 className="mt-1 text-xl font-black text-white">{statusModal.lead.company_name}</h3>
              </div>
              <button type="button" onClick={() => setStatusModal(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {EXEC_STATUSES.map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusChoice(s)}
                    className={`rounded-2xl border-2 px-3 py-3 text-sm font-extrabold transition ${
                      statusChoice === s ? 'border-black bg-black text-yellow-300' : 'border-gray-300 text-gray-700 hover:border-yellow-400'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {statusChoice === 'Call Later' && (
                <label className="block">
                  <span className="text-sm font-bold text-black">Schedule Call Date &amp; Time *</span>
                  <input
                    type="datetime-local"
                    value={callLaterDatetime}
                    onChange={e => setCallLaterDatetime(e.target.value)}
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  />
                  <p className="mt-1 text-xs text-blue-600">You will get an alert 15 minutes before the scheduled time.</p>
                </label>
              )}

              {statusChoice === 'Rejected' && (
                <label className="block">
                  <span className="text-sm font-bold text-black">Rejection Reason <span className="text-gray-400 font-normal">(optional)</span></span>
                  <textarea
                    rows="3"
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Why did they decline?"
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  />
                </label>
              )}

              {statusChoice === 'Agreed' && (
                <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
                  <p className="text-sm font-bold text-green-700">🎉 Great news! After saving, you will see a button to fill the partnership form.</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setStatusModal(null)} className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black hover:border-yellow-400 transition">Cancel</button>
                <button type="button" onClick={handleStatusSave} disabled={statusSaving || (statusChoice === 'Call Later' && !callLaterDatetime)} className="rounded-xl bg-black px-6 py-3 text-sm font-extrabold text-yellow-300 hover:bg-yellow-400 hover:text-black transition disabled:opacity-50">
                  {statusSaving ? 'Saving...' : 'Save Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partnership Form Modal */}
      {partnerFormLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-2xl rounded-[28px] border border-gray-200 bg-white shadow-2xl my-4">
            <div className="flex items-center justify-between rounded-t-[28px] bg-gradient-to-r from-black to-gray-800 px-6 py-5">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-300">Partnership Form</p>
                <h3 className="mt-1 text-xl font-black text-white">{partnerFormLead.company_name}</h3>
              </div>
              <button type="button" onClick={() => setPartnerFormLead(null)} className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">✕</button>
            </div>
            <form onSubmit={handlePartnerFormSubmit} className="p-6">
              <p className="text-sm text-gray-600 mb-5">Fill in the organisation details. Admin will review and issue login credentials.</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[
                  { name: 'name_of_organisation', label: 'Name of Organisation *', required: true },
                  { name: 'owner_of_organisation', label: 'Owner Name *', required: true },
                  { name: 'place', label: 'Place *', required: true },
                  { name: 'years_of_experience', label: 'Years of Experience *', required: true },
                  { name: 'contact_number', label: 'Contact Number *', required: true },
                  { name: 'whatsapp_number', label: 'WhatsApp Number *', required: true },
                  { name: 'email_id', label: 'Email ID (optional)' },
                  { name: 'website', label: 'Website (optional)' },
                  { name: 'location_map', label: 'Google Map Link (optional)' },
                ].map(field => (
                  <label key={field.name} className={`block ${field.name === 'location_map' ? 'md:col-span-2' : ''}`}>
                    <span className="text-sm font-bold text-black">{field.label}</span>
                    <input
                      value={partnerForm[field.name] || ''}
                      onChange={e => setPartnerForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                      required={field.required}
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                    />
                  </label>
                ))}

                <label className="block">
                  <span className="text-sm font-bold text-black">District *</span>
                  <select
                    value={partnerForm.district || ''}
                    onChange={e => setPartnerForm(prev => ({ ...prev, district: e.target.value }))}
                    required
                    className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  >
                    <option value="">Select district...</option>
                    {KERALA_DISTRICTS_EXEC.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>

                {[
                  { name: 'organisation_license_file', label: 'Organisation License (image)' },
                  { name: 'organisation_photo_file', label: 'Organisation Photo (image)' },
                  { name: 'organisation_name_board_photo_file', label: 'Name Board Photo (image)' },
                ].map(f => (
                  <label key={f.name} className="block">
                    <span className="text-sm font-bold text-black">{f.label}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => setPartnerFormFiles(prev => ({ ...prev, [f.name]: e.target.files[0] || null }))}
                      className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black text-sm outline-none"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={() => setPartnerFormLead(null)} className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black hover:border-yellow-400 transition">Cancel</button>
                <button type="submit" disabled={partnerFormSaving} className="rounded-xl bg-black px-6 py-3 text-sm font-extrabold text-yellow-300 hover:bg-yellow-400 hover:text-black transition disabled:opacity-50">
                  {partnerFormSaving ? 'Submitting...' : 'Submit to Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── End Partnership Executive Dashboard ──────────────────────────────────────

const AssistantRecruiterDashboard = ({ name, summary, API_URL }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [toast, setToast] = useState('');
  const [activeCompanyTab, setActiveCompanyTab] = useState('pending');
  const [internshipRequests, setInternshipRequests] = useState([]);
  const [internshipRequestsLoading, setInternshipRequestsLoading] = useState(false);
  const [internshipRequestsUnread, setInternshipRequestsUnread] = useState(0);
  const [internshipManageModal, setInternshipManageModal] = useState(null); // internship application object
  const [internshipManageTab, setInternshipManageTab] = useState('calls'); // 'calls' | 'applicant' | 'status'
  const [internshipCalls, setInternshipCalls] = useState([]);
  const [internshipCallsLoading, setInternshipCallsLoading] = useState(false);
  const [internshipCallForm, setInternshipCallForm] = useState({ company_name: '', contact_number: '', call_date: new Date().toISOString().slice(0, 10), call_status: '', vacancy_details: '', call_later_datetime: '', notes: '' });
  const [internshipCallSaving, setInternshipCallSaving] = useState(false);
  const [internshipStatusForm, setInternshipStatusForm] = useState({ ar_status: '', ar_notes: '' });
  const [internshipStatusSaving, setInternshipStatusSaving] = useState(false);
  const [internshipStatusFilter, setInternshipStatusFilter] = useState('All');
  const [internshipExpandedId, setInternshipExpandedId] = useState(null);
  const [companyPage, setCompanyPage] = useState(1);
  const [coordPage, setCoordPage] = useState(1);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [callLaterModal, setCallLaterModal] = useState(null);
  const [callLaterDatetime, setCallLaterDatetime] = useState('');
  const [callLaterSaving, setCallLaterSaving] = useState(false);
  const [callLaterTick, setCallLaterTick] = useState(0);
  const [showCallLaterPopup, setShowCallLaterPopup] = useState(false);
  const [vacancySavingId, setVacancySavingId] = useState(null);
  const [coordinationSavingId, setCoordinationSavingId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [coordinationItems, setCoordinationItems] = useState([]);
  const [selectedCoordination, setSelectedCoordination] = useState(null);
  const [vacancyForm, setVacancyForm] = useState(createInitialVacancyForm);
  const [coordinationForm, setCoordinationForm] = useState(createCompanyCoordinationForm());
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ company_name: '', contact: '', whatsapp: '', has_whatsapp: '', has_vacancy: '', district: '', category: '' });

  const fetchAssistantRecruiterCompanies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/assistant-recruiter/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data || []);
    } catch (err) {
      setCompanies([]);
      setFeedback(err?.response?.data?.error || 'Failed to fetch company call list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssistantRecruiterCompanies();
  }, []);

  const fetchInterviewCoordination = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/assistant-recruiter/interview-coordination`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoordinationItems(res.data || []);
    } catch {
      setCoordinationItems([]);
    }
  };

  useEffect(() => {
    fetchInterviewCoordination();
    // Auto-refresh every 30s so AR sees Recruiter's updated interview details
    const t = setInterval(fetchInterviewCoordination, 30000);
    return () => clearInterval(t);
  }, []);

  const fetchInternshipRequests = async () => {
    setInternshipRequestsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/assistant-recruiter/internship-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data || [];
      setInternshipRequestsUnread(data.filter(r => !r.ar_is_seen).length);
      setInternshipRequests(data);
    } catch {
      setInternshipRequests([]);
    } finally {
      setInternshipRequestsLoading(false);
    }
  };

  useEffect(() => {
    if (activeCompanyTab === 'internship-requests') fetchInternshipRequests();
  }, [activeCompanyTab]);

  const openInternshipManage = async (row) => {
    setInternshipManageModal(row);
    setInternshipManageTab('calls');
    setInternshipStatusForm({ ar_status: row.ar_status || 'Pending', ar_notes: row.ar_notes || '' });
    setInternshipCallForm({ company_name: '', contact_number: '', call_date: new Date().toISOString().slice(0, 10), call_status: '', vacancy_details: '', call_later_datetime: '', notes: '' });
    setInternshipCallsLoading(true);
    setInternshipCalls([]);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/assistant-recruiter/internship-requests/${row.id}/calls`, { headers: { Authorization: `Bearer ${token}` } });
      setInternshipCalls(res.data || []);
    } catch { setInternshipCalls([]); }
    finally { setInternshipCallsLoading(false); }
  };

  const handleAddInternshipCall = async () => {
    if (!internshipManageModal || !internshipCallForm.company_name || !internshipCallForm.call_status) return;
    setInternshipCallSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/staff/assistant-recruiter/internship-requests/${internshipManageModal.id}/calls`,
        internshipCallForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInternshipCalls(prev => [res.data.call, ...prev]);
      setInternshipCallForm({ company_name: '', contact_number: '', call_date: new Date().toISOString().slice(0, 10), call_status: '', vacancy_details: '', call_later_datetime: '', notes: '' });
      // Refresh request list to update ar_status badge
      const updated = await axios.get(`${API_URL}/api/staff/assistant-recruiter/internship-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setInternshipRequests(updated.data || []);
      const updatedRow = (updated.data || []).find(r => r.id === internshipManageModal.id);
      if (updatedRow) { setInternshipManageModal(updatedRow); setInternshipStatusForm({ ar_status: updatedRow.ar_status || 'Pending', ar_notes: updatedRow.ar_notes || '' }); }
    } catch (err) { alert(err?.response?.data?.error || 'Failed to add call.'); }
    finally { setInternshipCallSaving(false); }
  };

  const handleDeleteInternshipCall = async (callId) => {
    if (!window.confirm('Delete this call record?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/staff/assistant-recruiter/internship-requests/${internshipManageModal.id}/calls/${callId}`, { headers: { Authorization: `Bearer ${token}` } });
      setInternshipCalls(prev => prev.filter(c => c.id !== callId));
    } catch { alert('Failed to delete.'); }
  };

  const handleUpdateInternshipStatus = async () => {
    if (!internshipManageModal || !internshipStatusForm.ar_status) return;
    setInternshipStatusSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/staff/assistant-recruiter/internship-requests/${internshipManageModal.id}/status`,
        internshipStatusForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedApp = res.data.application;
      setInternshipRequests(prev => prev.map(r => r.id === updatedApp.id ? updatedApp : r));
      setInternshipManageModal(updatedApp);
    } catch (err) { alert(err?.response?.data?.error || 'Failed to update status.'); }
    finally { setInternshipStatusSaving(false); }
  };

  useEffect(() => {
    const timer = setInterval(() => setCallLaterTick((n) => n + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const totalFoundVacancies = useMemo(
    () => companies.reduce((count, company) => count + Number(company.vacancy_count || company.vacancies?.length || 0), 0),
    [companies]
  );

  const totalPendingCalls = useMemo(
    () => companies.filter((company) => (company.communication_status || 'Pending') === 'Pending').length,
    [companies]
  );

  const pendingCompanies = useMemo(
    () => companies.filter((company) => {
      const s = company.communication_status || 'Pending';
      return s !== 'Found Vacancies' && s !== 'Rejected' && s !== 'No Vacancies' && s !== 'Call Later' && s !== 'Number Does Not Exist' && Number(company.vacancy_count || 0) === 0;
    }),
    [companies]
  );

  const callLaterCompanies = useMemo(
    () => companies.filter((c) => (c.communication_status || '') === 'Call Later'),
    [companies]
  );

  const dueCallLaterCompanies = useMemo(
    () => callLaterCompanies.filter((c) => c.call_later_datetime && new Date(c.call_later_datetime) <= new Date()),
    [callLaterCompanies, callLaterTick]
  );

  useEffect(() => {
    if (dueCallLaterCompanies.length > 0) {
      setShowCallLaterPopup(true);
    }
  }, [dueCallLaterCompanies.length]);

  const foundVacancyCompanies = useMemo(
    () => companies.filter((company) => (company.communication_status || 'Pending') === 'Found Vacancies' || Number(company.vacancy_count || 0) > 0),
    [companies]
  );

  const rejectedCompanies = useMemo(
    () => companies.filter((company) => (company.communication_status || '') === 'Rejected' || (company.communication_status || '') === 'Number Does Not Exist'),
    [companies]
  );

  const noVacancyCompanies = useMemo(
    () => companies.filter((company) => (company.communication_status || '') === 'No Vacancies'),
    [companies]
  );

  const pendingCoordinationItems = useMemo(
    () => coordinationItems.filter((item) => item.vacancy_availability !== 'Available' || item.assistant_recruiter_confirmation !== 'Accepted').length,
    [coordinationItems]
  );

  const daysSince = (dateStr) => {
    if (!dateStr) return null;
    // support MM-DD-YYYY, YYYY-MM-DD, or DD/MM/YYYY
    let d;
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [m, day, y] = dateStr.split('-');
      d = new Date(Number(y), Number(m) - 1, Number(day));
    } else {
      d = new Date(dateStr);
    }
    if (isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  };

  const visibleCompanies = activeCompanyTab === 'found' ? foundVacancyCompanies
    : activeCompanyTab === 'rejected' ? rejectedCompanies
    : activeCompanyTab === 'no-vacancy' ? noVacancyCompanies
    : activeCompanyTab === 'call-later' ? callLaterCompanies
    : pendingCompanies;

  const filteredCompanies = useMemo(() => {
    let list = visibleCompanies;
    if (filters.company_name.trim()) {
      const q = filters.company_name.trim().toLowerCase();
      list = list.filter((c) => (c.company_name || '').toLowerCase().includes(q));
    }
    if (filters.contact.trim()) {
      const q = filters.contact.trim().toLowerCase();
      list = list.filter((c) => (c.contact_numbers_display || '').toLowerCase().includes(q));
    }
    if (filters.whatsapp.trim()) {
      const q = filters.whatsapp.trim().toLowerCase();
      list = list.filter((c) => (c.whatsapp_number || '').toLowerCase().includes(q));
    }
    if (filters.has_whatsapp === 'yes') list = list.filter((c) => c.whatsapp_number && c.whatsapp_number.trim());
    if (filters.has_whatsapp === 'no') list = list.filter((c) => !c.whatsapp_number || !c.whatsapp_number.trim());
    if (filters.has_vacancy === 'yes') list = list.filter((c) => Number(c.vacancy_count || 0) > 0 || (c.vacancies || []).length > 0);
    if (filters.has_vacancy === 'no') list = list.filter((c) => Number(c.vacancy_count || 0) === 0 && !(c.vacancies || []).length);
    if (filters.district.trim()) {
      const q = filters.district.trim().toLowerCase();
      list = list.filter((c) => (c.district || '').toLowerCase().includes(q));
    }
    if (filters.category.trim()) {
      const q = filters.category.trim().toLowerCase();
      list = list.filter((c) => (c.category || '').toLowerCase().includes(q));
    }
    // For No Vacancy tab: sort oldest contact first (highest days = most urgent)
    if (activeCompanyTab === 'no-vacancy') {
      list = list.slice().sort((a, b) => {
        const da = daysSince(a.date_of_communication) ?? 0;
        const db = daysSince(b.date_of_communication) ?? 0;
        return db - da;
      });
    }
    return list;
  }, [visibleCompanies, filters, activeCompanyTab]);

  const COMPANY_PAGE_SIZE = 10;
  const companyTotalPages = Math.max(1, Math.ceil(filteredCompanies.length / COMPANY_PAGE_SIZE));
  const safeCompanyPage = Math.min(Math.max(companyPage, 1), companyTotalPages);
  const pagedCompanies = filteredCompanies.slice((safeCompanyPage - 1) * COMPANY_PAGE_SIZE, safeCompanyPage * COMPANY_PAGE_SIZE);

  const COORD_PAGE_SIZE = 10;
  const coordTotalPages = Math.max(1, Math.ceil(coordinationItems.length / COORD_PAGE_SIZE));
  const safeCoordPage = Math.min(Math.max(coordPage, 1), coordTotalPages);
  const pagedCoordination = coordinationItems.slice((safeCoordPage - 1) * COORD_PAGE_SIZE, safeCoordPage * COORD_PAGE_SIZE);

  const updateCompanyInState = (updatedCompany) => {
    setCompanies((prev) => prev.map((company) => (company.id === updatedCompany.id ? updatedCompany : company)));
  };

  const handleCommunicationStatusChange = (companyId, nextStatus) => {
    if (nextStatus === 'Call Later') {
      const company = companies.find((c) => c.id === companyId);
      setCallLaterModal(company || { id: companyId });
      setCallLaterDatetime('');
      return;
    }
    saveStatusUpdate(companyId, nextStatus, null);
  };

  const saveStatusUpdate = async (companyId, nextStatus, callLaterDt) => {
    setStatusUpdatingId(companyId);
    setFeedback('');
    try {
      const token = localStorage.getItem('token');
      const payload = { communication_status: nextStatus };
      if (callLaterDt) payload.call_later_datetime = callLaterDt;
      const res = await axios.put(
        `${API_URL}/api/staff/assistant-recruiter/companies/${companyId}/status`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateCompanyInState(res.data.company);
      setFeedback('Communication status updated successfully.');
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to update communication status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleCallLaterSubmit = async () => {
    if (!callLaterModal || !callLaterDatetime) return;
    setCallLaterSaving(true);
    await saveStatusUpdate(callLaterModal.id, 'Call Later', callLaterDatetime);
    setCallLaterSaving(false);
    setCallLaterModal(null);
    setCallLaterDatetime('');
  };

  const handleOpenVacancyForm = (company, vacancy = null) => {
    setSelectedCompany(company);
    setVacancyForm(vacancy ? createVacancyFormFromVacancy(company, vacancy) : createVacancyFormFromCompany(company));
  };

  const handleStartNewVacancy = (company) => {
    handleOpenVacancyForm(company, null);
  };

  const handleVacancyFieldChange = (e) => {
    const { name, value } = e.target;
    setVacancyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCoordinationFieldChange = (e) => {
    const { name, value } = e.target;
    setCoordinationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenCoordination = (item) => {
    setSelectedCoordination(item);
    setCoordinationForm(createCompanyCoordinationForm(item));
  };

  const handleSaveVacancy = async (e) => {
    e.preventDefault();
    if (!selectedCompany) return;

    setVacancySavingId(selectedCompany.id);
    setFeedback('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        date_of_communication: vacancyForm.date_of_communication,
        company_firm_organisation: vacancyForm.company_firm_organisation,
        job_title: vacancyForm.job_title,
        department: vacancyForm.department,
        section: vacancyForm.section,
        salary: vacancyForm.salary,
        gender: vacancyForm.gender,
        age_limit: vacancyForm.age_limit,
        qualification: vacancyForm.qualification,
        experience: vacancyForm.experience,
        place: vacancyForm.place,
        district: vacancyForm.district,
        location_map: vacancyForm.location_map,
        total_vacancies: vacancyForm.total_vacancies,
        contact_person: vacancyForm.contact_person,
        requirement: vacancyForm.requirement
      };

      const isEditingVacancy = Boolean(vacancyForm.vacancy_id);
      const url = isEditingVacancy
        ? `${API_URL}/api/staff/assistant-recruiter/companies/${selectedCompany.id}/vacancy/${vacancyForm.vacancy_id}`
        : `${API_URL}/api/staff/assistant-recruiter/companies/${selectedCompany.id}/vacancy`;
      const request = isEditingVacancy ? axios.put : axios.post;

      const res = await request(
        url,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedCompany = res.data.company;
      updateCompanyInState(updatedCompany);
      setSelectedCompany(updatedCompany);
      fetchInterviewCoordination();
      if (isEditingVacancy) {
        const updatedVacancy = (updatedCompany.vacancies || []).find((vacancy) => String(vacancy.id) === String(vacancyForm.vacancy_id));
        setVacancyForm(updatedVacancy ? createVacancyFormFromVacancy(updatedCompany, updatedVacancy) : createVacancyFormFromCompany(updatedCompany));
        setToast('Vacancy updated and synced to Jobs listing.');
      } else {
        setVacancyForm(createVacancyFormFromCompany(updatedCompany));
        setToast('Vacancy added successfully and synced to Jobs listing.');
      }
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to save vacancy details.');
    } finally {
      setVacancySavingId(null);
    }
  };

  const handleSaveCoordination = async (e) => {
    e.preventDefault();
    if (!selectedCoordination) return;

    setCoordinationSavingId(selectedCoordination.workflow_id);
    setFeedback('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/staff/assistant-recruiter/interview-coordination/${selectedCoordination.workflow_id}`,
        coordinationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.coordination) {
        setCoordinationItems((prev) => prev.map((item) => (
          String(item.workflow_id) === String(res.data.coordination.workflow_id) ? res.data.coordination : item
        )));
        setSelectedCoordination(res.data.coordination);
        setCoordinationForm(createCompanyCoordinationForm(res.data.coordination));
      }
      setFeedback('Company availability updated successfully.');
      setTimeout(() => setSelectedCoordination(null), 1000);
    } catch (err) {
      setFeedback(err?.response?.data?.error || 'Failed to update company interview availability.');
    } finally {
      setCoordinationSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-accent px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1920px] rounded-[28px] border border-yellow-200 bg-white shadow-[0_25px_80px_rgba(0,0,0,0.08)]">
        <div className="rounded-t-[28px] bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.32),transparent_28%),linear-gradient(135deg,#07111d_0%,#14253b_52%,#4d3b00_100%)] px-5 py-3 text-white md:px-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.34em] text-yellow-300">Staff Dashboard</p>
              <h1 className="mt-0.5 text-base font-black">Assistant Recruiter Vacancy Calls</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Leads', value: companies.length },
                  { label: 'Pending', value: pendingCompanies.length },
                  { label: 'Vacancies', value: totalFoundVacancies },
                  { label: 'No Vacancy', value: noVacancyCompanies.length },
                  { label: 'Rejected', value: rejectedCompanies.length },
                  { label: 'Call Later', value: callLaterCompanies.length },
                  { label: 'Interview Q', value: pendingCoordinationItems }
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur-sm text-center">
                    <p className="text-[9px] font-bold uppercase tracking-wide text-yellow-300">{stat.label}</p>
                    <p className="text-sm font-black">{stat.value}</p>
                  </div>
                ))}
              </div>
              <NotificationBell API_URL={API_URL} />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 md:px-8">
          <TargetWidget API_URL={API_URL} />

          {feedback && (
            <div className="mt-5 rounded-2xl border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm font-semibold text-gray-800">
              {feedback}
            </div>
          )}


          <div className="flex flex-wrap items-center gap-2 mt-4">
            {[
              { key: 'pending', label: 'Pending', count: pendingCompanies.length },
              { key: 'found', label: 'Found Vacancies', count: foundVacancyCompanies.length },
              { key: 'no-vacancy', label: 'No Vacancy', count: noVacancyCompanies.length },
              { key: 'rejected', label: 'Rejected', count: rejectedCompanies.length },
              { key: 'call-later', label: `Call Later${dueCallLaterCompanies.length > 0 ? ' 🔔' : ''}`, count: callLaterCompanies.length },
              { key: 'coordination', label: 'Interview Queue', count: pendingCoordinationItems },
              { key: 'internship-requests', label: `Internship Requests${internshipRequestsUnread > 0 ? ' 🔔' : ''}`, count: internshipRequests.length || internshipRequestsUnread }
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => { setActiveCompanyTab(tab.key); setCompanyPage(1); }}
                className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold transition ${activeCompanyTab === tab.key ? 'border-black bg-black text-white' : 'border-gray-200 bg-white text-black hover:border-yellow-400'}`}
              >
                {tab.label}
                <span className={`rounded-full px-2 py-0.5 text-xs font-black ${activeCompanyTab === tab.key ? 'bg-white/20 text-white' : 'bg-yellow-100 text-yellow-800'}`}>{tab.count}</span>
              </button>
            ))}
            <button
              type="button"
              onClick={() => { fetchAssistantRecruiterCompanies(); fetchInterviewCoordination(); fetchInternshipRequests(); }}
              className="ml-auto rounded-xl border border-gray-300 px-4 py-2 text-sm font-bold text-black hover:border-yellow-400"
            >
              ↻ Refresh
            </button>
            {activeCompanyTab !== 'coordination' && (() => {
              const activeFilterCount = Object.entries(filters).filter(([, v]) => v !== '').length;
              return (
                <button
                  type="button"
                  onClick={() => setFilterOpen((o) => !o)}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold transition ${filterOpen || activeFilterCount > 0 ? 'border-yellow-400 bg-yellow-50 text-yellow-800' : 'border-gray-300 text-black hover:border-yellow-400'}`}
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 4h18M7 9h10M11 14h2" /></svg>
                  Filter
                  {activeFilterCount > 0 && <span className="rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-black text-black">{activeFilterCount}</span>}
                </button>
              );
            })()}
          </div>

          {/* ── FILTER PANEL ── */}
          {filterOpen && activeCompanyTab !== 'coordination' && (
            <div className="mt-3 rounded-2xl border border-yellow-200 bg-yellow-50/60 px-5 py-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-extrabold uppercase tracking-[0.22em] text-yellow-800">Filter Companies</p>
                <button
                  type="button"
                  onClick={() => { setFilters({ company_name: '', contact: '', whatsapp: '', has_whatsapp: '', has_vacancy: '', district: '', category: '' }); setCompanyPage(1); }}
                  className="text-xs font-bold text-red-500 hover:underline"
                >
                  Clear All
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Company Name</span>
                  <input
                    type="text"
                    value={filters.company_name}
                    onChange={(e) => { setFilters((f) => ({ ...f, company_name: e.target.value })); setCompanyPage(1); }}
                    placeholder="Search by name..."
                    className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Contact Number</span>
                  <input
                    type="text"
                    value={filters.contact}
                    onChange={(e) => { setFilters((f) => ({ ...f, contact: e.target.value })); setCompanyPage(1); }}
                    placeholder="Search by contact..."
                    className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">WhatsApp Number</span>
                  <input
                    type="text"
                    value={filters.whatsapp}
                    onChange={(e) => { setFilters((f) => ({ ...f, whatsapp: e.target.value })); setCompanyPage(1); }}
                    placeholder="Search by WhatsApp..."
                    className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">District</span>
                  <input
                    type="text"
                    value={filters.district}
                    onChange={(e) => { setFilters((f) => ({ ...f, district: e.target.value })); setCompanyPage(1); }}
                    placeholder="e.g. Ernakulam"
                    className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Category</span>
                  <input
                    type="text"
                    value={filters.category}
                    onChange={(e) => { setFilters((f) => ({ ...f, category: e.target.value })); setCompanyPage(1); }}
                    placeholder="e.g. IT, Manufacturing..."
                    className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                  />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Has WhatsApp</span>
                    <select
                      value={filters.has_whatsapp}
                      onChange={(e) => { setFilters((f) => ({ ...f, has_whatsapp: e.target.value })); setCompanyPage(1); }}
                      className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400"
                    >
                      <option value="">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">Has Vacancy</span>
                    <select
                      value={filters.has_vacancy}
                      onChange={(e) => { setFilters((f) => ({ ...f, has_vacancy: e.target.value })); setCompanyPage(1); }}
                      className="mt-1.5 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-yellow-400"
                    >
                      <option value="">All</option>
                      <option value="yes">With Vacancy</option>
                      <option value="no">No Vacancy</option>
                    </select>
                  </label>
                </div>
              </div>
              {filteredCompanies.length !== visibleCompanies.length && (
                <p className="mt-4 text-xs font-bold text-yellow-700">Showing {filteredCompanies.length} of {visibleCompanies.length} companies</p>
              )}
            </div>
          )}

          {activeCompanyTab === 'coordination' && (
            <div className="mt-5 rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff7e6_100%)] p-4 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-black text-black">Interview Coordination</h3>
                  <p className="mt-1 text-sm text-gray-600">Open each recruiter-linked candidate here and confirm company availability and final acceptance.</p>
                  <p className="mt-0.5 text-xs text-yellow-600 font-semibold">🔄 Auto-refreshing every 30s — recruiter schedule updates will appear automatically.</p>
                </div>
                <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-black text-yellow-800">{coordinationItems.length}</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                {coordinationItems.length === 0 && <div className="rounded-2xl border border-dashed border-yellow-200 bg-white px-4 py-6 text-sm text-gray-500 xl:col-span-2">No recruiter interview coordination items yet.</div>}
                {pagedCoordination.map((item) => (
                  <button
                    key={item.workflow_id}
                    type="button"
                    onClick={() => handleOpenCoordination(item)}
                    className="rounded-2xl border border-yellow-200 bg-white px-4 py-4 text-left transition hover:border-yellow-400 hover:bg-yellow-50/40 xl:col-span-1"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <p className="font-black text-black">{item.name}</p>
                        <button
                          type="button"
                          title="Send Mini CV to WhatsApp"
                          className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[9px] font-bold text-black border border-yellow-600 hover:bg-yellow-500 hover:border-yellow-700 transition flex-shrink-0"
                          style={{minWidth: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                          onClick={e => {
                            e.stopPropagation();
                            import('../utils/miniCV').then(({ generateMiniCV }) => {
                              generateMiniCV(item); // Download for user
                              if (item.whatsapp_number) {
                                const phone = item.whatsapp_number.replace(/\D/g, '');
                                const msg = encodeURIComponent(`Mini CV for candidate ${item.name}. Please see the attached PDF.`);
                                window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
                              } else {
                                alert('No WhatsApp number available for this company.');
                              }
                            });
                          }}
                        >
                          <span role="img" aria-label="Mini CV" className="text-[13px]">📄</span>
                        </button>
                      </div>
                      <span className="rounded-full bg-yellow-100 px-3 py-1 text-[11px] font-bold text-yellow-800">{item.vacancy_availability || 'Pending'}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <p>Candidate: {item.candidate_availability || 'Pending'}</p>
                      <p>Company: {item.vacancy_availability || 'Pending'}</p>
                      <p>HR Confirmation: {item.hr_assistant_confirmation || 'Pending'}</p>
                      <p>Company Confirmation: {item.assistant_recruiter_confirmation || 'Pending'}</p>
                    </div>
                    {item.interview_date && (
                      <div className="mt-2 rounded-xl border border-yellow-300 bg-yellow-50 px-3 py-2 text-xs">
                        <span className="font-black text-yellow-800">📅 Interview Set: </span>
                        <span className="text-yellow-900">{item.interview_date}{item.interview_time ? ` at ${item.interview_time}` : ''}{item.interview_location ? ` · ${item.interview_location}` : ''}</span>
                      </div>
                    )}
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.16em] text-yellow-700">Open Interview Process</p>
                  </button>
                ))}
              </div>
              {coordTotalPages > 1 && (
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-yellow-200 pt-3">
                  <span className="text-xs text-gray-500 mr-auto">Page {safeCoordPage} of {coordTotalPages} · {coordinationItems.length} items</span>
                  <button type="button" onClick={() => setCoordPage(1)} disabled={safeCoordPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">«</button>
                  <button type="button" onClick={() => setCoordPage((p) => Math.max(p - 1, 1))} disabled={safeCoordPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">‹ Prev</button>
                  {Array.from({ length: coordTotalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === coordTotalPages || Math.abs(p - safeCoordPage) <= 1).map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 text-xs px-1">…</span>}
                      <button type="button" onClick={() => setCoordPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${safeCoordPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-yellow-400'}`}>{p}</button>
                    </React.Fragment>
                  ))}
                  <button type="button" onClick={() => setCoordPage((p) => Math.min(p + 1, coordTotalPages))} disabled={safeCoordPage === coordTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">Next ›</button>
                  <button type="button" onClick={() => setCoordPage(coordTotalPages)} disabled={safeCoordPage === coordTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">»</button>
                </div>
              )}
            </div>
          )}

          {/* ── INTERNSHIP REQUESTS TAB ── */}
          {activeCompanyTab === 'internship-requests' && (() => {
            const STATUS_COLORS = {
              'Pending':      'border-gray-300 bg-gray-50 text-gray-600',
              'In Progress':  'border-blue-300 bg-blue-50 text-blue-700',
              'Vacancy Found':'border-emerald-300 bg-emerald-50 text-emerald-700',
              'Placed':       'border-green-400 bg-green-50 text-green-800',
              'No Vacancy':   'border-red-300 bg-red-50 text-red-600',
              'Try Later':    'border-yellow-300 bg-yellow-50 text-yellow-700',
            };
            const CALL_STATUS_COLORS = {
              'Vacancy Found':  'bg-emerald-100 text-emerald-700',
              'No Vacancy':     'bg-red-100 text-red-600',
              'Call Later':     'bg-yellow-100 text-yellow-700',
              'Not Reachable':  'bg-gray-100 text-gray-500',
            };
            const STATUS_OPTIONS = ['All','Pending','In Progress','Vacancy Found','Placed','No Vacancy','Try Later'];
            const filtered = internshipStatusFilter === 'All' ? internshipRequests : internshipRequests.filter(r => (r.ar_status || 'Pending') === internshipStatusFilter);
            return (
              <div className="mt-5 space-y-4">
                {/* Header */}
                <div className="rounded-[24px] border border-blue-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                      <h3 className="text-lg font-black text-blue-700">Internship Requests</h3>
                      <p className="mt-0.5 text-sm text-gray-500">Call companies, track responses, and manage applicant placements.</p>
                    </div>
                    <button type="button" onClick={fetchInternshipRequests}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-bold text-gray-600 hover:border-blue-300 transition">
                      ↻ Refresh
                    </button>
                  </div>

                  {/* Status filter tabs */}
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map(s => {
                      const cnt = s === 'All' ? internshipRequests.length : internshipRequests.filter(r => (r.ar_status || 'Pending') === s).length;
                      return (
                        <button key={s} type="button" onClick={() => setInternshipStatusFilter(s)}
                          className={`flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-bold transition border ${internshipStatusFilter === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}>
                          {s} <span className={`rounded-full px-1.5 text-[10px] font-black ${internshipStatusFilter === s ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{cnt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {internshipRequestsLoading && <p className="py-8 text-center text-sm text-gray-400">Loading...</p>}
                {!internshipRequestsLoading && filtered.length === 0 && (
                  <div className="rounded-2xl border border-dashed border-blue-200 py-12 text-center text-sm text-gray-400">
                    {internshipRequests.length === 0 ? 'No internship requests forwarded to you yet.' : 'No requests match this filter.'}
                  </div>
                )}

                {!internshipRequestsLoading && filtered.map((row, idx) => {
                  const status = row.ar_status || 'Pending';
                  const isNew = !row.ar_is_seen;
                  return (
                    <div key={row.id} className="rounded-[20px] border border-gray-200 bg-white shadow-sm overflow-hidden">
                      {/* Card header */}
                      <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-black text-blue-700">{idx + 1}</span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-black text-sm text-gray-900">{row.full_name}</p>
                              {isNew && <span className="rounded-full bg-blue-500 px-2 py-0.5 text-[10px] font-extrabold text-white uppercase">New</span>}
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${STATUS_COLORS[status] || STATUS_COLORS['Pending']}`}>{status}</span>
                            </div>
                            <p className="text-xs text-gray-500 truncate mt-0.5">{row.course || '—'} · {row.desired_industry || '—'} · {row.availability || '—'}</p>
                          </div>
                        </div>
                        <button type="button" onClick={() => openInternshipManage(row)}
                          className="shrink-0 rounded-xl bg-blue-600 px-4 py-2 text-xs font-extrabold text-white hover:bg-blue-700 transition">
                          Manage
                        </button>
                      </div>
                      {/* Quick info strip */}
                      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-2 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-gray-500">
                        <span><span className="font-bold text-gray-700">Contact:</span> {row.contact_number}</span>
                        <span><span className="font-bold text-gray-700">Email:</span> {row.email_address}</span>
                        <span><span className="font-bold text-gray-700">Location:</span> {row.desired_location || '—'}</span>
                        {row.forwarded_at && <span><span className="font-bold text-gray-700">Received:</span> {new Date(row.forwarded_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                        {row.ar_notes && <span className="text-blue-600 font-semibold italic truncate max-w-xs">Note: {row.ar_notes}</span>}
                      </div>
                    </div>
                  );
                })}

                {/* ── Manage Modal ── */}
                {internshipManageModal && (() => {
                  const row = internshipManageModal;
                  const status = row.ar_status || 'Pending';
                  const CALL_STATUSES = ['Vacancy Found', 'No Vacancy', 'Call Later', 'Not Reachable'];
                  const AR_STATUSES = ['Pending', 'In Progress', 'Vacancy Found', 'Placed', 'No Vacancy', 'Try Later'];
                  return (
                    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm px-0 sm:px-4">
                      <div className="w-full sm:max-w-2xl max-h-[92vh] flex flex-col rounded-t-[28px] sm:rounded-[28px] border border-blue-200 bg-white shadow-2xl overflow-hidden">

                        {/* Modal header */}
                        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 shrink-0">
                          <div>
                            <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-500">Internship Request</p>
                            <h3 className="mt-0.5 text-lg font-black text-gray-900">{row.full_name}</h3>
                            <p className="text-xs text-gray-500">{row.course || '—'} · {row.desired_industry || '—'} · {row.desired_location || '—'}</p>
                          </div>
                          <button type="button" onClick={() => setInternshipManageModal(null)}
                            className="shrink-0 rounded-full border border-gray-200 px-3 py-1 text-xs font-bold text-gray-500 hover:border-red-300 hover:text-red-500 transition">
                            ✕
                          </button>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-100 px-5 shrink-0">
                          {[['calls','Company Calls'],['applicant','Applicant Details'],['status','Update Status']].map(([key, label]) => (
                            <button key={key} type="button" onClick={() => setInternshipManageTab(key)}
                              className={`px-4 py-3 text-xs font-extrabold uppercase tracking-wide border-b-2 transition ${internshipManageTab === key ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-400 hover:text-gray-700'}`}>
                              {label}
                            </button>
                          ))}
                        </div>

                        {/* Modal body */}
                        <div className="flex-1 overflow-y-auto px-5 py-5">

                          {/* ── Company Calls tab ── */}
                          {internshipManageTab === 'calls' && (
                            <div className="space-y-5">
                              {/* Add call form */}
                              <div className="rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                                <p className="mb-3 text-xs font-extrabold uppercase tracking-widest text-blue-600">Add Company Call</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <label className="block sm:col-span-2">
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Company Name *</span>
                                    <input value={internshipCallForm.company_name}
                                      onChange={e => setInternshipCallForm(p => ({...p, company_name: e.target.value}))}
                                      placeholder="Enter company name"
                                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100" />
                                  </label>
                                  <label className="block">
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Contact Number</span>
                                    <input value={internshipCallForm.contact_number}
                                      onChange={e => setInternshipCallForm(p => ({...p, contact_number: e.target.value.replace(/\D/g,'').slice(0,10)}))}
                                      placeholder="Company contact"
                                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100" />
                                  </label>
                                  <label className="block">
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Call Date</span>
                                    <input type="date" value={internshipCallForm.call_date}
                                      onChange={e => setInternshipCallForm(p => ({...p, call_date: e.target.value}))}
                                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100" />
                                  </label>
                                  <label className="block sm:col-span-2">
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Call Result *</span>
                                    <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      {[
                                        {v:'Vacancy Found', color:'border-emerald-400 bg-emerald-50 text-emerald-700'},
                                        {v:'No Vacancy', color:'border-red-400 bg-red-50 text-red-600'},
                                        {v:'Call Later', color:'border-yellow-400 bg-yellow-50 text-yellow-700'},
                                        {v:'Not Reachable', color:'border-gray-300 bg-gray-50 text-gray-600'},
                                      ].map(({v, color}) => (
                                        <button key={v} type="button" onClick={() => setInternshipCallForm(p => ({...p, call_status: v}))}
                                          className={`rounded-xl border-2 px-3 py-2 text-xs font-extrabold text-center transition ${internshipCallForm.call_status === v ? color + ' ring-2 ring-offset-1 ring-blue-300' : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'}`}>
                                          {v}
                                        </button>
                                      ))}
                                    </div>
                                  </label>
                                  {internshipCallForm.call_status === 'Vacancy Found' && (
                                    <label className="block sm:col-span-2">
                                      <span className="text-[11px] font-bold text-emerald-600 uppercase tracking-wide">Vacancy Details</span>
                                      <textarea value={internshipCallForm.vacancy_details}
                                        onChange={e => setInternshipCallForm(p => ({...p, vacancy_details: e.target.value}))}
                                        placeholder="Describe the vacancy: position, stipend, duration, start date..."
                                        rows={3}
                                        className="mt-1 w-full rounded-xl border border-emerald-300 bg-emerald-50/30 px-3 py-2 text-sm outline-none focus:border-emerald-400 resize-none" />
                                    </label>
                                  )}
                                  {internshipCallForm.call_status === 'Call Later' && (
                                    <label className="block sm:col-span-2">
                                      <span className="text-[11px] font-bold text-yellow-600 uppercase tracking-wide">Schedule Callback</span>
                                      <input type="datetime-local" value={internshipCallForm.call_later_datetime}
                                        onChange={e => setInternshipCallForm(p => ({...p, call_later_datetime: e.target.value}))}
                                        className="mt-1 w-full rounded-xl border border-yellow-300 bg-yellow-50/30 px-3 py-2 text-sm outline-none focus:border-yellow-400" />
                                    </label>
                                  )}
                                  <label className="block sm:col-span-2">
                                    <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Notes</span>
                                    <textarea value={internshipCallForm.notes}
                                      onChange={e => setInternshipCallForm(p => ({...p, notes: e.target.value}))}
                                      placeholder="Any additional notes..."
                                      rows={2}
                                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-400 resize-none" />
                                  </label>
                                </div>
                                <div className="mt-3 flex justify-end">
                                  <button type="button" onClick={handleAddInternshipCall} disabled={internshipCallSaving || !internshipCallForm.company_name || !internshipCallForm.call_status}
                                    className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-extrabold text-white hover:bg-blue-700 transition disabled:opacity-50">
                                    {internshipCallSaving ? 'Saving...' : 'Save Call'}
                                  </button>
                                </div>
                              </div>

                              {/* Call history */}
                              <div>
                                <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Call History ({internshipCalls.length})</p>
                                {internshipCallsLoading && <p className="text-xs text-gray-400 py-4 text-center">Loading...</p>}
                                {!internshipCallsLoading && internshipCalls.length === 0 && (
                                  <div className="rounded-2xl border border-dashed border-gray-200 py-6 text-center text-xs text-gray-400">No calls logged yet. Add your first company call above.</div>
                                )}
                                <div className="space-y-2">
                                  {internshipCalls.map(call => (
                                    <div key={call.id} className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <p className="text-sm font-black text-gray-900">{call.company_name}</p>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${CALL_STATUS_COLORS[call.call_status] || 'bg-gray-100 text-gray-500'}`}>{call.call_status}</span>
                                          </div>
                                          {call.contact_number && <p className="text-xs text-gray-500 mt-0.5">📞 {call.contact_number}</p>}
                                          {call.vacancy_details && (
                                            <div className="mt-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                                              <p className="font-bold mb-0.5">Vacancy Details:</p>
                                              <p className="leading-5">{call.vacancy_details}</p>
                                            </div>
                                          )}
                                          {call.call_later_datetime && (
                                            <p className="mt-1 text-xs text-yellow-700 font-semibold">⏰ Callback: {new Date(call.call_later_datetime).toLocaleString('en-IN')}</p>
                                          )}
                                          {call.notes && <p className="text-xs text-gray-500 mt-1 italic">{call.notes}</p>}
                                        </div>
                                        <div className="shrink-0 text-right">
                                          <p className="text-[10px] text-gray-400">{call.call_date || new Date(call.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short' })}</p>
                                          <button type="button" onClick={() => handleDeleteInternshipCall(call.id)}
                                            className="mt-1 text-[10px] text-red-400 hover:text-red-600 font-bold transition">Delete</button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* ── Applicant Details tab ── */}
                          {internshipManageTab === 'applicant' && (
                            <div className="space-y-4">
                              {/* Shareable summary box */}
                              <div className="rounded-2xl border border-blue-200 bg-blue-50/40 px-4 py-4">
                                <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-500 mb-2">Candidate Summary (share with company)</p>
                                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans leading-6 select-all">{[
                                  `Name        : ${row.full_name}`,
                                  `Age / Gender: ${row.age || '—'} / ${row.gender || '—'}`,
                                  `Course      : ${row.course || '—'} (${row.year_semester || '—'})`,
                                  `Institution : ${row.institution_name || '—'}`,
                                  `Industry    : ${row.desired_industry || '—'}`,
                                  `Position    : ${row.desired_position || '—'}`,
                                  `Location    : ${row.desired_location || '—'}`,
                                  `Availability: ${row.availability || '—'}`,
                                  `Start Date  : ${row.preferred_start_date || '—'}`,
                                  `Contact     : ${row.contact_number}`,
                                  `WhatsApp    : ${row.whatsapp_number || '—'}`,
                                  `Email       : ${row.email_address}`,
                                ].join('\n')}</pre>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-500 mb-2">Personal</p>
                                  <div className="space-y-1.5 text-xs">
                                    {[['Name', row.full_name],['Address', row.address],['Contact', row.contact_number],['WhatsApp', row.whatsapp_number],['Email', row.email_address],['Age', row.age],['Gender', row.gender]].map(([l,v]) => (
                                      <p key={l}><span className="font-bold text-gray-600">{l}:</span> {v || '—'}</p>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-500 mb-2">Education</p>
                                  <div className="space-y-1.5 text-xs">
                                    {[['Institution', row.institution_name],['Address', row.institution_address],['Course', row.course],['Year/Sem', row.year_semester]].map(([l,v]) => (
                                      <p key={l}><span className="font-bold text-gray-600">{l}:</span> {v || '—'}</p>
                                    ))}
                                  </div>
                                  <p className="mt-4 text-[10px] font-extrabold uppercase tracking-widest text-yellow-600 mb-2">Internship Preferences</p>
                                  <div className="space-y-1.5 text-xs">
                                    {[['Industry', row.desired_industry],['Position', row.desired_position],['Location', row.desired_location],['Start', row.preferred_start_date],['End', row.preferred_end_date],['Availability', row.availability]].map(([l,v]) => (
                                      <p key={l}><span className="font-bold text-gray-600">{l}:</span> {v || '—'}</p>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* ── Update Status tab ── */}
                          {internshipManageTab === 'status' && (
                            <div className="space-y-4">
                              <div>
                                <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Current Status</p>
                                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-extrabold uppercase tracking-wide ${({
                                  'Pending':'border-gray-300 bg-gray-50 text-gray-600',
                                  'In Progress':'border-blue-300 bg-blue-50 text-blue-700',
                                  'Vacancy Found':'border-emerald-300 bg-emerald-50 text-emerald-700',
                                  'Placed':'border-green-400 bg-green-50 text-green-800',
                                  'No Vacancy':'border-red-300 bg-red-50 text-red-600',
                                  'Try Later':'border-yellow-300 bg-yellow-50 text-yellow-700',
                                })[status] || 'border-gray-200'}`}>{status}</span>
                              </div>

                              <div>
                                <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-gray-500">Set New Status</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  {AR_STATUSES.map(s => (
                                    <button key={s} type="button" onClick={() => setInternshipStatusForm(p => ({...p, ar_status: s}))}
                                      className={`rounded-xl border-2 px-3 py-2.5 text-xs font-extrabold text-center transition ${internshipStatusForm.ar_status === s
                                        ? {
                                            'Pending':'border-gray-400 bg-gray-100 text-gray-700',
                                            'In Progress':'border-blue-500 bg-blue-100 text-blue-800',
                                            'Vacancy Found':'border-emerald-500 bg-emerald-100 text-emerald-800',
                                            'Placed':'border-green-500 bg-green-100 text-green-800',
                                            'No Vacancy':'border-red-400 bg-red-100 text-red-700',
                                            'Try Later':'border-yellow-400 bg-yellow-100 text-yellow-800',
                                          }[s] || 'border-blue-400 bg-blue-50 text-blue-700'
                                        : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                                      }`}>
                                      {s}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              <div>
                                <p className="mb-1 text-xs font-extrabold uppercase tracking-widest text-gray-500">Notes / Remarks</p>
                                <textarea value={internshipStatusForm.ar_notes}
                                  onChange={e => setInternshipStatusForm(p => ({...p, ar_notes: e.target.value}))}
                                  placeholder="Add any notes about this request..."
                                  rows={3}
                                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-blue-400 resize-none" />
                              </div>

                              <button type="button" onClick={handleUpdateInternshipStatus} disabled={internshipStatusSaving}
                                className="w-full rounded-xl bg-blue-600 py-3 text-sm font-extrabold text-white hover:bg-blue-700 transition disabled:opacity-50">
                                {internshipStatusSaving ? 'Saving...' : 'Save Status'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}

          {loading ? (
            <div className="mt-6 text-sm text-gray-600">Loading company call list...</div>
          ) : null}

          {/* ── CALL LATER TAB ── */}
          {!loading && activeCompanyTab === 'call-later' && (
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-4">
                <p className="text-sm font-bold text-blue-800">⏰ These companies are scheduled for a callback. The 🔔 bell appears when the call time has arrived. Update the status after you call.</p>
              </div>
              {filteredCompanies.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-blue-200 bg-white px-4 py-10 text-center text-sm text-gray-500">{visibleCompanies.length === 0 ? 'No companies scheduled for callback.' : 'No results match the current filter.'}</div>
              ) : (
                <div className="rounded-2xl border border-blue-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white text-sm">
                      <thead className="bg-blue-50 text-left">
                        <tr>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Company</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Category</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">District</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Contacts</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Whatsapp</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Call Scheduled</th>
                          <th className="border-b border-blue-100 px-3 py-3 font-extrabold text-blue-800">Update Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCompanies.map((company) => {
                          const isDue = company.call_later_datetime && new Date(company.call_later_datetime) <= new Date();
                          const scheduledDate = company.call_later_datetime ? new Date(company.call_later_datetime) : null;
                          return (
                            <tr key={company.id} className={`align-top ${isDue ? 'bg-blue-50/60' : ''}`}>
                              <td className="border-b border-blue-100 px-3 py-3">
                                <p className="font-semibold text-black">{company.company_name || '-'}</p>
                                <PartnerVacancyTag company={company} />
                                <p className="mt-0.5 text-xs text-gray-400">Added by {company.created_by_name || 'Data Analyst'}</p>
                              </td>
                              <td className="border-b border-blue-100 px-3 py-3 text-gray-700">{company.category || '-'}</td>
                              <td className="border-b border-blue-100 px-3 py-3 text-gray-700">{company.district || '-'}</td>
                              <td className="border-b border-blue-100 px-3 py-3 text-gray-700"><ContactList value={company.contact_numbers_display} /></td>
                              <td className="border-b border-blue-100 px-3 py-3 text-gray-700"><ContactLink value={company.whatsapp_number} type="whatsapp" /></td>
                              <td className="border-b border-blue-100 px-3 py-3">
                                {scheduledDate ? (
                                  <div className={`inline-flex flex-col gap-0.5 rounded-xl border px-3 py-2 text-xs font-bold ${isDue ? 'border-blue-400 bg-blue-100 text-blue-800' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                                    <span>{scheduledDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                                    <span>{scheduledDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isDue && <span className="text-blue-600 font-black">🔔 Call Now!</span>}
                                  </div>
                                ) : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="border-b border-blue-100 px-3 py-3">
                                <select
                                  value={company.communication_status || 'Call Later'}
                                  onChange={(e) => handleCommunicationStatusChange(company.id, e.target.value)}
                                  disabled={statusUpdatingId === company.id}
                                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-400"
                                >
                                  {assistantRecruiterStatusOptions.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {companyTotalPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-blue-100 bg-blue-50/40 px-4 py-3">
                      <span className="text-xs text-gray-600 mr-auto">Page {safeCompanyPage} of {companyTotalPages} · {filteredCompanies.length} records</span>
                      <button type="button" onClick={() => setCompanyPage(1)} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-blue-400 disabled:opacity-50">«</button>
                      <button type="button" onClick={() => setCompanyPage((p) => Math.max(p - 1, 1))} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-blue-400 disabled:opacity-50">‹ Prev</button>
                      {Array.from({ length: companyTotalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === companyTotalPages || Math.abs(p - safeCompanyPage) <= 1).map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 text-xs px-1">…</span>}
                          <button type="button" onClick={() => setCompanyPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${safeCompanyPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-blue-400'}`}>{p}</button>
                        </React.Fragment>
                      ))}
                      <button type="button" onClick={() => setCompanyPage((p) => Math.min(p + 1, companyTotalPages))} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-blue-400 disabled:opacity-50">Next ›</button>
                      <button type="button" onClick={() => setCompanyPage(companyTotalPages)} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-blue-400 disabled:opacity-50">»</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── REJECTED TAB ── */}
          {!loading && activeCompanyTab === 'rejected' && (
            <div className="mt-6 rounded-2xl border border-red-200 overflow-hidden">
              {filteredCompanies.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">{visibleCompanies.length === 0 ? 'No rejected companies.' : 'No results match the current filter.'}</div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white text-sm">
                      <thead className="bg-red-50 text-left">
                        <tr>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Entry Date</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Company</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Category</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Location</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">District</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Contacts</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Whatsapp</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Date of Communication</th>
                          <th className="border-b border-red-100 px-3 py-3 font-extrabold text-red-700">Change Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCompanies.map((company) => (
                          <tr key={company.id} className="hover:bg-red-50/30 align-top">
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700">{company.entry_date || '-'}</td>
                            <td className="border-b border-red-100 px-3 py-3">
                              <p className="font-semibold text-black">{company.company_name || '-'}</p>
                                <PartnerVacancyTag company={company} />
                              <p className="mt-0.5 text-xs text-gray-400">{company.category || ''}</p>
                            </td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700">{company.category || '-'}</td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700">{company.location || '-'}</td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700">{company.district || '-'}</td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700"><ContactList value={company.contact_numbers_display} /></td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700"><ContactLink value={company.whatsapp_number} type="whatsapp" /></td>
                            <td className="border-b border-red-100 px-3 py-3 text-gray-700">
                              <p className="text-sm">{company.date_of_communication || '-'}</p>
                              {(() => {
                                const days = daysSince(company.date_of_communication);
                                if (days === null) return null;
                                return <p className="mt-0.5 text-xs font-bold text-red-600">{days}d ago</p>;
                              })()}
                            </td>
                            <td className="border-b border-red-100 px-3 py-3">
                              <select
                                value={company.communication_status || 'Rejected'}
                                onChange={(e) => handleCommunicationStatusChange(company.id, e.target.value)}
                                disabled={statusUpdatingId === company.id}
                                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-400"
                              >
                                {assistantRecruiterStatusOptions.map((status) => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {companyTotalPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-red-100 bg-red-50/40 px-4 py-3">
                      <span className="text-xs text-gray-600 mr-auto">Page {safeCompanyPage} of {companyTotalPages} · {filteredCompanies.length} records</span>
                      <button type="button" onClick={() => setCompanyPage(1)} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-red-400 disabled:opacity-50">«</button>
                      <button type="button" onClick={() => setCompanyPage((p) => Math.max(p - 1, 1))} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-red-400 disabled:opacity-50">‹ Prev</button>
                      {Array.from({ length: companyTotalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === companyTotalPages || Math.abs(p - safeCompanyPage) <= 1).map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 text-xs px-1">…</span>}
                          <button type="button" onClick={() => setCompanyPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${safeCompanyPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-red-400'}`}>{p}</button>
                        </React.Fragment>
                      ))}
                      <button type="button" onClick={() => setCompanyPage((p) => Math.min(p + 1, companyTotalPages))} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-red-400 disabled:opacity-50">Next ›</button>
                      <button type="button" onClick={() => setCompanyPage(companyTotalPages)} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-red-400 disabled:opacity-50">»</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── NO VACANCY TAB ── */}
          {!loading && activeCompanyTab === 'no-vacancy' && (
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4">
                <p className="text-sm font-bold text-orange-800">💡 These companies currently have no vacancies. Use the days counter to identify companies that were contacted long ago — they may have openings now. Re-contact and update the status accordingly.</p>
              </div>
              {filteredCompanies.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-orange-200 bg-white px-4 py-10 text-center text-sm text-gray-500">{visibleCompanies.length === 0 ? 'No companies in the No Vacancy list.' : 'No results match the current filter.'}</div>
              ) : (
                <div className="rounded-2xl border border-orange-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white text-sm">
                      <thead className="bg-orange-50 text-left">
                        <tr>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Company</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Category</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Location</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">District</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Contacts</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Whatsapp</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Last Contact</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Days Since</th>
                          <th className="border-b border-orange-100 px-3 py-3 font-extrabold text-orange-800">Update Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedCompanies.map((company) => {
                          const days = daysSince(company.date_of_communication);
                          const urgency = days === null ? 'none'
                            : days >= 30 ? 'high'
                            : days >= 14 ? 'medium'
                            : 'low';
                          const urgencyColors = {
                            high: 'bg-red-100 text-red-700 border-red-300',
                            medium: 'bg-orange-100 text-orange-700 border-orange-300',
                            low: 'bg-yellow-100 text-yellow-700 border-yellow-300',
                            none: 'bg-gray-100 text-gray-500 border-gray-200'
                          };
                          return (
                            <tr key={company.id} className={`align-top ${urgency === 'high' ? 'bg-red-50/30' : ''}`}>
                              <td className="border-b border-orange-100 px-3 py-3">
                                <p className="font-semibold text-black">{company.company_name || '-'}</p>
                                <PartnerVacancyTag company={company} />
                                <p className="mt-0.5 text-xs text-gray-400">Added by {company.created_by_name || 'Data Analyst'}</p>
                              </td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700">{company.category || '-'}</td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700">{company.location || '-'}</td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700">{company.district || '-'}</td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700"><ContactList value={company.contact_numbers_display} /></td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700"><ContactLink value={company.whatsapp_number} type="whatsapp" /></td>
                              <td className="border-b border-orange-100 px-3 py-3 text-gray-700 text-sm">{company.date_of_communication || '—'}</td>
                              <td className="border-b border-orange-100 px-3 py-4">
                                {days !== null ? (
                                  <div className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-black ${urgencyColors[urgency]}`}>
                                    <span>{days}</span>
                                    <span className="font-normal">days ago</span>
                                    {urgency === 'high' && <span>🔔</span>}
                                  </div>
                                ) : <span className="text-gray-400 text-xs">—</span>}
                                {urgency === 'high' && <p className="mt-1 text-[10px] text-red-600 font-bold">Try re-contacting now!</p>}
                              </td>
                              <td className="border-b border-orange-100 px-3 py-3">
                                <select
                                  value={company.communication_status || 'No Vacancies'}
                                  onChange={(e) => handleCommunicationStatusChange(company.id, e.target.value)}
                                  disabled={statusUpdatingId === company.id}
                                  className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-orange-400"
                                >
                                  {assistantRecruiterStatusOptions.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {companyTotalPages > 1 && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-orange-100 bg-orange-50/40 px-4 py-3">
                      <span className="text-xs text-gray-600 mr-auto">Page {safeCompanyPage} of {companyTotalPages} · {filteredCompanies.length} records · oldest contact first</span>
                      <button type="button" onClick={() => setCompanyPage(1)} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-orange-400 disabled:opacity-50">«</button>
                      <button type="button" onClick={() => setCompanyPage((p) => Math.max(p - 1, 1))} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-orange-400 disabled:opacity-50">‹ Prev</button>
                      {Array.from({ length: companyTotalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === companyTotalPages || Math.abs(p - safeCompanyPage) <= 1).map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 text-xs px-1">…</span>}
                          <button type="button" onClick={() => setCompanyPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${safeCompanyPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-orange-400'}`}>{p}</button>
                        </React.Fragment>
                      ))}
                      <button type="button" onClick={() => setCompanyPage((p) => Math.min(p + 1, companyTotalPages))} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-orange-400 disabled:opacity-50">Next ›</button>
                      <button type="button" onClick={() => setCompanyPage(companyTotalPages)} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-orange-400 disabled:opacity-50">»</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!loading && activeCompanyTab !== 'coordination' && activeCompanyTab !== 'rejected' && activeCompanyTab !== 'no-vacancy' && activeCompanyTab !== 'call-later' && (
            <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-200">
              <table className="min-w-full bg-white text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Entry Date</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Company</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Category</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Location</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">District</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Contacts</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Whatsapp</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Communication Status</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Date of Communication</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Vacancy</th>
                    <th className="border-b px-3 py-3 font-extrabold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCompanies.length === 0 && (
                    <tr>
                      <td colSpan="11" className="px-3 py-6 text-center text-gray-500">
                        {visibleCompanies.length === 0
                          ? (activeCompanyTab === 'found' ? 'No companies with saved vacancies yet.' : 'No pending company follow-up items right now.')
                          : 'No results match the current filter.'}
                      </td>
                    </tr>
                  )}
                  {pagedCompanies.map((company) => (
                    <tr key={company.id} className="align-top">
                      <td className="border-b px-3 py-3 text-gray-700">{company.entry_date || '-'}</td>
                      <td className="border-b px-3 py-3">
                        <p className="font-semibold text-black">{company.company_name || '-'}</p>
                                <PartnerVacancyTag company={company} />
                        <p className="mt-1 text-xs text-gray-500">Added by {company.created_by_name || 'Data Analyst'}</p>
                      </td>
                      <td className="border-b px-3 py-3 text-gray-700">{company.category || '-'}</td>
                      <td className="border-b px-3 py-3 text-gray-700">{company.location || '-'}</td>
                      <td className="border-b px-3 py-3 text-gray-700">{company.district || '-'}</td>
                      <td className="border-b px-3 py-3 text-gray-700"><ContactList value={company.contact_numbers_display} /></td>
                      <td className="border-b px-3 py-3 text-gray-700"><ContactLink value={company.whatsapp_number} type="whatsapp" /></td>
                      <td className="border-b px-3 py-3">
                        <select
                          value={company.communication_status || 'Pending'}
                          onChange={(e) => handleCommunicationStatusChange(company.id, e.target.value)}
                          disabled={statusUpdatingId === company.id}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-yellow-400"
                        >
                          {assistantRecruiterStatusOptions.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </td>
                      <td className="border-b px-3 py-3 text-gray-700">{company.date_of_communication || '-'}</td>
                      <td className="border-b px-3 py-3 text-gray-700">
                        {Array.isArray(company.vacancies) && company.vacancies.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-500">{company.vacancies.length} vacancy{company.vacancies.length > 1 ? 'ies' : 'y'} saved</p>
                            {company.vacancies.slice(0, 3).map((vacancy) => (
                              <button
                                key={vacancy.id}
                                type="button"
                                onClick={() => handleOpenVacancyForm(company, vacancy)}
                                className="block w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-left transition hover:border-yellow-400 hover:bg-yellow-50"
                              >
                                <p className="font-semibold text-black">{vacancy.job_title}</p>
                                <p className="mt-1 text-xs text-gray-500">{vacancy.total_vacancies ? `${vacancy.total_vacancies} openings` : 'Vacancy saved'}</p>
                              </button>
                            ))}
                            {company.vacancies.length > 3 && <p className="text-xs text-gray-500">+{company.vacancies.length - 3} more saved vacancies</p>}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="border-b px-3 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleStartNewVacancy(company)}
                            disabled={company.communication_status !== 'Found Vacancies'}
                            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                              company.communication_status === 'Found Vacancies'
                                ? 'border border-emerald-500 text-emerald-700 hover:bg-emerald-50'
                                : 'cursor-not-allowed border border-gray-300 text-gray-400'
                            }`}
                          >
                            {company.vacancy_count ? 'Add Another Vacancy' : 'Add Vacancy'}
                          </button>
                          {company.vacancy_count > 0 && (
                            <button
                              type="button"
                              onClick={() => handleOpenVacancyForm(company, company.vacancies[0])}
                              className="rounded-lg border border-yellow-500 px-3 py-1.5 text-xs font-bold text-yellow-700 transition hover:bg-yellow-50"
                            >
                              Manage Vacancies
                            </button>
                          )}
                          {company.website && (
                            <a
                              href={company.website}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-lg border border-blue-500 px-3 py-1.5 text-xs font-bold text-blue-600 transition hover:bg-blue-50"
                            >
                              Website
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {companyTotalPages > 1 && (
                <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
                  <span className="text-xs text-gray-600 mr-auto">Page {safeCompanyPage} of {companyTotalPages} · {filteredCompanies.length} records{filteredCompanies.length !== visibleCompanies.length ? ` (filtered from ${visibleCompanies.length})` : ''}</span>
                  <button type="button" onClick={() => setCompanyPage(1)} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">«</button>
                  <button type="button" onClick={() => setCompanyPage((p) => Math.max(p - 1, 1))} disabled={safeCompanyPage === 1} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">‹ Prev</button>
                  {Array.from({ length: companyTotalPages }, (_, i) => i + 1).filter((p) => p === 1 || p === companyTotalPages || Math.abs(p - safeCompanyPage) <= 1).map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && <span className="text-gray-400 text-xs px-1">…</span>}
                      <button type="button" onClick={() => setCompanyPage(p)} className={`rounded-xl px-3 py-2 text-xs font-bold transition ${safeCompanyPage === p ? 'bg-black text-yellow-300' : 'border border-gray-300 text-gray-700 hover:border-yellow-400'}`}>{p}</button>
                    </React.Fragment>
                  ))}
                  <button type="button" onClick={() => setCompanyPage((p) => Math.min(p + 1, companyTotalPages))} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">Next ›</button>
                  <button type="button" onClick={() => setCompanyPage(companyTotalPages)} disabled={safeCompanyPage === companyTotalPages} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-black hover:border-yellow-400 disabled:opacity-50">»</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCallLaterPopup && dueCallLaterCompanies.length > 0 && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-[0_30px_100px_rgba(0,0,0,0.38)]">
            <div className="flex items-start gap-4 border-b border-blue-100 pb-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100 text-2xl">🔔</div>
              <div className="flex-1">
                <p className="text-xs font-extrabold uppercase tracking-widest text-blue-600">Call Later Reminder</p>
                <h3 className="mt-1 text-lg font-black text-black">Time to call!</h3>
                <p className="mt-1 text-sm text-gray-500">{dueCallLaterCompanies.length} compan{dueCallLaterCompanies.length > 1 ? 'ies' : 'y'} scheduled for a callback right now.</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {dueCallLaterCompanies.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-xl border border-blue-100 bg-blue-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-bold text-black">{c.company_name}</p>
                    <p className="text-xs text-gray-500">{c.district} · {c.category}</p>
                  </div>
                  {c.contact_numbers_display && (
                    <a href={`tel:${c.contact_numbers_display.split(',')[0]?.replace(/\D/g,'')}`} className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700">Call</a>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setShowCallLaterPopup(false)}
                className="flex-1 rounded-2xl border border-gray-300 px-4 py-3 text-sm font-bold text-black hover:border-gray-400"
              >
                Dismiss
              </button>
              <button
                type="button"
                onClick={() => { setShowCallLaterPopup(false); setActiveCompanyTab('call-later'); setCompanyPage(1); }}
                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700"
              >
                View All
              </button>
            </div>
          </div>
        </div>
      )}

      {callLaterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-[0_30px_100px_rgba(0,0,0,0.32)]">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest text-blue-600">Call Later</p>
                <h3 className="mt-1 text-lg font-black text-black">{callLaterModal.company_name || 'Company'}</h3>
                <p className="mt-1 text-sm text-gray-500">Set the date and time you want to be reminded to call this company.</p>
              </div>
              <button type="button" onClick={() => setCallLaterModal(null)} className="rounded-xl border border-gray-300 px-3 py-1.5 text-sm font-bold text-black hover:border-red-400">✕</button>
            </div>
            <div className="mt-5">
              <label className="block">
                <span className="text-sm font-bold text-gray-700">Call Date &amp; Time</span>
                <input
                  type="datetime-local"
                  value={callLaterDatetime}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={(e) => setCallLaterDatetime(e.target.value)}
                  className="mt-2 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-black outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
                />
              </label>
              {callLaterDatetime && (
                <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
                  🔔 Reminder set for: {new Date(callLaterDatetime).toLocaleString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setCallLaterModal(null)}
                className="flex-1 rounded-2xl border border-gray-300 px-4 py-3 text-sm font-bold text-black hover:border-gray-400"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCallLaterSubmit}
                disabled={!callLaterDatetime || callLaterSaving}
                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {callLaterSaving ? 'Saving…' : 'Set Reminder'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedCompany && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-[28px] bg-white p-6 text-black shadow-[0_30px_100px_rgba(0,0,0,0.32)] md:p-8">
            <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.28em] text-yellow-600">Vacancy Form</p>
                <h3 className="mt-2 text-2xl font-black">{selectedCompany.company_name}</h3>
                <p className="mt-2 text-sm text-gray-600">Add more than one vacancy for the same company and keep each one synced into Admin Jobs.</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedCompany(null)}
                className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 rounded-[24px] border border-yellow-200 bg-yellow-50/50 p-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-yellow-700">Saved Vacancies</p>
                  <p className="mt-1 text-sm text-gray-700">{selectedCompany.vacancy_count || 0} vacancy record{Number(selectedCompany.vacancy_count || 0) === 1 ? '' : 's'} under this company.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setVacancyForm(createVacancyFormFromCompany(selectedCompany))}
                  className="rounded-xl border border-yellow-500 px-4 py-2 text-sm font-bold text-yellow-700 transition hover:bg-yellow-100"
                >
                  Add Another Vacancy
                </button>
              </div>

              {Array.isArray(selectedCompany.vacancies) && selectedCompany.vacancies.length > 0 ? (
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {selectedCompany.vacancies.map((vacancy) => (
                    <button
                      key={vacancy.id}
                      type="button"
                      onClick={() => setVacancyForm(createVacancyFormFromVacancy(selectedCompany, vacancy))}
                      className={`rounded-2xl border px-4 py-3 text-left transition ${String(vacancyForm.vacancy_id || '') === String(vacancy.id) ? 'border-yellow-500 bg-white shadow-sm' : 'border-yellow-200 bg-white/80 hover:border-yellow-400'}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-semibold text-black">{vacancy.job_title}</p>
                          <p className="mt-1 text-xs text-gray-500">{vacancy.total_vacancies ? `${vacancy.total_vacancies} openings` : 'Vacancy saved'}</p>
                        </div>
                        <span className="rounded-full bg-yellow-100 px-3 py-1 text-[11px] font-bold text-yellow-800">
                          {vacancy.job_id ? 'Synced to Jobs' : 'Saved'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-sm text-gray-600">No vacancy added yet for this company.</p>
              )}
            </div>

            <form onSubmit={handleSaveVacancy} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="text-sm font-bold text-black">Date of Communication</span>
                <input
                  value={vacancyForm.date_of_communication}
                  readOnly
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-100 px-4 py-3 text-black outline-none"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Company</span>
                <input
                  value={selectedCompany?.company_name || ''}
                  readOnly
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-100 px-4 py-3 text-gray-600 outline-none"
                />
                <p className="mt-1 text-xs text-gray-400">Original company name — shown in all internal areas</p>
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Company / Firm / Organisation <span className="text-yellow-600">(Jobs Page Display)</span></span>
                <input
                  name="company_firm_organisation"
                  value={vacancyForm.company_firm_organisation}
                  onChange={handleVacancyFieldChange}
                  placeholder="Enter name to show on the public Jobs page"
                  className="mt-1.5 w-full rounded-2xl border border-yellow-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200"
                />
                <p className="mt-1 text-xs text-yellow-700">This name is shown on the public Jobs listing page only</p>
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Job Title</span>
                <input
                  name="job_title"
                  value={vacancyForm.job_title}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  required
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Department</span>
                <input
                  name="department"
                  value={vacancyForm.department}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Section</span>
                <input
                  name="section"
                  value={vacancyForm.section}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Salary</span>
                <input
                  name="salary"
                  value={vacancyForm.salary}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Gender</span>
                <input
                  name="gender"
                  value={vacancyForm.gender}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Age Limit</span>
                <input
                  name="age_limit"
                  value={vacancyForm.age_limit}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Qualification</span>
                <input
                  name="qualification"
                  value={vacancyForm.qualification}
                  onChange={handleVacancyFieldChange}
                  placeholder="e.g. Any Degree, B.Com, B.Tech"
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Experience</span>
                <input
                  name="experience"
                  value={vacancyForm.experience}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Place</span>
                <input
                  name="place"
                  value={vacancyForm.place}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">District</span>
                <input
                  name="district"
                  value={vacancyForm.district}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="text-sm font-bold text-black">Location Map</span>
                <input
                  name="location_map"
                  value={vacancyForm.location_map}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Total Vacancies</span>
                <input
                  name="total_vacancies"
                  value={vacancyForm.total_vacancies}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block">
                <span className="text-sm font-bold text-black">Contact Person</span>
                <input
                  name="contact_person"
                  value={vacancyForm.contact_person}
                  onChange={handleVacancyFieldChange}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <label className="block md:col-span-2">
                <span className="text-sm font-bold text-black">Requirement</span>
                <textarea
                  name="requirement"
                  value={vacancyForm.requirement}
                  onChange={handleVacancyFieldChange}
                  rows={5}
                  className="mt-1.5 w-full rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3 text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                />
              </label>

              <div className="md:col-span-2 flex justify-end gap-3 border-t border-gray-200 pt-5">
                <button
                  type="button"
                  onClick={() => setSelectedCompany(null)}
                  className="rounded-xl border border-gray-300 px-5 py-3 text-sm font-bold text-black transition hover:border-yellow-400 hover:text-yellow-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={vacancySavingId === selectedCompany.id}
                  className="rounded-xl bg-yellow-400 px-6 py-3 text-sm font-extrabold text-black transition hover:bg-yellow-500 disabled:opacity-60"
                >
                  {vacancySavingId === selectedCompany.id ? 'Saving...' : vacancyForm.vacancy_id ? 'Update Vacancy' : 'Save Vacancy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedCoordination && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-3xl bg-white text-black shadow-[0_32px_80px_rgba(0,0,0,0.35)]">

            {/* ── Header ── */}
            <div className="bg-black px-6 py-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-yellow-400">Interview Coordination</p>
                  <h3 className="mt-1.5 truncate text-xl font-black text-white">{selectedCoordination.name}</h3>
                  <p className="mt-0.5 truncate text-xs text-gray-400">
                    {selectedCoordination.selected_job_title || '-'} &nbsp;·&nbsp; {selectedCoordination.selected_job_company || selectedCoordination.company_name || '-'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedCoordination(null)}
                  className="mt-0.5 flex-shrink-0 rounded-lg border border-gray-600 px-3 py-1.5 text-xs font-bold text-gray-300 transition hover:border-yellow-400 hover:text-yellow-400"
                >
                  Close
                </button>
              </div>
            </div>

            {/* ── Info strip (read-only) ── */}
            <div className="grid grid-cols-2 gap-px bg-gray-200">
              <div className="bg-white px-5 py-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Candidate Availability</p>
                <p className="mt-1 text-sm font-semibold text-black">{selectedCoordination.candidate_availability || '—'}</p>
              </div>
              <div className="bg-white px-5 py-4">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">HR Assistant Confirmation</p>
                <p className="mt-1 text-sm font-semibold text-black">{selectedCoordination.hr_assistant_confirmation || '—'}</p>
              </div>
            </div>

            {/* ── Form ── */}
            <form onSubmit={handleSaveCoordination} className="px-6 py-6">
              <p className="mb-4 text-[10px] font-extrabold uppercase tracking-[0.24em] text-yellow-600">Company Response</p>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Company Availability</span>
                  <select
                    name="vacancy_availability"
                    value={coordinationForm.vacancy_availability}
                    onChange={handleCoordinationFieldChange}
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  >
                    {availabilityStatusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>

                <label className="block">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Company Confirmation</span>
                  <select
                    name="assistant_recruiter_confirmation"
                    value={coordinationForm.assistant_recruiter_confirmation}
                    onChange={handleCoordinationFieldChange}
                    className="mt-1.5 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  >
                    {interviewConfirmationOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                </label>

                <label className="block md:col-span-2">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-500">Availability Note</span>
                  <textarea
                    name="vacancy_availability_note"
                    value={coordinationForm.vacancy_availability_note}
                    onChange={handleCoordinationFieldChange}
                    rows={3}
                    placeholder="Any notes about company availability..."
                    className="mt-1.5 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-black outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-200"
                  />
                </label>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-100 pt-5">
                <button
                  type="button"
                  onClick={() => setSelectedCoordination(null)}
                  className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-bold text-black transition hover:border-gray-400 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={coordinationSavingId === selectedCoordination.workflow_id}
                  className="rounded-xl bg-black px-6 py-2.5 text-sm font-extrabold text-yellow-400 transition hover:bg-gray-900 disabled:opacity-60"
                >
                  {coordinationSavingId === selectedCoordination.workflow_id ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <Toast message={toast} onClose={() => setToast('')} />
    </div>
  );
};

const NotificationBell = ({ API_URL, onNavigate }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data || []);
    } catch {
      // silent fail
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    const handleFocus = () => fetchNotifications();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleMarkRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/staff/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    } catch { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/staff/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    } catch { /* silent */ } finally {
      setMarkingAll(false);
    }
  };

  const handleClearAll = () => {
    setNotifications([]);
    const token = localStorage.getItem('token');
    axios.delete(`${API_URL}/api/staff/notifications/clear-all`, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {});
  };

  const formatTime = (dateStr) => fmtDateTime(dateStr);

  const handleNavigate = (section) => {
    setOpen(false);
    if (onNavigate) onNavigate(section);
  };

  return (
    <div className="relative" style={{ zIndex: 50 }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition hover:bg-white/20"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-black text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-[20px] border border-gray-200 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.18)]">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <p className="text-sm font-black text-gray-900">
              Alerts{' '}
              {unreadCount > 0 && (
                <span className="ml-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600">{unreadCount} new</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="text-xs font-semibold text-blue-600 hover:underline"
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-xs font-semibold text-red-500 hover:underline"
                >
                  Clear all
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="max-h-96 divide-y divide-gray-50 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-400">No alerts yet.</p>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className={`px-4 py-3 ${n.is_read ? 'bg-white' : n.type === 'offer_ready' ? 'bg-amber-50' : n.type === 'service_charge_set' ? 'bg-blue-50' : 'bg-yellow-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {n.type === 'offer_ready' && (
                        <span className="mb-1 inline-block rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-amber-800">📋 Offer Inbox</span>
                      )}
                      {n.type === 'offer_ready' && onNavigate && (
                        <button type="button" onClick={() => handleNavigate('offers')} className="mt-1.5 block text-[11px] font-bold text-amber-700 hover:underline">→ View Offer Inbox</button>
                      )}
                      {n.type === 'service_charge_set' && (
                        <span className="mb-1 inline-block rounded-full bg-blue-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-blue-800">💳 Service Charge</span>
                      )}
                      <p className="text-xs font-black leading-snug text-gray-900">{n.title}</p>
                      <p className="mt-0.5 text-xs leading-snug text-gray-600">{n.message}</p>
                      <p className="mt-1 text-[10px] text-gray-400">{formatTime(n.created_at)}</p>
                      {n.type === 'service_charge_set' && onNavigate && (
                        <button type="button" onClick={() => handleNavigate('service-charges')} className="mt-1.5 text-[11px] font-bold text-blue-600 hover:underline">→ View Service Charges</button>
                      )}
                    </div>
                    {!n.is_read && (
                      <button
                        type="button"
                        onClick={() => handleMarkRead(n.id)}
                        className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-semibold text-gray-600 hover:bg-gray-200"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Assistant Recruiter: company that came from a partner's shared vacancy
const PartnerVacancyTag = ({ company }) => (company?.partner_shared_vacancy_id ? (
  <div className="mt-1 inline-flex max-w-[260px] flex-col rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] leading-snug text-amber-900">
    <span className="font-black">🤝 Partner: {company.partner_vacancy_org || 'Partner'}</span>
    <span>{company.partner_vacancy_job_title} × {company.partner_vacancy_count}{company.partner_vacancy_contact_person ? ` · Contact: ${company.partner_vacancy_contact_person}` : ''}</span>
    {company.partner_vacancy_last_date && <span className="font-bold text-orange-700">Last date of joining: {company.partner_vacancy_last_date}</span>}
  </div>
) : null);

const PartnerTag = ({ row }) => Number(row?.via_partner) === 1 ? (
  <span
    className="inline-flex items-center gap-0.5 rounded-full bg-amber-400 border border-amber-500 px-1.5 py-0.5 text-[9px] font-black text-black leading-none whitespace-nowrap"
    title={row.partner_org_name ? `Via Partner: ${row.partner_org_name}` : 'Via Partner'}
  >
    🤝 {row.partner_org_name ? row.partner_org_name : 'Partner Ref'}
  </span>
) : null;

const ServiceChargeCard = ({ row, API_URL, fetchServiceCharges, fetchHRPaymentRequests }) => {
  const [reqProformaSaving, setReqProformaSaving] = React.useState(false);
  const [markSentSaving, setMarkSentSaving] = React.useState(false);
  const [markPaidSaving, setMarkPaidSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState('');

  const prStatus = row.payment_request_status || null;
  const proforma = row.proforma_data || null;
  const phone = String(row.whatsapp_number || row.contact_number || '').replace(/\D/g, '');
  const dialPhone = phone.length === 10 ? `91${phone}` : phone;

  const proformaWaMsg = proforma ? [
    `Dear ${row.name || 'Candidate'},`,
    ``,
    `Please find below your Proforma Invoice for the service charge related to your placement.`,
    ``,
    `📋 Proforma No: ${row.proforma_number}`,
    `📌 ${proforma.description}`,
    `✅ Total Payable: ₹${Number(proforma.total_amount || proforma.amount || 0).toLocaleString('en-IN')}`,
    proforma.due_date ? `📅 Due Date: ${proforma.due_date}` : null,
    ``,
    `Payment: UPI lwin123@fbl  |  A/c: 1256955555 IFSC: FDRL00025 Federal Bank`,
    ``,
    `Please share payment screenshot after paying. – Dani Job Club`,
  ].filter(Boolean).join('\n') : null;

  const handleRequestProforma = async () => {
    setReqProformaSaving(true); setFeedback('');
    try {
      const tk = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/staff/service-charges/${row.id}/request-proforma`, {}, { headers: { Authorization: `Bearer ${tk}` } });
      setFeedback('✅ Request sent to Accountant');
      fetchServiceCharges(); fetchHRPaymentRequests();
    } catch (err) {
      setFeedback(`❌ ${err?.response?.data?.error || 'Failed to send request'}`);
    } finally { setReqProformaSaving(false); }
  };

  const handleMarkSentToMember = async () => {
    if (!row.payment_request_id) return;
    setMarkSentSaving(true); setFeedback('');
    try {
      const tk = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/hr/payment-requests/${row.payment_request_id}/mark-sent-to-member`, {}, { headers: { Authorization: `Bearer ${tk}` } });
      setFeedback('✅ Marked as sent to member');
      fetchServiceCharges(); fetchHRPaymentRequests();
    } catch (err) {
      setFeedback(`❌ ${err?.response?.data?.error || 'Failed to update'}`);
    } finally { setMarkSentSaving(false); }
  };

  const handleMarkPaymentReceived = async () => {
    setMarkPaidSaving(true); setFeedback('');
    try {
      const tk = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/staff/service-charges/${row.id}/mark-payment-received`, {}, { headers: { Authorization: `Bearer ${tk}` } });
      setFeedback('✅ Payment received — Accountant & Recruiter notified');
      fetchServiceCharges(); fetchHRPaymentRequests();
    } catch (err) {
      setFeedback(`❌ ${err?.response?.data?.error || 'Failed to mark payment'}`);
    } finally { setMarkPaidSaving(false); }
  };

  // Status badge config
  const statusConfig = {
    null:             { label: 'Action Required',    cls: 'bg-blue-600 text-white' },
    'Pending':        { label: 'Sent to Accountant', cls: 'bg-amber-500 text-white' },
    'Invoice Ready':  { label: 'Proforma Ready',     cls: 'bg-teal-600 text-white' },
    'Sent to Member': { label: 'Sent to Member',     cls: 'bg-indigo-600 text-white' },
    'Paid':           { label: 'Payment Received',   cls: 'bg-emerald-600 text-white' },
    'Partially Paid': { label: '1st Installment Paid', cls: 'bg-orange-500 text-white' },
  };
  const isInstallmentPlan = row.payment_plan === 'Installment' && Number(row.first_installment_amount) > 0;
  const badge = statusConfig[prStatus] || statusConfig[null];

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {/* ── Row: member + charge ── */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-black text-gray-900 text-sm truncate">{row.name || 'Candidate'}</p>
            <PartnerTag row={row} />
          </div>
          <p className="text-xs text-gray-400 truncate">
            {[row.offered_job_title || row.selected_job_title, row.selected_job_company].filter(Boolean).join(' — ') || 'No vacancy linked'}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-lg font-black text-gray-900">₹{row.service_charge_amount}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${badge.cls}`}>{badge.label}</span>
        </div>
      </div>

      {/* ── Action area ── */}
      <div className="px-4 py-3 space-y-2">
        {feedback && (
          <p className={`text-xs font-semibold ${feedback.startsWith('✅') ? 'text-emerald-600' : 'text-red-500'}`}>{feedback}</p>
        )}

        {/* New request → forward to Accountant */}
        {!prStatus && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">Recruiter set this charge. Forward to Accountant to prepare proforma.</p>
            <button
              type="button"
              onClick={handleRequestProforma}
              disabled={reqProformaSaving}
              className="shrink-0 rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {reqProformaSaving ? 'Sending…' : 'Send to Accountant'}
            </button>
          </div>
        )}

        {/* Waiting for Accountant */}
        {prStatus === 'Pending' && (
          <p className="text-xs text-amber-700 font-semibold">⏳ Waiting for Accountant to prepare the proforma invoice…</p>
        )}

        {/* Proforma ready → send to member + mark sent */}
        {prStatus === 'Invoice Ready' && proforma && (
          <div className="space-y-2">
            <div className="rounded-xl bg-teal-50 border border-teal-200 px-3 py-2 text-xs text-gray-700 flex flex-wrap gap-x-4 gap-y-0.5">
              <span><span className="font-bold text-gray-500">Proforma No:</span> {row.proforma_number}</span>
              <span><span className="font-bold text-gray-500">Total:</span> <span className="font-black text-teal-800">₹{Number(proforma.total_amount || proforma.amount || 0).toLocaleString('en-IN')}</span></span>
            </div>
            <div className="flex gap-2">
              {dialPhone && proformaWaMsg && (
                <a
                  href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(proformaWaMsg)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-xl border border-green-400 bg-white px-3 py-1.5 text-xs font-bold text-green-800 hover:bg-green-50"
                >
                  💬 Send via WhatsApp
                </a>
              )}
              <button
                type="button"
                onClick={handleMarkSentToMember}
                disabled={markSentSaving}
                className="rounded-xl bg-teal-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-teal-700 disabled:opacity-60"
              >
                {markSentSaving ? 'Saving…' : '✓ Mark Sent to Member'}
              </button>
            </div>
          </div>
        )}

        {/* Sent to member → mark payment received */}
        {prStatus === 'Sent to Member' && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              Proforma sent.{' '}
              {proforma && <span className="font-semibold text-gray-700">₹{Number(proforma.total_amount || proforma.amount || 0).toLocaleString('en-IN')}</span>}
              {isInstallmentPlan ? (
                <>
                  {' '}· <span className="font-bold text-orange-700">2 installments: collect ₹{Number(row.first_installment_amount).toLocaleString('en-IN')} now</span>
                  {row.second_installment_amount ? ` (balance ₹${Number(row.second_installment_amount).toLocaleString('en-IN')} due ${row.second_installment_due || ''})` : ''}.
                  {' '}Mark Paid once the 1st installment is received.
                </>
              ) : <>{' '}Once member pays, mark it received.</>}
            </p>
            <div className="flex gap-2 shrink-0">
              {dialPhone && proformaWaMsg && (
                <a
                  href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(proformaWaMsg)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50"
                >
                  💬
                </a>
              )}
              <button
                type="button"
                onClick={handleMarkPaymentReceived}
                disabled={markPaidSaving}
                className="rounded-xl bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {markPaidSaving ? 'Saving…' : '💰 Mark Paid'}
              </button>
            </div>
          </div>
        )}

        {/* Done */}
        {prStatus === 'Paid' && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-emerald-700 font-semibold">✅ Payment received. Accountant is issuing the final invoice.</p>
            {dialPhone && (
              <a
                href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(`🌟 Dear ${row.name || 'Candidate'}, congratulations on your first day at work! Wishing you the very best in your new role. – Team Dani Job Club 💛`)}`}
                target="_blank" rel="noopener noreferrer"
                className="shrink-0 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800 hover:bg-emerald-100"
              >
                🌟 First Day Wishes
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const LegacyStaffDashboard = ({ name, role, summary, API_URL }) => {
  const [forwardedRegistrations, setForwardedRegistrations] = useState([]);
  const [serviceChargeItems, setServiceChargeItems] = useState([]);
  const [pendingOffersItems, setPendingOffersItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [forwardingToRecruiterId, setForwardingToRecruiterId] = useState(null);
  const [recruiterModal, setRecruiterModal] = useState(null); // { row } when open
  const [recruiterList, setRecruiterList] = useState([]);
  const [recruiterListLoading, setRecruiterListLoading] = useState(false);
  const [selectedRecruiterId, setSelectedRecruiterId] = useState('');
  const [recruiterForwarding, setRecruiterForwarding] = useState(false);
  const [partnerModal, setPartnerModal] = useState(null); // { forwardId, pendingStatus } when open
  const [partnerList, setPartnerList] = useState([]);
  const [partnerListLoading, setPartnerListLoading] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(createInitialForwardFilters);
  const [detailForm, setDetailForm] = useState(createTalentDetailForm());
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailFeedback, setDetailFeedback] = useState('');
  const [newQualificationInput, setNewQualificationInput] = useState('');
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [mapSearch, setMapSearch] = useState('');
  const [mapSearchResults, setMapSearchResults] = useState([]);
  const [mapSearching, setMapSearching] = useState(false);
  const [mapPreviewCoords, setMapPreviewCoords] = useState(null); // {lat, lon, display_name}
  const [mapGpsLoading, setMapGpsLoading] = useState(false);
  const [fileUploadState, setFileUploadState] = useState({ resumeFile: null, photoFile: null });
  const [fileUploading, setFileUploading] = useState(false);
  const [fileUploadFeedback, setFileUploadFeedback] = useState('');
  const [interviewCoordinationForm, setInterviewCoordinationForm] = useState(createInterviewCoordinationForm());
  const [interviewCoordinationSaving, setInterviewCoordinationSaving] = useState(false);
  const [interviewCoordinationFeedback, setInterviewCoordinationFeedback] = useState('');
  const [placementStepsSaving, setPlacementStepsSaving] = useState(false);
  const [placementStepsFeedback, setPlacementStepsFeedback] = useState('');
  const [toast, setToast] = useState('');
  const showToast = (msg) => setToast(msg);
  const [activeSection, setActiveSection] = useState('pending');
  const [sectionPages, setSectionPages] = useState({
    pending: 1,
    forwarded: 1,
    rejected: 1,
    returned: 1
  });
  const [interviewCoordEditRow, setInterviewCoordEditRow] = useState(null);
  const [interviewCoordModalOpen, setInterviewCoordModalOpen] = useState(false);
  const [placementCelebration, setPlacementCelebration] = useState(null); // { name, job, company, phone }
  const [callLaterModal, setCallLaterModal] = useState(false);
  const [callLaterRow, setCallLaterRow] = useState(null);
  const [callLaterDateTime, setCallLaterDateTime] = useState('');
  const [callLaterSaving, setCallLaterSaving] = useState(false);
  const [activeAlarm, setActiveAlarm] = useState(null);
  const [readyToPayModal, setReadyToPayModal] = useState(false);
  const [readyToPayRow, setReadyToPayRow] = useState(null);
  const [readyToPaySaving, setReadyToPaySaving] = useState(false);
  const [feePaidModal, setFeePaidModal] = useState(false);
  const [feePaidRow, setFeePaidRow] = useState(null);
  const [feePaidAmount, setFeePaidAmount] = useState('');
  const [feePaidType, setFeePaidType] = useState('');
  const [feePaidSaving, setFeePaidSaving] = useState(false);
  const [feePaidFeedback, setFeePaidFeedback] = useState('');
  const [paymentRequestsMap, setPaymentRequestsMap] = useState({});
  const [secondPayModal, setSecondPayModal] = useState(false);
  const [secondPayRow, setSecondPayRow] = useState(null);
  const [secondPayAmount, setSecondPayAmount] = useState('2999');
  const [secondPaySaving, setSecondPaySaving] = useState(false);
  const [secondPayFeedback, setSecondPayFeedback] = useState('');
  const READY_TO_PAY_MESSAGE = `Hello! We are excited to help you find your next career opportunity. To complete your enrollment and activate your profile in our placement system, please fulfill the one-time registration fee ₹500.

Payment Options:

🔷 Scan & Pay: Use the QR code image shared below.

🏦 Bank Transfer:
• Account Name: Dani Job Club
• Account Number: 1256955555
• IFSC Code: FDRL00025
• Bank: FEDERAL BANK
• Branch: KULAPPULLY

Please send a screenshot of your payment confirmation to this chat so we can assign you an HR Assistant and start your job matching process immediately.`;

  const communicationStatusOptions = [
    'Communicated',
    'Call Not Connected',
    'Call Later',
    'Ready To Pay',
    'Not Interested',
    'Interested',
    'Registration Fee Paid',
    'Prime Membership Fee Paid',
    'Partner Member',
    'Black Listed',
    'Registration Fee Returned',
    'Job Not Required',
    'Not Attended',
    'Waiting for the results'
  ];

  const totalRegistrations = forwardedRegistrations.length;
  const totalForwardedRegistrations = forwardedRegistrations.filter((item) => getForwardedSectionKey(item) === 'forwarded').length;
  const totalPendingRegistrations = forwardedRegistrations.filter((item) => getForwardedSectionKey(item) === 'pending').length;
  const totalRejectedRegistrations = forwardedRegistrations.filter((item) => getForwardedSectionKey(item) === 'rejected').length;
  const totalReturnedRegistrations = forwardedRegistrations.filter((item) => getForwardedSectionKey(item) === 'returned').length;
  const coordinationRegistrations = useMemo(
    // Exclude placed members — HR Assistant's job is done once placed
    () => forwardedRegistrations.filter((item) => Number(item.workflow_id || 0) > 0 && item.placement_status !== 'Placed'),
    [forwardedRegistrations]
  );
  const pendingInterviewCoordinationCount = useMemo(
    () => coordinationRegistrations.filter((item) => item.candidate_availability !== 'Available' || item.hr_assistant_confirmation !== 'Accepted').length,
    [coordinationRegistrations]
  );
  const pendingOffers = pendingOffersItems;
  // pendingServiceCharges comes from the dedicated /api/staff/pending-service-charges endpoint
  // so it is never affected by the complex LEFT JOIN in GET /api/staff/forwards
  const pendingServiceCharges = serviceChargeItems;
  const filteredForwardedRegistrations = useMemo(() => {
    return forwardedRegistrations.filter((item) => {
      const nameMatch = String(item.name || '').toLowerCase().includes(filters.name.toLowerCase());
      const placeMatch = String(item.place || '').toLowerCase().includes(filters.place.toLowerCase());
      const contactMatch = String(item.contact_number || '').toLowerCase().includes(filters.contact_number.toLowerCase());
      const statusMatch = filters.communication_status === 'all'
        ? true
        : String(item.communication_status || 'Waiting for the results') === filters.communication_status;
      const sectionMatch = getForwardedSectionKey(item) === activeSection;

      return nameMatch && placeMatch && contactMatch && statusMatch && sectionMatch;
    });
  }, [forwardedRegistrations, filters, activeSection]);

  const sectionTabs = [
    {
      key: 'pending',
      label: 'Pending',
      count: totalPendingRegistrations,
      description: ''
    },
    {
      key: 'forwarded',
      label: 'Forwarded',
      count: totalForwardedRegistrations,
      description: 'Profiles already moved to recruiter.'
    },
    {
      key: 'rejected',
      label: 'Rejected',
      count: totalRejectedRegistrations,
      description: 'Black Listed, Job Not Required, and Not Interested.'
    },
    {
      key: 'returned',
      label: 'Registration Fee Returned',
      count: totalReturnedRegistrations,
      description: 'Candidates whose fee was returned.'
    },
    {
      key: 'interview',
      label: 'Interview Coordination',
      count: pendingInterviewCoordinationCount,
      description: 'Candidates awaiting your action.'
    },
    {
      key: 'offers',
      label: '📋 Offer Inbox',
      count: pendingOffers.length,
      description: 'Pending offer details from recruiters awaiting delivery to members.'
    },
    {
      key: 'service-charges',
      label: '💳 Service Charges',
      count: pendingServiceCharges.length,
      description: 'Selected candidates with a service charge set by recruiter pending collection.'
    }
  ];
  const activeSectionMeta = sectionTabs.find((section) => section.key === activeSection) || sectionTabs[0];
  const activeSectionPage = sectionPages[activeSection] || 1;
  const totalSectionPages = Math.max(1, Math.ceil(filteredForwardedRegistrations.length / FORWARDED_SECTION_PAGE_SIZE));
  const paginatedForwardedRegistrations = useMemo(() => {
    const startIndex = (activeSectionPage - 1) * FORWARDED_SECTION_PAGE_SIZE;
    return filteredForwardedRegistrations.slice(startIndex, startIndex + FORWARDED_SECTION_PAGE_SIZE);
  }, [activeSectionPage, filteredForwardedRegistrations]);

  const fetchForwardedRegistrations = async (options = {}) => {
    const { silent = false } = options;
    if (!silent) {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/forwards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForwardedRegistrations(res.data || []);
    } catch {
      setForwardedRegistrations([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchForwardedRegistrations();
    // Auto-refresh every 30s so HR/AR updates from Recruiter (interview details, notifications) are visible
    const t = setInterval(() => fetchForwardedRegistrations(true), 30000);
    return () => clearInterval(t);
  }, []);

  // Fetch pending service charges from dedicated endpoint
  const fetchServiceCharges = async () => {
    if (role !== 'HR Assistant') return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/pending-service-charges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceChargeItems(res.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchServiceCharges();
    const t = setInterval(fetchServiceCharges, 15000);
    window.addEventListener('focus', fetchServiceCharges);
    return () => { clearInterval(t); window.removeEventListener('focus', fetchServiceCharges); };
  }, []); // eslint-disable-line

  // Fetch pending offers from dedicated endpoint (bypasses /forwards LEFT JOIN reliability issues)
  const fetchPendingOffers = async () => {
    if (role !== 'HR Assistant') return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/pending-offers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingOffersItems(res.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchPendingOffers();
    const t = setInterval(fetchPendingOffers, 15000);
    window.addEventListener('focus', fetchPendingOffers);
    return () => { clearInterval(t); window.removeEventListener('focus', fetchPendingOffers); };
  }, []); // eslint-disable-line

  // Load HR payment requests for badge display
  const fetchHRPaymentRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/hr/payment-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const map = {};
      (res.data || []).forEach((r) => { map[r.forward_id] = r; });
      setPaymentRequestsMap(map);
    } catch { /* silent */ }
  };

  useEffect(() => {
    if (role === 'HR Assistant') fetchHRPaymentRequests();
  }, []); // eslint-disable-line

  useEffect(() => {
    setSectionPages((prev) => ({ ...prev, [activeSection]: 1 }));
  }, [activeSection, filters]);

  useEffect(() => {
    if (activeSectionPage <= totalSectionPages) return;
    setSectionPages((prev) => ({ ...prev, [activeSection]: totalSectionPages }));
  }, [activeSection, activeSectionPage, totalSectionPages]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchForwardedRegistrations({ silent: true });
    }, 8000);

    const handleWindowFocus = () => {
      fetchForwardedRegistrations({ silent: true });
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchForwardedRegistrations({ silent: true });
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleViewRegistration = (row) => {
    setSelectedRegistration(row);
    setDetailForm(createTalentDetailForm(row));
    setDetailFeedback('');
    setFileUploadState({ resumeFile: null, photoFile: null });
    setFileUploadFeedback('');
    setInterviewCoordinationForm(createInterviewCoordinationForm(row));
    setInterviewCoordinationFeedback('');
  };

  const handleFilterChange = (e) => {
    const { name: fieldName, value } = e.target;
    setFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleClearFilters = () => {
    setFilters(createInitialForwardFilters());
  };

  const handleSectionPageChange = (nextPage) => {
    setSectionPages((prev) => ({
      ...prev,
      [activeSection]: Math.min(Math.max(nextPage, 1), totalSectionPages)
    }));
  };

  const CALL_LATER_STORAGE_KEY = 'danijobclub_callLaterAlarms';

  const playAlarmSound = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      [0, 0.45, 0.9, 1.35].forEach((offset) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.value = offset % 0.9 === 0 ? 660 : 880;
        gain.gain.setValueAtTime(0.55, ctx.currentTime + offset);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.35);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.35);
      });
    } catch (_) {}
  };

  const loadCallLaterAlarms = () => {
    try { return JSON.parse(localStorage.getItem(CALL_LATER_STORAGE_KEY) || '[]'); } catch (_) { return []; }
  };

  const saveCallLaterAlarm = (forwardId, candidateName, isoDateTime) => {
    const alarms = loadCallLaterAlarms().filter((a) => a.forwardId !== forwardId);
    alarms.push({ forwardId, candidateName, scheduledAt: isoDateTime, dismissed: false });
    localStorage.setItem(CALL_LATER_STORAGE_KEY, JSON.stringify(alarms));
  };

  const dismissCallLaterAlarm = (forwardId) => {
    const alarms = loadCallLaterAlarms().map((a) =>
      a.forwardId === forwardId ? { ...a, dismissed: true } : a
    );
    localStorage.setItem(CALL_LATER_STORAGE_KEY, JSON.stringify(alarms));
    setActiveAlarm(null);
  };

  useEffect(() => {
    const check = () => {
      const alarms = loadCallLaterAlarms();
      const now = Date.now();
      const fired = alarms.find((a) => !a.dismissed && new Date(a.scheduledAt).getTime() <= now);
      if (fired) {
        setActiveAlarm(fired);
        playAlarmSound();
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const handleUpdateCommunicationStatus = async (forwardId, nextStatus) => {
    if (nextStatus === 'Partner Member') {
      // Open partner selection modal before saving
      setPartnerListLoading(true);
      setSelectedPartnerId('');
      setPartnerModal({ forwardId, pendingStatus: nextStatus });
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_URL}/api/staff/partners`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPartnerList(res.data || []);
      } catch {
        setPartnerList([]);
      } finally {
        setPartnerListLoading(false);
      }
      return;
    }
    setStatusUpdatingId(forwardId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/staff/forwards/${forwardId}/status`,
        { communication_status: nextStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForwardedRegistrations((prev) =>
        prev.map((item) => (item.id === forwardId ? { ...item, communication_status: nextStatus } : item))
      );
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update communication status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handlePartnerModalConfirm = async () => {
    if (!selectedPartnerId || !partnerModal) return;
    const partner = partnerList.find((p) => p.partner_user_id === selectedPartnerId);
    if (!partner) return;
    setStatusUpdatingId(partnerModal.forwardId);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/staff/forwards/${partnerModal.forwardId}/status`,
        {
          communication_status: 'Partner Member',
          partner_user_id: partner.partner_user_id,
          partner_org_name: partner.name_of_organisation || partner.partner_user_id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForwardedRegistrations((prev) =>
        prev.map((item) =>
          item.id === partnerModal.forwardId
            ? { ...item, communication_status: 'Partner Member', via_partner: 1, partner_org_name: partner.name_of_organisation || partner.partner_user_id }
            : item
        )
      );
      setPartnerModal(null);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to set Partner Member status.');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleForwardToRecruiter = async (row) => {
    setRecruiterListLoading(true);
    setSelectedRecruiterId('');
    setRecruiterModal({ row });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/staff/recruiters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecruiterList(res.data || []);
    } catch {
      setRecruiterList([]);
    } finally {
      setRecruiterListLoading(false);
    }
  };

  const handleRecruiterModalConfirm = async () => {
    if (!selectedRecruiterId || !recruiterModal) return;
    const recruiter = recruiterList.find((r) => r.user_id === selectedRecruiterId);
    if (!recruiter) return;
    setRecruiterForwarding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/staff/forwards/${recruiterModal.row.id}/forward-to-recruiter`,
        { recruiter_user_id: recruiter.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForwardedRegistrations((prev) =>
        prev.map((item) =>
          item.id === recruiterModal.row.id
            ? { ...item, forwarded_to_recruiter_count: Number(item.forwarded_to_recruiter_count || 0) + 1 }
            : item
        )
      );
      showToast(`✅ Forwarded to ${recruiter.name}`);
      setRecruiterModal(null);
    } catch (err) {
      showToast(`❌ ${err?.response?.data?.error || 'Failed to forward to recruiter'}`);
    } finally {
      setRecruiterForwarding(false);
    }
  };

  const handleDetailFieldChange = (e) => {
    const { name: fieldName, value } = e.target;
    const nextValue = fieldName === 'guardian_number' ? normalizeDigits(value).slice(0, 10) : value;
    setDetailForm((prev) => ({ ...prev, [fieldName]: nextValue }));
  };

  const handleAddQualification = () => {
    const value = newQualificationInput.trim();
    if (!value) return;
    setDetailForm((prev) => ({
      ...prev,
      additional_qualification: prev.additional_qualification.includes(value)
        ? prev.additional_qualification
        : [...prev.additional_qualification, value]
    }));
    setNewQualificationInput('');
  };
  const handleRemoveQualification = (value) => {
    setDetailForm((prev) => ({
      ...prev,
      additional_qualification: prev.additional_qualification.filter((q) => q !== value)
    }));
  };

  const handleMapSearch = async () => {
    if (!mapSearch.trim()) return;
    setMapSearching(true);
    setMapSearchResults([]);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(mapSearch)}&limit=5`, {
        headers: { 'Accept-Language': 'en', 'User-Agent': 'DaniJobClub/1.0' }
      });
      const data = await res.json();
      setMapSearchResults(data || []);
    } catch {
      setMapSearchResults([]);
    } finally {
      setMapSearching(false);
    }
  };

  const handleMapSelectResult = (result) => {
    setMapPreviewCoords({ lat: result.lat, lon: result.lon, display_name: result.display_name });
    setMapSearchResults([]);
  };

  const handleMapGPS = () => {
    if (!navigator.geolocation) return;
    setMapGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lon = pos.coords.longitude.toFixed(6);
        setMapPreviewCoords({ lat, lon, display_name: `${lat}, ${lon} (Device GPS)` });
        setMapGpsLoading(false);
      },
      () => setMapGpsLoading(false),
      { timeout: 10000 }
    );
  };

  const handleMapConfirm = () => {
    if (!mapPreviewCoords) return;
    const url = `https://maps.google.com/?q=${mapPreviewCoords.lat},${mapPreviewCoords.lon}`;
    setDetailForm((prev) => ({ ...prev, google_location: url }));
    setMapPickerOpen(false);
    setMapPreviewCoords(null);
    setMapSearch('');
    setMapSearchResults([]);
  };

  const handleExperienceFieldChange = (index, fieldName, value) => {
    setDetailForm((prev) => ({
      ...prev,
      experience_details: prev.experience_details.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [fieldName]: value } : item
      ))
    }));
  };

  const handleAddExperience = () => {
    setDetailForm((prev) => ({
      ...prev,
      experience_details: [...prev.experience_details, createEmptyExperienceDetail()]
    }));
  };

  const handleRemoveExperience = (index) => {
    setDetailForm((prev) => {
      const nextItems = prev.experience_details.filter((_, itemIndex) => itemIndex !== index);
      return {
        ...prev,
        experience_details: nextItems.length > 0 ? nextItems : [createEmptyExperienceDetail()]
      };
    });
  };

  const handleUploadMemberFiles = async () => {
    if (!selectedRegistration) return;
    const { resumeFile, photoFile } = fileUploadState;
    if (!resumeFile && !photoFile) return;

    setFileUploading(true);
    setFileUploadFeedback('');
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      if (resumeFile) fd.append('resume', resumeFile);
      if (photoFile) fd.append('photo', photoFile);

      const res = await axios.put(
        `${API_URL}/api/staff/forwards/${selectedRegistration.id}/upload-files`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );

      const updatedRegistration = res.data?.registration;
      if (updatedRegistration) {
        setForwardedRegistrations((prev) => prev.map((item) => (
          item.id === updatedRegistration.id ? updatedRegistration : item
        )));
        setSelectedRegistration(updatedRegistration);
        setFileUploadState({ resumeFile: null, photoFile: null });
        setFileUploadFeedback('Files uploaded successfully.');
        showToast('Files uploaded successfully.');
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Failed to upload files.';
      setFileUploadFeedback(message);
      showToast(message);
    } finally {
      setFileUploading(false);
    }
  };

  const handleSaveRegistrationDetails = async () => {
    if (!selectedRegistration) return;

    setDetailSaving(true);
    setDetailFeedback('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...detailForm,
        preferred_district: Array.isArray(detailForm.preferred_district) ? detailForm.preferred_district.join(',') : (detailForm.preferred_district || ''),
        highlight_language: Array.isArray(detailForm.highlight_language) ? detailForm.highlight_language.join(',') : (detailForm.highlight_language || ''),
        additional_qualification: Array.isArray(detailForm.additional_qualification) ? detailForm.additional_qualification.join(',') : (detailForm.additional_qualification || ''),
        experience_details: detailForm.experience_details.filter((item) => (
          item.exp_department || item.title_post || item.company_name || item.salary || item.total_years || item.location
        ))
      };
      const res = await axios.put(
        `${API_URL}/api/staff/forwards/${selectedRegistration.id}/details`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedRegistration = res.data?.registration;
      if (updatedRegistration) {
        setForwardedRegistrations((prev) => prev.map((item) => (
          item.id === updatedRegistration.id ? updatedRegistration : item
        )));
        setSelectedRegistration(updatedRegistration);
        setDetailForm(createTalentDetailForm(updatedRegistration));
        setDetailFeedback('Talent details saved successfully.');
        showToast('Talent details saved successfully.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.message || 'Failed to save registration details.';
      const msg = status ? `Save failed (${status}): ${message}` : message;
      setDetailFeedback(msg);
      showToast(msg);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleInterviewCoordinationFieldChange = (e) => {
    const { name, value } = e.target;
    setInterviewCoordinationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveInterviewCoordination = async () => {
    const reg = interviewCoordEditRow || selectedRegistration;
    if (!reg) return;

    setInterviewCoordinationSaving(true);
    setInterviewCoordinationFeedback('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/staff/forwards/${reg.id}/interview-coordination`,
        interviewCoordinationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedRegistration = res.data?.registration;
      if (updatedRegistration) {
        setForwardedRegistrations((prev) => prev.map((item) => (
          item.id === updatedRegistration.id ? updatedRegistration : item
        )));
        if (interviewCoordEditRow) setInterviewCoordEditRow(updatedRegistration);
        if (selectedRegistration) setSelectedRegistration(updatedRegistration);
        setInterviewCoordinationForm(createInterviewCoordinationForm(updatedRegistration));
        setInterviewCoordinationFeedback('Candidate interview coordination updated successfully.');
        showToast('Interview coordination updated successfully.');
      }
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.error || err?.message || 'Failed to update interview coordination.';
      const msg = status ? `Save failed (${status}): ${message}` : message;
      setInterviewCoordinationFeedback(msg);
      showToast(msg);
    } finally {
      setInterviewCoordinationSaving(false);
    }
  };

  const handleSavePlacementSteps = async (fields) => {
    const reg = selectedRegistration || interviewCoordEditRow;
    if (!reg) return;
    const wasPaymentReceived = Number(reg.payment_received || 0) > 0;
    setPlacementStepsSaving(true);
    setPlacementStepsFeedback('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/staff/forwards/${reg.id}/placement-steps`,
        fields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedReg = res.data?.registration;
      if (updatedReg) {
        setForwardedRegistrations((prev) => prev.map((item) => (item.id === updatedReg.id ? updatedReg : item)));
        if (selectedRegistration) setSelectedRegistration(updatedReg);
        if (interviewCoordEditRow) setInterviewCoordEditRow(updatedReg);
        setPlacementStepsFeedback('Placement steps saved.');
        showToast('Placement steps saved.');
        // Trigger celebration when payment is first marked received
        const isNowPaid = (fields.payment_received === true || Number(fields.payment_received || 0) > 0);
        if (isNowPaid && !wasPaymentReceived) {
          const phone = String(updatedReg.whatsapp_number || updatedReg.contact_number || '').replace(/\D/g, '');
          setPlacementCelebration({
            name: updatedReg.name || 'the candidate',
            job: updatedReg.selected_job_title || '',
            company: updatedReg.selected_job_company || '',
            phone: phone.length === 10 ? `91${phone}` : phone,
          });
          setInterviewCoordModalOpen(false);
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to save placement steps.';
      setPlacementStepsFeedback(msg);
      showToast(msg);
    } finally {
      setPlacementStepsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.22),transparent_18%),linear-gradient(180deg,#fffdf8_0%,#f8fafc_50%,#fffaf0_100%)] px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      {/* ── Toast Popup ───────────────────────────────────────── */}
      <Toast message={toast} onClose={() => setToast('')} />
      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translate(-50%,20px)}to{opacity:1;transform:translate(-50%,0)}}.animate-fadeInUp{animation:fadeInUp 0.25s ease-out both}@keyframes pendingRowGlow{0%,100%{background-color:rgba(254,243,199,0.65)}50%{background-color:rgba(253,224,71,0.32)}}.pending-row-pulse{animation:pendingRowGlow 1.8s ease-in-out infinite}@keyframes interviewBtnPulse{0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,0.7)}60%{box-shadow:0 0 0 7px rgba(245,158,11,0)}}.interview-pending-btn{animation:interviewBtnPulse 1.6s ease-out infinite}`}</style>
      <div className="mx-auto w-full max-w-[1920px] overflow-hidden rounded-[30px] border border-yellow-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]">
        <div className="bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.26),transparent_24%),linear-gradient(135deg,#090909_0%,#1c1c1c_55%,#5b4703_100%)] px-5 py-3 text-white md:px-8">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.34em] text-yellow-300">Staff Dashboard</p>
              <h1 className="mt-0.5 text-base font-black">HR Assistant Forward Desk</h1>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-1.5">
                {[
                  { label: 'Total', value: totalRegistrations },
                  { label: 'Forwarded', value: totalForwardedRegistrations },
                  { label: 'Pending', value: totalPendingRegistrations },
                  { label: 'Rejected', value: totalRejectedRegistrations },
                  { label: 'Fee Ret.', value: totalReturnedRegistrations },
                  { label: 'Interview Q', value: pendingInterviewCoordinationCount }
                ].map((stat) => (
                  <div key={stat.label} className="rounded-lg border border-white/10 bg-white/10 px-2.5 py-1.5 backdrop-blur-sm text-center">
                    <p className="text-[9px] font-bold uppercase tracking-wide text-yellow-300">{stat.label}</p>
                    <p className="text-sm font-black">{stat.value}</p>
                  </div>
                ))}
              </div>
              {role === 'HR Assistant' && <NotificationBell API_URL={API_URL} onNavigate={setActiveSection} />}
            </div>
          </div>
        </div>

        <div className="px-4 py-3 md:px-6">
          <TargetWidget API_URL={API_URL} />
          {/* ── SERVICE CHARGE ALERT BANNER (HR Assistant only) ──────────── */}
          {role === 'HR Assistant' && pendingServiceCharges.length > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-2xl border border-blue-300 bg-blue-50 px-4 py-3">
              <span className="text-xl">💳</span>
              <div className="flex-1">
                <p className="text-sm font-black text-blue-900">
                  {pendingServiceCharges.length} service charge{pendingServiceCharges.length > 1 ? 's' : ''} pending collection
                </p>
                <p className="text-xs text-blue-700">Recruiter has set a service charge — send payment request to members via WhatsApp.</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSection('service-charges')}
                className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-black text-white hover:bg-blue-700"
              >
                View Charges
              </button>
            </div>
          )}

          {/* ── OFFER ALERT BANNER (HR Assistant only) ─────────────────────── */}
          {role === 'HR Assistant' && pendingOffers.length > 0 && (
            <div className="mb-3 flex items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3">
              <span className="text-xl">📋</span>
              <div className="flex-1">
                <p className="text-sm font-black text-amber-900">
                  {pendingOffers.length} offer{pendingOffers.length > 1 ? 's' : ''} waiting for your action
                </p>
                <p className="text-xs text-amber-700">Recruiter has sent offer terms — please deliver to members via WhatsApp.</p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSection('offers')}
                className="rounded-xl bg-amber-500 px-4 py-2 text-xs font-black text-white hover:bg-amber-600"
              >
                View Offer Inbox
              </button>
            </div>
          )}

          <div className="mb-3 flex flex-wrap items-center gap-2">
            {sectionTabs.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  activeSection === section.key
                    ? 'border-black bg-black text-white'
                    : section.key === 'offers' && section.count > 0
                      ? 'border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100'
                      : section.key === 'service-charges' && section.count > 0
                        ? 'border-blue-400 bg-blue-50 text-blue-900 hover:bg-blue-100'
                        : section.key === 'interview' && section.count > 0
                          ? 'border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100 interview-pending-btn'
                          : 'border-gray-200 bg-white text-black hover:border-yellow-400'
                }`}
              >
                {section.label}
                <span className={`rounded-full px-2 py-0.5 text-xs font-black ${
                  activeSection === section.key ? 'bg-white/20 text-white'
                  : section.key === 'offers' && section.count > 0 ? 'bg-amber-200 text-amber-900'
                  : section.key === 'service-charges' && section.count > 0 ? 'bg-blue-200 text-blue-900'
                  : section.key === 'interview' && section.count > 0 ? 'bg-amber-300 text-amber-900'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>{section.count}</span>
              </button>
            ))}
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((prev) => !prev)}
                className={`rounded-xl border px-3 py-2 text-sm font-bold transition ${
                  showFilters ? 'border-black bg-black text-yellow-300' : 'border-gray-300 bg-white text-black hover:border-yellow-400'
                }`}
              >
                {showFilters ? 'Hide Filters' : 'Filters'}
              </button>
              <button
                type="button"
                onClick={fetchForwardedRegistrations}
                className="rounded-xl bg-yellow-400 px-3 py-2 text-sm font-bold text-black hover:bg-yellow-500"
              >
                ↻ Refresh
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mb-5 rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff8e6_50%,#ffffff_100%)] p-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <input
                  type="text"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                  placeholder="Filter by candidate name"
                  className="rounded-2xl border border-gray-300 bg-white p-3"
                />
                <input
                  type="text"
                  name="place"
                  value={filters.place}
                  onChange={handleFilterChange}
                  placeholder="Filter by place"
                  className="rounded-2xl border border-gray-300 bg-white p-3"
                />
                <input
                  type="text"
                  name="contact_number"
                  value={filters.contact_number}
                  onChange={handleFilterChange}
                  placeholder="Filter by contact number"
                  className="rounded-2xl border border-gray-300 bg-white p-3"
                />
                <select
                  name="communication_status"
                  value={filters.communication_status}
                  onChange={handleFilterChange}
                  className="rounded-2xl border border-gray-300 bg-white p-3"
                >
                  <option value="all">All communication status</option>
                  {communicationStatusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-600">Showing {filteredForwardedRegistrations.length} of {activeSectionMeta.count} registrations in {activeSectionMeta.label}.</p>
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-black hover:border-yellow-400"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}

          {loading && <div className="text-gray-600">Loading forwarded registrations...</div>}

          {/* ─── INTERVIEW COORDINATION TAB ─────────────────────────────── */}
          {!loading && activeSection === 'interview' && (
          <div className="overflow-hidden rounded-[24px] border border-indigo-200 bg-white shadow-sm">
            <div className="flex items-center justify-between gap-3 border-b border-indigo-100 px-4 py-3 bg-[linear-gradient(135deg,#eef2ff_0%,#e0e7ff_100%)]">
              <p className="text-xs font-black uppercase tracking-wider text-indigo-700">Interview Coordination — Auto-refreshing every 30s</p>
              <button
                type="button"
                onClick={() => fetchForwardedRegistrations(true)}
                className="rounded-xl border border-indigo-300 bg-white px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-50"
              >
                ↻ Refresh Now
              </button>
            </div>
            {coordinationRegistrations.length === 0 ? (
              <div className="py-12 text-center text-gray-500">No candidates with active interview workflow yet.</div>
            ) : (
            <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-sm">
              <thead>
                <tr className="bg-[linear-gradient(135deg,#eef2ff_0%,#e0e7ff_100%)]">
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Name</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Contact</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Vacancy</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Company</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Interview Date</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Interview Time</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Candidate Availability</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">HR Confirmation</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Interview Status</th>
                  <th className="border px-3 py-2 text-left text-xs font-black uppercase tracking-wider text-indigo-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coordinationRegistrations.map((row) => {
                  const isPending = row.candidate_availability !== 'Available' || row.hr_assistant_confirmation !== 'Accepted';
                  return (
                  <tr key={row.id} className={`border-b transition-colors ${isPending ? 'pending-row-pulse' : 'bg-white hover:bg-indigo-50/40'}`}>
                    <td className="border px-3 py-2 font-bold text-gray-900">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isPending && <span className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0" style={{boxShadow:'0 0 0 3px rgba(245,158,11,0.3)'}} />}
                        {row.name || '-'}
                        <PartnerTag row={row} />
                      </div>
                    </td>
                    <td className="border px-3 py-2">
                      <ContactLink value={row.contact_number} />
                    </td>
                    <td className="border px-3 py-2 text-xs text-gray-700">{row.selected_job_title || '-'}</td>
                    <td className="border px-3 py-2 text-xs text-gray-700">{row.selected_job_company || '-'}</td>
                    <td className="border px-3 py-2 text-xs">{row.interview_date || <span className="text-gray-400">Not set</span>}</td>
                    <td className="border px-3 py-2 text-xs">{row.interview_time || <span className="text-gray-400">-</span>}</td>
                    <td className="border px-3 py-2">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${row.candidate_availability === 'Available' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : row.candidate_availability === 'Not Available' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}`}>
                        {row.candidate_availability || 'Pending'}
                      </span>
                    </td>
                    <td className="border px-3 py-2">
                      <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${row.hr_assistant_confirmation === 'Accepted' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : row.hr_assistant_confirmation === 'Rejected' ? 'bg-red-100 text-red-700 border-red-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}`}>
                        {row.hr_assistant_confirmation || 'Pending'}
                      </span>
                    </td>
                    <td className="border px-3 py-2 text-xs text-gray-700">{row.interview_status || '-'}</td>
                    <td className="border px-3 py-2">
                      <div className="flex gap-1 flex-wrap">
                        <button
                          type="button"
                          onClick={() => handleViewRegistration(row)}
                          className="rounded border border-blue-400 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                        >
                          View
                        </button>
                        {role === 'HR Assistant' && (
                          <button
                            type="button"
                            onClick={() => {
                              setInterviewCoordEditRow(row);
                              setInterviewCoordinationForm(createInterviewCoordinationForm(row));
                              setInterviewCoordinationFeedback('');
                              setInterviewCoordModalOpen(true);
                            }}
                            className="rounded border border-indigo-500 bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
                          >
                            Edit Coordination
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            )}
          </div>
          )}

          {/* ─── SERVICE CHARGES TAB ─────────────────────────────────────── */}
          {!loading && activeSection === 'service-charges' && (
          <div className="space-y-4">
            {pendingServiceCharges.length === 0 ? (
              <div className="rounded-[24px] border border-blue-200 bg-blue-50 py-12 text-center text-blue-700 font-bold">
                No pending service charges. All charges have been collected.
              </div>
            ) : (
              pendingServiceCharges.map((row) => (
                <ServiceChargeCard
                  key={row.id}
                  row={row}
                  API_URL={API_URL}
                  fetchServiceCharges={fetchServiceCharges}
                  fetchHRPaymentRequests={fetchHRPaymentRequests}
                />
              ))
            )}
          </div>
          )}

          {/* ─── OFFER INBOX TAB ─────────────────────────────────────────── */}
          {!loading && activeSection === 'offers' && (
          <div className="space-y-4">
            {pendingOffers.length === 0 ? (
              <div className="rounded-[24px] border border-amber-200 bg-amber-50 py-12 text-center text-amber-700 font-bold">
                No pending offers. All offer details have been delivered to members.
              </div>
            ) : (
              pendingOffers.map((row) => {
                const phone = String(row.whatsapp_number || row.contact_number || '').replace(/\D/g, '');
                const dialPhone = phone.length === 10 ? `91${phone}` : phone;
                const handleOfferCardSave = async (fields) => {
                  try {
                    const tk = localStorage.getItem('token');
                    const res = await axios.put(
                      `${API_URL}/api/staff/forwards/${row.id}/placement-steps`,
                      fields,
                      { headers: { Authorization: `Bearer ${tk}` } }
                    );
                    const updated = res.data?.registration;
                    if (updated) {
                      setForwardedRegistrations((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                    }
                    fetchPendingOffers();
                  } catch (err) {
                    alert(err?.response?.data?.error || 'Failed to save.');
                  }
                };
                const waLines = [
                  `Dear ${row.name || 'Candidate'},`,
                  ``,
                  `Congratulations! You have been selected for the following position:`,
                  ``,
                  row.offered_job_title ? `📌 Position: ${row.offered_job_title}` : null,
                  row.offered_salary ? `💰 Salary: ${row.offered_salary}` : null,
                  row.joining_date ? `📅 Joining Date: ${row.joining_date}` : null,
                  row.work_location ? `📍 Location: ${row.work_location}` : null,
                  row.probation_period ? `⏳ Probation: ${row.probation_period}` : null,
                  row.work_schedule ? `🕐 Schedule: ${row.work_schedule}` : null,
                  row.other_terms ? `📝 Terms: ${row.other_terms}` : null,
                  ``,
                  `Please confirm your acceptance at the earliest. – Dani Job Club`,
                ].filter((l) => l !== null).join('\n');
                return (
                  <div key={row.id} className={`overflow-hidden rounded-[20px] border bg-white shadow-sm ${row.dani_offer_sent ? 'border-blue-200' : 'border-amber-200'}`}>
                    <div className={`flex items-center justify-between gap-3 px-5 py-3 ${row.dani_offer_sent ? 'bg-[linear-gradient(135deg,#eff6ff_0%,#dbeafe_100%)]' : 'bg-[linear-gradient(135deg,#fffbeb_0%,#fef3c7_100%)]'}`}>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-base font-black ${row.dani_offer_sent ? 'text-blue-900' : 'text-amber-900'}`}>{row.name || 'Candidate'}</p>
                          <PartnerTag row={row} />
                        </div>
                        <p className={`text-xs ${row.dani_offer_sent ? 'text-blue-700' : 'text-amber-700'}`}>{row.selected_job_title ? `${row.selected_job_title}${row.selected_job_company ? ` — ${row.selected_job_company}` : ''}` : 'No vacancy linked'}</p>
                      </div>
                      {row.dani_offer_sent
                        ? <span className="rounded-full bg-blue-500 px-3 py-1 text-xs font-black text-white">⏳ Awaiting Member Response</span>
                        : <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-black text-white">Offer Pending Delivery</span>}
                    </div>
                    <div className="p-5">
                      <div className="mb-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-3">
                        {row.offered_job_title && (
                          <p><span className="font-bold text-gray-500">Position:</span> <span className="text-gray-900">{row.offered_job_title}</span></p>
                        )}
                        {row.offered_salary && (
                          <p><span className="font-bold text-gray-500">Salary:</span> <span className="font-bold text-emerald-700">{row.offered_salary}</span></p>
                        )}
                        {row.joining_date && (
                          <p><span className="font-bold text-gray-500">Joining:</span> <span className="text-gray-900">{row.joining_date}</span></p>
                        )}
                        {row.work_location && (
                          <p><span className="font-bold text-gray-500">Location:</span> <span className="text-gray-900">{row.work_location}</span></p>
                        )}
                        {row.probation_period && (
                          <p><span className="font-bold text-gray-500">Probation:</span> <span className="text-gray-900">{row.probation_period}</span></p>
                        )}
                        {row.work_schedule && (
                          <p><span className="font-bold text-gray-500">Schedule:</span> <span className="text-gray-900">{row.work_schedule}</span></p>
                        )}
                        {row.other_terms && (
                          <p className="col-span-2 md:col-span-3"><span className="font-bold text-gray-500">Other Terms:</span> <span className="text-gray-900">{row.other_terms}</span></p>
                        )}
                        {row.service_charge_amount && (
                          <p className="col-span-2 md:col-span-3"><span className="font-bold text-gray-500">Service Charge:</span> <span className="font-bold text-amber-700">{row.service_charge_amount}</span></p>
                        )}
                      </div>
                      {/* ── Member Response Section ── */}
                      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
                        <p className="mb-2 text-xs font-extrabold uppercase tracking-widest text-blue-800">Member Offer Response</p>
                        {row.dani_offer_sent ? (
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs text-blue-700">Offer already sent to member. Record their response:</p>
                            {['Accepted', 'Rejected'].map((opt) => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => handleOfferCardSave({ dani_offer_sent: true, member_offer_response: opt, payment_received: row.payment_received || 0, company_offer_sent: row.company_offer_sent || 0 })}
                                className={`rounded-xl px-5 py-2 text-sm font-extrabold transition ${row.member_offer_response === opt ? (opt === 'Accepted' ? 'bg-emerald-500 text-white shadow' : 'bg-red-500 text-white shadow') : (opt === 'Accepted' ? 'border-2 border-emerald-400 bg-white text-emerald-700 hover:bg-emerald-50' : 'border-2 border-red-300 bg-white text-red-600 hover:bg-red-50')}`}
                              >
                                {opt === 'Accepted' ? '✅ Member Accepted' : '❌ Member Rejected'}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-3">
                            <p className="text-xs text-gray-500">First send the offer letter to the member, then record their response.</p>
                            <button
                              type="button"
                              onClick={() => handleOfferCardSave({ dani_offer_sent: true, member_offer_response: '', payment_received: row.payment_received || 0, company_offer_sent: row.company_offer_sent || 0 })}
                              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-indigo-700"
                            >
                              ✉ Mark Offer Sent to Member
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-3">
                        {dialPhone && (
                          <a
                            href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(waLines)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 rounded-xl border border-green-400 bg-white px-4 py-2 text-sm font-bold text-green-800 hover:bg-green-50"
                          >
                            <span className="text-base">💬</span> Send Offer via WhatsApp
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setInterviewCoordEditRow(row);
                            setInterviewCoordinationForm({
                              candidate_availability: row.candidate_availability || 'Pending',
                              candidate_availability_note: row.candidate_availability_note || '',
                              hr_assistant_confirmation: row.hr_assistant_confirmation || 'Pending',
                              interview_date: row.interview_date || '',
                              interview_time: row.interview_time || '',
                              interview_location: row.interview_location || '',
                              interview_mode: row.interview_mode || '',
                              reporting_officer: row.reporting_officer || '',
                            });
                            setInterviewCoordModalOpen(true);
                          }}
                          className="flex items-center gap-2 rounded-xl border border-indigo-400 bg-white px-4 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-50"
                        >
                          <span>🔗</span> Open Candidate Record
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          )}

          {/* ─── REGULAR SECTION TABLE ──────────────────────────────────── */}
          {!loading && activeSection !== 'interview' && activeSection !== 'offers' && activeSection !== 'service-charges' && (
          <div className="overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="bg-[linear-gradient(135deg,#fffdfa_0%,#fff6d8_100%)]">
                  <th className="border px-3 py-2">Name</th>
                  <th className="border px-3 py-2">Place</th>
                  <th className="border px-3 py-2">Contact Number</th>
                  <th className="border px-3 py-2">Whatsapp Number</th>
                  <th className="border px-3 py-2">Highest Qualification</th>
                  <th className="border px-3 py-2">Resume</th>
                  <th className="border px-3 py-2">Photo</th>
                  <th className="border px-3 py-2">Communication Status</th>
                  <th className="border px-3 py-2">Forwarded At</th>
                  <th className="border px-3 py-2">Recruitment Process</th>
                  <th className="border px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredForwardedRegistrations.length === 0 && (
                  <tr>
                    <td colSpan="11" className="border px-3 py-3 text-center text-gray-500">No registrations found in {activeSectionMeta.label}.</td>
                  </tr>
                )}
                {paginatedForwardedRegistrations.map((row) => (
                  <tr key={row.id} className={`align-top odd:bg-white even:bg-gray-50/50 ${row.plan_type === 'prime' ? 'odd:bg-yellow-50 even:bg-yellow-50/60' : row.plan_type === 'regular' ? 'odd:bg-blue-50 even:bg-blue-50/60' : ''}`}>
                    <td className="border px-3 py-2">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-semibold">{row.name || '-'}</span>
                        {Number(row.via_partner) === 1 && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-400 border border-amber-500 px-2 py-0.5 text-[10px] font-black text-black w-fit" title={row.partner_org_name ? `Via Partner: ${row.partner_org_name}` : 'Via Partner'}>
                            🤝 Via Partner{row.partner_org_name ? ` · ${row.partner_org_name}` : ''}
                          </span>
                        )}
                        {row.plan_type === 'prime' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black text-black w-fit">👑 Prime</span>
                        )}
                        {row.plan_type === 'regular' && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 border border-blue-300 px-2 py-0.5 text-[10px] font-black text-blue-700 w-fit">⭐ Regular</span>
                        )}
                      </div>
                    </td>
                    <td className="border px-3 py-2">{row.place || '-'}</td>
                    <td className="border px-3 py-2"><ContactLink value={row.contact_number} /></td>
                    <td className="border px-3 py-2"><ContactLink value={row.whatsapp_number} type="whatsapp" /></td>
                    <td className="border px-3 py-2">{row.highest_qualification || '-'}</td>
                    <td className="border px-3 py-2">
                      {row.resume_file ? (
                        <a
                          href={row.resume_file.startsWith('http') ? row.resume_file : `${API_URL}${row.resume_file}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          View
                        </a>
                      ) : '-'}
                    </td>
                    <td className="border px-3 py-2">
                      {row.photo_file ? (
                        <a
                          href={row.photo_file.startsWith('http') ? row.photo_file : `${API_URL}${row.photo_file}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block"
                        >
                          <img
                            src={row.photo_file.startsWith('http') ? row.photo_file : `${API_URL}${row.photo_file}`}
                            alt="Forwarded candidate"
                            className="h-12 w-12 rounded border object-cover"
                          />
                        </a>
                      ) : '-'}
                    </td>
                    <td className="border px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <select
                          value={row.communication_status || 'Waiting for the results'}
                          onChange={(e) => {
                            if (e.target.value === 'Call Later') {
                              setCallLaterRow(row);
                              setCallLaterDateTime('');
                              setCallLaterModal(true);
                            } else if (e.target.value === 'Ready To Pay') {
                              setReadyToPayRow(row);
                              setReadyToPayModal(true);
                            } else if (e.target.value === 'Registration Fee Paid' || e.target.value === 'Prime Membership Fee Paid') {
                              setFeePaidRow(row);
                              setFeePaidType(e.target.value === 'Registration Fee Paid' ? 'Registration Fee' : 'Prime Membership');
                              setFeePaidAmount(e.target.value === 'Registration Fee Paid' ? '500' : '2000');
                              setFeePaidModal(true);
                            } else {
                              handleUpdateCommunicationStatus(row.id, e.target.value);
                            }
                          }}
                          disabled={statusUpdatingId === row.id}
                          className="rounded border px-2 py-1 text-sm"
                        >
                          {communicationStatusOptions.map((status) => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                        {row.communication_status === 'Call Later' && (() => {
                          const alarms = loadCallLaterAlarms();
                          const alarm = alarms.find((a) => a.forwardId === row.id && !a.dismissed);
                          return alarm ? (
                            <span className="text-[10px] text-amber-700 font-semibold">
                              ⏰ {fmtDateTime(alarm.scheduledAt)}
                            </span>
                          ) : null;
                        })()}
                        {paymentRequestsMap[row.id] && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${
                            paymentRequestsMap[row.id].status === 'Invoice Created'
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {paymentRequestsMap[row.id].status === 'Invoice Created' ? '✅ Invoice Created' : '📋 Sent to Accounts'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="border px-3 py-2">{row.forwarded_at || '-'}</td>
                    <td className="border px-3 py-2 text-xs text-gray-700">
                      {Number(row.workflow_id || 0) > 0 ? (
                        <div className="space-y-2">
                          <p className="font-bold text-black">{row.selected_job_title || 'Recruiter linked vacancy'}</p>
                          <p>Candidate: {row.candidate_availability || 'Pending'}</p>
                          <p>HR Confirmation: {row.hr_assistant_confirmation || 'Pending'}</p>
                        </div>
                      ) : (
                        <span className="text-gray-500">Waiting for recruiter shortlist</span>
                      )}
                    </td>
                    <td className="border px-3 py-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewRegistration(row)}
                          className="rounded border border-blue-500 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                        >
                          View
                        </button>
                        {role === 'HR Assistant' && Number(row.workflow_id || 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => handleViewRegistration(row)}
                            className="rounded border border-amber-500 px-2 py-1 text-xs font-bold text-amber-700 hover:bg-amber-50"
                            title="Interview Process"
                          >
                            Interview Process
                          </button>
                        )}
                        {role === 'HR Assistant' && (
                          <button
                            type="button"
                            onClick={() => handleForwardToRecruiter(row)}
                            disabled={!recruiterEligibleStatuses.includes(row.communication_status)}
                            className={`rounded border px-2 py-1 text-xs ${
                              recruiterEligibleStatuses.includes(row.communication_status)
                                ? 'border-emerald-600 text-emerald-700 hover:bg-emerald-50'
                                : 'cursor-not-allowed border-gray-300 text-gray-400'
                            }`}
                            title={
                              recruiterEligibleStatuses.includes(row.communication_status)
                                ? 'Forward To Recruiter'
                                : `Status must be Registration Fee Paid, Prime Membership Fee Paid, or Partner Member`
                            }
                          >
                            Forward To Recruiter
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {filteredForwardedRegistrations.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-gray-200 bg-gray-50 px-5 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">
                  Page {activeSectionPage} of {totalSectionPages} in {activeSectionMeta.label}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleSectionPageChange(activeSectionPage - 1)}
                    disabled={activeSectionPage === 1}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-black hover:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSectionPageChange(activeSectionPage + 1)}
                    disabled={activeSectionPage === totalSectionPages}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-black hover:border-yellow-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {selectedRegistration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-[28px] bg-white p-6 text-black shadow-xl">
            <div className="flex flex-col gap-4 border-b border-gray-200 pb-5 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.26em] text-amber-600">Forwarded Registration</p>
                <h3 className="mt-2 text-2xl font-black">{selectedRegistration.name || 'Talent Registration'}</h3>
                <p className="mt-1 text-sm text-gray-500">Communication status: {selectedRegistration.communication_status || 'Waiting for the results'}</p>
              </div>
              <div className="grid min-w-[220px] grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Registration No</p>
                  <p className="mt-1 text-base font-black text-gray-900">{selectedRegistration.registration_number || detailForm.registration_number || '-'}</p>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Forwarded At</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{selectedRegistration.forwarded_at || '-'}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_1fr]">
              <div className="space-y-6">
                <div className="rounded-[24px] border border-gray-200 bg-gray-50 p-5">
                  <h4 className="text-sm font-black uppercase tracking-[0.24em] text-gray-500">Basic Registration</h4>
                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <div><span className="font-semibold">Name:</span> {selectedRegistration.name || '-'}</div>
                    <div><span className="font-semibold">Email:</span> {selectedRegistration.email || '-'}</div>
                    <div><span className="font-semibold">Place:</span> {selectedRegistration.place || '-'}</div>
                    <div><span className="font-semibold">District:</span> {selectedRegistration.district || '-'}</div>
                    <div><span className="font-semibold">Contact Number:</span> <ContactLink value={selectedRegistration.contact_number} /></div>
                    <div><span className="font-semibold">Whatsapp Number:</span> <ContactLink value={selectedRegistration.whatsapp_number} type="whatsapp" /></div>
                    <div><span className="font-semibold">Highest Qualification:</span> {selectedRegistration.highest_qualification || '-'}</div>
                    <div><span className="font-semibold">Expecting Job:</span> {selectedRegistration.expecting_job || '-'}</div>
                    <div><span className="font-semibold">Department:</span> {selectedRegistration.department || '-'}</div>
                    <div><span className="font-semibold">Section:</span> {selectedRegistration.section || '-'}</div>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-4 text-sm">
                    <div>
                      <span className="font-semibold">Resume: </span>
                      {selectedRegistration.resume_file ? (
                        <a
                          href={selectedRegistration.resume_file.startsWith('http') ? selectedRegistration.resume_file : `${API_URL}${selectedRegistration.resume_file}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          View
                        </a>
                      ) : '-'}
                    </div>
                    <div>
                      <span className="font-semibold">Photo: </span>
                      {selectedRegistration.photo_file ? (
                        <a
                          href={selectedRegistration.photo_file.startsWith('http') ? selectedRegistration.photo_file : `${API_URL}${selectedRegistration.photo_file}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 underline"
                        >
                          View
                        </a>
                      ) : '-'}
                    </div>
                  </div>

                  {role === 'HR Assistant' && (
                    <div className="mt-5 rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-4">
                      <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-700 mb-3">Upload Resume / Photo</p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="text-sm font-semibold text-gray-700">
                          Resume <span className="text-xs font-normal text-gray-400">(PDF or JPG/JPEG)</span>
                          <input
                            type="file"
                            accept=".pdf,application/pdf,.jpg,.jpeg,image/jpeg,image/jpg"
                            onChange={(e) => setFileUploadState((prev) => ({ ...prev, resumeFile: e.target.files[0] || null }))}
                            className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-amber-100 file:px-3 file:py-1 file:text-xs file:font-bold file:text-amber-700 hover:file:bg-amber-200"
                          />
                          {fileUploadState.resumeFile && (
                            <span className="mt-1 block truncate text-xs text-gray-500">{fileUploadState.resumeFile.name}</span>
                          )}
                        </label>
                        <label className="text-sm font-semibold text-gray-700">
                          Photo <span className="text-xs font-normal text-gray-400">(JPG only)</span>
                          <input
                            type="file"
                            accept=".jpg,.jpeg,image/jpeg"
                            onChange={(e) => setFileUploadState((prev) => ({ ...prev, photoFile: e.target.files[0] || null }))}
                            className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-full file:border-0 file:bg-amber-100 file:px-3 file:py-1 file:text-xs file:font-bold file:text-amber-700 hover:file:bg-amber-200"
                          />
                          {fileUploadState.photoFile && (
                            <span className="mt-1 block truncate text-xs text-gray-500">{fileUploadState.photoFile.name}</span>
                          )}
                        </label>
                      </div>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={handleUploadMemberFiles}
                          disabled={fileUploading || (!fileUploadState.resumeFile && !fileUploadState.photoFile)}
                          className="rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-amber-300"
                        >
                          {fileUploading ? 'Uploading...' : 'Upload'}
                        </button>
                        {fileUploadFeedback && (
                          <span className={`text-xs font-semibold ${fileUploadFeedback.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                            {fileUploadFeedback}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-black uppercase tracking-[0.24em] text-gray-500">Experience Details</h4>
                    {role === 'HR Assistant' && (
                      <button
                        type="button"
                        onClick={handleAddExperience}
                        className="rounded-full border border-amber-300 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-amber-700 hover:bg-amber-50"
                      >
                        Add Experience
                      </button>
                    )}
                  </div>

                  <div className="mt-4 space-y-4">
                    {detailForm.experience_details.map((experience, index) => (
                      <div key={`${selectedRegistration.id}-experience-${index}`} className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <p className="text-sm font-bold text-gray-800">Experience {index + 1}</p>
                          {role === 'HR Assistant' && detailForm.experience_details.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveExperience(index)}
                              className="rounded-full border border-red-200 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-red-600 hover:bg-red-50"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <label className="text-sm font-semibold text-gray-700">Exp Department
                            <input
                              value={experience.exp_department}
                              onChange={(e) => handleExperienceFieldChange(index, 'exp_department', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                          <label className="text-sm font-semibold text-gray-700">Title Post
                            <input
                              value={experience.title_post}
                              onChange={(e) => handleExperienceFieldChange(index, 'title_post', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                          <label className="text-sm font-semibold text-gray-700">Company Name
                            <input
                              value={experience.company_name}
                              onChange={(e) => handleExperienceFieldChange(index, 'company_name', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                          <label className="text-sm font-semibold text-gray-700">Salary
                            <input
                              value={experience.salary}
                              onChange={(e) => handleExperienceFieldChange(index, 'salary', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                          <label className="text-sm font-semibold text-gray-700">Total Years
                            <input
                              value={experience.total_years}
                              onChange={(e) => handleExperienceFieldChange(index, 'total_years', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              placeholder="e.g. 2.5"
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                          <label className="text-sm font-semibold text-gray-700">Location
                            <input
                              value={experience.location}
                              onChange={(e) => handleExperienceFieldChange(index, 'location', e.target.value)}
                              disabled={role !== 'HR Assistant'}
                              placeholder="City / Country"
                              className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 font-normal disabled:bg-gray-100"
                            />
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="rounded-[24px] border border-amber-200 bg-[linear-gradient(180deg,#fffdf8_0%,#fff7e6_100%)] p-5 shadow-sm">
                <h4 className="text-sm font-black uppercase tracking-[0.24em] text-amber-700">Internal HR Details</h4>
                <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                  <label className="text-sm font-semibold text-gray-700">Date of Communication
                    <input
                      type="date"
                      name="date_of_communication"
                      value={detailForm.date_of_communication}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">Age
                    <input
                      name="age"
                      value={detailForm.age}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">Gender
                    <select
                      name="gender"
                      value={detailForm.gender}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    >
                      <option value="">— Select —</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </label>
                  <div className="text-sm font-semibold text-gray-700 md:col-span-2">
                    <span>Google Location</span>
                    <div className="mt-1 space-y-2">
                      <div className="flex gap-2 items-center">
                        <input
                          name="google_location"
                          value={detailForm.google_location}
                          onChange={handleDetailFieldChange}
                          disabled={role !== 'HR Assistant'}
                          placeholder="Google Maps URL or coordinates"
                          className="flex-1 rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                        />
                        {role === 'HR Assistant' && (
                          <button
                            type="button"
                            onClick={() => { setMapPickerOpen(true); setMapPreviewCoords(null); setMapSearch(''); setMapSearchResults([]); }}
                            className="shrink-0 rounded-xl bg-amber-500 px-3 py-2 text-white font-bold hover:bg-amber-600 text-sm"
                            title="Pick location on map"
                          >📍 Pick on Map</button>
                        )}
                        {detailForm.google_location && (
                          <a
                            href={detailForm.google_location}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 rounded-xl bg-green-500 px-3 py-2 text-white font-bold hover:bg-green-600 text-sm"
                          >🗺️ View</a>
                        )}
                      </div>
                      {detailForm.google_location && detailForm.google_location.includes('maps.google.com/?q=') && (() => {
                        const m = detailForm.google_location.match(/q=([-\d.]+),([-\d.]+)/);
                        if (!m) return null;
                        const lat = m[1], lon = m[2];
                        return (
                          <div className="overflow-hidden rounded-xl border border-amber-200">
                            <iframe
                              title="Location Preview"
                              width="100%"
                              height="180"
                              style={{ border: 0 }}
                              loading="lazy"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(lon)-0.01},${parseFloat(lat)-0.01},${parseFloat(lon)+0.01},${parseFloat(lat)+0.01}&layer=mapnik&marker=${lat},${lon}`}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {mapPickerOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 md:col-span-2">
                      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
                        <div className="bg-amber-500 px-5 py-4 flex items-center justify-between">
                          <h3 className="text-lg font-extrabold text-white">📍 Pick Member Location</h3>
                          <button type="button" onClick={() => setMapPickerOpen(false)} className="text-white font-bold text-xl hover:text-amber-200">✕</button>
                        </div>
                        <div className="p-5 space-y-4">
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={mapSearch}
                              onChange={(e) => setMapSearch(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleMapSearch()}
                              placeholder="Search address or area..."
                              className="flex-1 rounded-xl border border-gray-300 px-3 py-2 outline-none focus:border-amber-400"
                            />
                            <button type="button" onClick={handleMapSearch} disabled={mapSearching} className="rounded-xl bg-amber-500 px-4 py-2 font-bold text-white hover:bg-amber-600 disabled:bg-amber-300">
                              {mapSearching ? '...' : 'Search'}
                            </button>
                          </div>
                          {mapSearchResults.length > 0 && (
                            <ul className="rounded-xl border border-gray-200 divide-y divide-gray-100 max-h-40 overflow-y-auto">
                              {mapSearchResults.map((r, i) => (
                                <li key={i}>
                                  <button type="button" onClick={() => handleMapSelectResult(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 text-gray-800">
                                    {r.display_name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <button type="button" onClick={handleMapGPS} disabled={mapGpsLoading} className="w-full rounded-xl border-2 border-amber-400 py-2 font-bold text-amber-700 hover:bg-amber-50 disabled:opacity-60">
                            {mapGpsLoading ? '⏳ Getting GPS...' : '📡 Use Device GPS Location'}
                          </button>
                          {mapPreviewCoords && (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-600 font-medium truncate">📌 {mapPreviewCoords.display_name}</p>
                              <div className="overflow-hidden rounded-xl border border-amber-200">
                                <iframe
                                  title="Map Preview"
                                  width="100%"
                                  height="200"
                                  style={{ border: 0 }}
                                  loading="lazy"
                                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(mapPreviewCoords.lon)-0.01},${parseFloat(mapPreviewCoords.lat)-0.01},${parseFloat(mapPreviewCoords.lon)+0.01},${parseFloat(mapPreviewCoords.lat)+0.01}&layer=mapnik&marker=${mapPreviewCoords.lat},${mapPreviewCoords.lon}`}
                                />
                              </div>
                              <button type="button" onClick={handleMapConfirm} className="w-full rounded-xl bg-green-500 py-2 font-bold text-white hover:bg-green-600">
                                ✅ Confirm This Location
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <label className="text-sm font-semibold text-gray-700">Guardian Number
                    <input
                      name="guardian_number"
                      value={detailForm.guardian_number}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      maxLength={10}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">Contact Through
                    <input
                      name="contact_through"
                      value={detailForm.contact_through}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <div className="text-sm font-semibold text-gray-700">Qualification Department
                    {role === 'HR Assistant' ? (
                      <div className="mt-1 space-y-1">
                        <select
                          value={QUALIFICATION_OPTIONS.includes(detailForm.qualification_department) ? detailForm.qualification_department : (detailForm.qualification_department ? 'Other' : '')}
                          onChange={(e) => {
                            if (e.target.value === 'Other') {
                              // Keep a typed custom value; clear a list value so the box below is ready for typing
                              setDetailForm((prev) => ({ ...prev, qualification_department: QUALIFICATION_OPTIONS.includes(prev.qualification_department) && prev.qualification_department !== 'Other' ? '' : prev.qualification_department }));
                              return;
                            }
                            setDetailForm((prev) => ({ ...prev, qualification_department: e.target.value }));
                          }}
                          className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal"
                        >
                          <option value="">-- Pick from list --</option>
                          {QUALIFICATION_OPTIONS.map((q) => <option key={q} value={q}>{q}</option>)}
                        </select>
                        <input
                          name="qualification_department"
                          value={detailForm.qualification_department}
                          onChange={handleDetailFieldChange}
                          placeholder="Or type custom qualification..."
                          className="w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 font-normal text-sm"
                        />
                      </div>
                    ) : (
                      <input
                        name="qualification_department"
                        value={detailForm.qualification_department}
                        disabled
                        className="mt-1 w-full rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 font-normal"
                      />
                    )}
                  </div>
                  <label className="text-sm font-semibold text-gray-700">Completed Year
                    <input
                      name="completed_year"
                      value={detailForm.completed_year}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">Exam Status
                    <select
                      name="exam_status"
                      value={detailForm.exam_status}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    >
                      <option value="">-- Select --</option>
                      {EXAM_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <div className="text-sm font-semibold text-gray-700 md:col-span-2">
                    <span>Preferred District</span>
                    <span className="ml-1 text-xs font-normal text-gray-400">(multiple allowed)</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {KERALA_DISTRICTS_HR.map((d) => {
                        const selected = detailForm.preferred_district.includes(d);
                        return (
                          <button
                            key={d}
                            type="button"
                            disabled={role !== 'HR Assistant'}
                            onClick={() => {
                              if (role !== 'HR Assistant') return;
                              setDetailForm((prev) => ({
                                ...prev,
                                preferred_district: selected
                                  ? prev.preferred_district.filter((x) => x !== d)
                                  : [...prev.preferred_district, d]
                              }));
                            }}
                            className={`rounded-full px-3 py-1 text-xs font-bold border transition ${selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-amber-200 hover:border-amber-400'} disabled:opacity-60 disabled:cursor-default`}
                          >{d}</button>
                        );
                      })}
                    </div>
                  </div>
                  <label className="text-sm font-semibold text-gray-700">Preferred Location
                    <input
                      name="preferred_location"
                      value={detailForm.preferred_location}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      placeholder="Specific area / town (optional)"
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <div className="text-sm font-semibold text-gray-700">
                    <span>Driving License</span>
                    {role === 'HR Assistant' ? (
                      <div className="mt-1 flex gap-3">
                        {['Yes','No'].map((v) => (
                          <label key={v} className="flex items-center gap-1 cursor-pointer font-normal text-gray-800">
                            <input
                              type="radio"
                              name="driving_license"
                              value={v}
                              checked={detailForm.driving_license === v}
                              onChange={handleDetailFieldChange}
                              className="accent-amber-500"
                            />
                            {v}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 font-normal text-gray-700">{detailForm.driving_license || '—'}</p>
                    )}
                  </div>
                  {detailForm.driving_license === 'Yes' && (
                    <label className="text-sm font-semibold text-gray-700">License Category
                      <input
                        name="license_category"
                        value={detailForm.license_category}
                        onChange={handleDetailFieldChange}
                        disabled={role !== 'HR Assistant'}
                        placeholder="e.g. LMV, HMV, Two-wheeler..."
                        className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                      />
                    </label>
                  )}
                  <label className="text-sm font-semibold text-gray-700">Marital Status
                    <input
                      name="marital_status"
                      value={detailForm.marital_status}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <div className="text-sm font-semibold text-gray-700 md:col-span-2">
                    <span>Highlight Language</span>
                    <span className="ml-1 text-xs font-normal text-gray-400">(multiple allowed)</span>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {HIGHLIGHT_LANGUAGE_OPTIONS.map((lang) => {
                        const selected = detailForm.highlight_language.includes(lang);
                        return (
                          <button
                            key={lang}
                            type="button"
                            disabled={role !== 'HR Assistant'}
                            onClick={() => {
                              if (role !== 'HR Assistant') return;
                              setDetailForm((prev) => ({
                                ...prev,
                                highlight_language: selected
                                  ? prev.highlight_language.filter((x) => x !== lang)
                                  : [...prev.highlight_language, lang]
                              }));
                            }}
                            className={`rounded-full px-3 py-1 text-xs font-bold border transition ${selected ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-amber-200 hover:border-amber-400'} disabled:opacity-60 disabled:cursor-default`}
                          >{lang}</button>
                        );
                      })}
                    </div>
                  </div>
                  <label className="text-sm font-semibold text-gray-700">Job Priority
                    <input
                      name="job_priority"
                      value={detailForm.job_priority}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">Rating
                    <input
                      name="rating"
                      value={detailForm.rating}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </label>
                  <label className="text-sm font-semibold text-gray-700">How you heard about Dani
                    <select
                      name="how_you_heard"
                      value={detailForm.how_you_heard}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    >
                      <option value="">-- Select --</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Facebook">Facebook</option>
                      <option value="OLX">OLX</option>
                      <option value="Google">Google</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Friends">Friends</option>
                      <option value="Theatre Ad">Theatre Ad</option>
                      <option value="News Paper">News Paper</option>
                      <option value="YouTube">YouTube</option>
                      <option value="Twitter / X">Twitter / X</option>
                      <option value="LinkedIn">LinkedIn</option>
                      <option value="Radio">Radio</option>
                      <option value="Hoarding / Banner">Hoarding / Banner</option>
                      <option value="Walk-in">Walk-in</option>
                      <option value="Other">Other</option>
                    </select>
                  </label>
                  <div className="text-sm font-semibold text-gray-700 md:col-span-2">
                    <span>Additional Qualification</span>
                    <span className="ml-1 text-xs font-normal text-gray-400">(add multiple)</span>
                    {role === 'HR Assistant' && (
                      <div className="mt-1 flex gap-2">
                        <input
                          value={newQualificationInput}
                          onChange={(e) => setNewQualificationInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddQualification();
                            }
                          }}
                          placeholder="e.g. Diploma in Computer Applications"
                          className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal"
                        />
                        <button
                          type="button"
                          onClick={handleAddQualification}
                          className="shrink-0 rounded-xl bg-amber-500 px-4 py-2 font-bold text-white hover:bg-amber-600"
                        >
                          Add
                        </button>
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {detailForm.additional_qualification.length === 0 && (
                        <span className="text-xs font-normal text-gray-400">No additional qualifications added.</span>
                      )}
                      {detailForm.additional_qualification.map((q) => (
                        <span key={q} className="flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-gray-700">
                          {q}
                          {role === 'HR Assistant' && (
                            <button type="button" onClick={() => handleRemoveQualification(q)} className="text-gray-400 hover:text-red-600">✕</button>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-gray-700 md:col-span-2">
                    <span>Remarks</span>
                    <textarea
                      name="remarks"
                      rows={3}
                      value={detailForm.remarks}
                      onChange={handleDetailFieldChange}
                      disabled={role !== 'HR Assistant'}
                      placeholder="Any additional notes about this candidate..."
                      className="mt-1 w-full resize-none rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal disabled:bg-amber-50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <div className="flex flex-wrap gap-3">
                {role === 'HR Assistant' && (
                  <button
                    type="button"
                    onClick={handleSaveRegistrationDetails}
                    disabled={detailSaving}
                    className="rounded-full bg-amber-500 px-5 py-2 font-bold text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-amber-300"
                  >
                    {detailSaving ? 'Saving...' : 'Save Details'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedRegistration(null);
                    setDetailFeedback('');
                    setInterviewCoordinationFeedback('');
                  }}
                  className="rounded-full bg-gray-200 px-5 py-2 font-bold text-black hover:bg-gray-300"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Call Later — Date/Time Picker Modal */}
      {/* ══════════════════════════════════════════════════════════════
          PLACEMENT CELEBRATION MODAL
      ══════════════════════════════════════════════════════════════ */}
      {placementCelebration && (() => {
        const { name, job, company, phone } = placementCelebration;
        const jobLine = job ? `${job}${company ? ` at ${company}` : ''}` : '';
        const firstDayMsg = [
          `🌟 Dear ${name},`,
          ``,
          `Congratulations on your first day at work!`,
          ``,
          `We at Dani Job Club are incredibly proud of you. You have worked hard to reach this milestone and today marks the beginning of an exciting new chapter in your career.`,
          ``,
          jobLine ? `📌 Position: ${jobLine}` : null,
          ``,
          `Remember — every great journey begins with a single step. Give your best, stay positive, and never stop growing. We believe in you!`,
          ``,
          `Wishing you the very best,`,
          `Team Dani Job Club 💛`,
        ].filter((l) => l !== null).join('\n');
        return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 overflow-hidden">
            {/* Animated backdrop */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/95 via-black/90 to-yellow-900/95" />

            {/* Confetti particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${(i * 37) % 100}%`,
                    top: `-${10 + (i * 13) % 20}%`,
                    width: `${8 + (i % 5) * 4}px`,
                    height: `${8 + (i % 4) * 3}px`,
                    borderRadius: i % 3 === 0 ? '50%' : '2px',
                    background: ['#fbbf24', '#34d399', '#60a5fa', '#f472b6', '#a78bfa', '#fb923c'][i % 6],
                    animation: `confettiFall ${2 + (i % 4) * 0.5}s ${(i * 0.12) % 2}s ease-in infinite`,
                    opacity: 0.85,
                    transform: `rotate(${i * 47}deg)`,
                  }}
                />
              ))}
            </div>

            <style>{`
              @keyframes confettiFall {
                0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
              }
              @keyframes celebPop {
                0% { transform: scale(0.5) translateY(40px); opacity: 0; }
                70% { transform: scale(1.05) translateY(-4px); opacity: 1; }
                100% { transform: scale(1) translateY(0); opacity: 1; }
              }
              @keyframes pulseGlow {
                0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.4); }
                50% { box-shadow: 0 0 0 20px rgba(251,191,36,0); }
              }
            `}</style>

            {/* Card */}
            <div
              className="relative z-10 w-full max-w-md rounded-[32px] bg-white text-center overflow-hidden"
              style={{ animation: 'celebPop 0.6s cubic-bezier(0.34,1.56,0.64,1) both' }}
            >
              {/* Header band */}
              <div className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-emerald-400 px-6 pt-8 pb-6">
                <div className="text-5xl mb-2" style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.15))' }}>🎉</div>
                <h2 className="text-2xl font-black text-gray-900 leading-tight">Wonderful Job!</h2>
                <p className="text-sm font-extrabold text-gray-700 mt-1">Payment Successfully Received</p>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                <div
                  className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-5 py-4"
                  style={{ animation: 'pulseGlow 2s ease-in-out infinite' }}
                >
                  <p className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-1">Placement Complete</p>
                  <p className="text-xl font-black text-gray-900">{name}</p>
                  {jobLine && <p className="text-sm font-semibold text-emerald-700 mt-0.5">📌 {jobLine}</p>}
                </div>

                <p className="text-sm text-gray-600 leading-relaxed font-medium">
                  The service charge payment has been received. The candidate is now <span className="font-extrabold text-emerald-700">Officially Placed</span>. The Accountant will issue the final tax invoice.
                </p>

                <div className="space-y-2">
                  {phone && (
                    <a
                      href={`https://wa.me/${phone}?text=${encodeURIComponent(firstDayMsg)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-extrabold text-white hover:bg-emerald-700 transition"
                      onClick={() => setPlacementCelebration(null)}
                    >
                      🌟 Send First Job Day Wishes via WhatsApp
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setPlacementCelebration(null)}
                    className="w-full rounded-2xl bg-gray-100 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-200 transition"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {callLaterModal && callLaterRow && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-[24px] bg-white p-6 shadow-2xl">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-amber-600">Schedule Call Later</p>
            <h3 className="mt-1 text-lg font-black text-gray-900">{callLaterRow.name || 'Candidate'}</h3>
            <p className="mt-0.5 text-sm text-gray-500">{callLaterRow.contact_number || callLaterRow.whatsapp_number || ''}</p>
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-700">
                Select Date &amp; Time
                <input
                  type="datetime-local"
                  value={callLaterDateTime}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  onChange={(e) => setCallLaterDateTime(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </label>
            </div>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                disabled={!callLaterDateTime || callLaterSaving}
                onClick={async () => {
                  if (!callLaterDateTime) return;
                  setCallLaterSaving(true);
                  try {
                    await handleUpdateCommunicationStatus(callLaterRow.id, 'Call Later');
                    saveCallLaterAlarm(callLaterRow.id, callLaterRow.name || 'Candidate', callLaterDateTime);
                    setCallLaterModal(false);
                    setCallLaterRow(null);
                  } finally {
                    setCallLaterSaving(false);
                  }
                }}
                className="flex-1 rounded-full bg-amber-500 px-4 py-2 font-bold text-white hover:bg-amber-600 disabled:opacity-50"
              >
                {callLaterSaving ? 'Saving…' : 'Set Alarm'}
              </button>
              <button
                type="button"
                onClick={() => { setCallLaterModal(false); setCallLaterRow(null); }}
                className="flex-1 rounded-full bg-gray-200 px-4 py-2 font-bold text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ready To Pay — Payment Details Modal */}
      {readyToPayModal && readyToPayRow && (() => {
        const rawPhone = (readyToPayRow.whatsapp_number || readyToPayRow.contact_number || '').replace(/\D/g, '');
        const phone = rawPhone.startsWith('91') && rawPhone.length === 12 ? rawPhone : `91${rawPhone}`;
        const waLink = `https://wa.me/${phone}?text=${encodeURIComponent(READY_TO_PAY_MESSAGE)}`;
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-[24px] bg-white shadow-2xl">
              {/* Header */}
              <div className="bg-emerald-600 px-6 py-4 rounded-t-[24px]">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-emerald-200">Payment Request</p>
                <h3 className="mt-0.5 text-lg font-black text-white">{readyToPayRow.name || 'Candidate'}</h3>
                <p className="text-emerald-200 text-sm">{readyToPayRow.whatsapp_number || readyToPayRow.contact_number || ''}</p>
              </div>

              <div className="p-6 space-y-5">
                {/* QR Code */}
                <div className="flex flex-col items-center gap-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Scan &amp; Pay</p>
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/payment-qr.png`}
                    alt="Payment QR Code"
                    className="w-48 h-48 rounded-xl border border-gray-200 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="text-xs text-gray-400">UPI: lwin123@fbl</p>
                </div>

                {/* Bank Details */}
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm space-y-1.5">
                  <p className="font-black text-emerald-800 text-xs uppercase tracking-wider mb-2">Bank Transfer Details</p>
                  <p><span className="font-semibold text-gray-700">Account Name:</span> Dani Job Club</p>
                  <p><span className="font-semibold text-gray-700">Account Number:</span> 1256955555</p>
                  <p><span className="font-semibold text-gray-700">IFSC Code:</span> FDRL00025</p>
                  <p><span className="font-semibold text-gray-700">Bank:</span> Federal Bank</p>
                  <p><span className="font-semibold text-gray-700">Branch:</span> Kulappully</p>
                  <p><span className="font-semibold text-emerald-700">Amount:</span> <span className="font-black text-emerald-700">₹500</span></p>
                </div>

                {/* WhatsApp Message Preview */}
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">WhatsApp Message</p>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
                    {READY_TO_PAY_MESSAGE}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    disabled={readyToPaySaving}
                    onClick={async () => {
                      setReadyToPaySaving(true);
                      try {
                        await handleUpdateCommunicationStatus(readyToPayRow.id, 'Ready To Pay');
                        window.open(waLink, '_blank', 'noopener,noreferrer');
                        setReadyToPayModal(false);
                        setReadyToPayRow(null);
                      } catch (_) {
                        // error already shown by handleUpdateCommunicationStatus
                      } finally {
                        setReadyToPaySaving(false);
                      }
                    }}
                    className="flex-1 rounded-full bg-emerald-600 px-4 py-2.5 text-center font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {readyToPaySaving ? 'Saving…' : '📲 Send via WhatsApp'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setReadyToPayModal(false); setReadyToPayRow(null); }}
                    className="flex-1 rounded-full bg-gray-200 px-4 py-2.5 font-bold text-gray-700 hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Fee Paid Modal — Registration Fee / Prime Membership */}
      {feePaidModal && feePaidRow && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-[28px] bg-white shadow-2xl overflow-hidden">
            <div className="bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.26),transparent_24%),linear-gradient(135deg,#090909_0%,#1c1c1c_55%,#5b4703_100%)] px-6 py-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-400">💰 Payment Received</p>
              <h3 className="mt-0.5 text-lg font-black text-white">{feePaidRow.name || 'Candidate'}</h3>
              <p className="text-xs text-gray-400 mt-0.5">{feePaidType} · {feePaidRow.place || ''}{feePaidRow.district ? `, ${feePaidRow.district}` : ''}</p>
            </div>

            <div className="p-6 space-y-5">
              {feePaidFeedback && (
                <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
                  feePaidFeedback.includes('✅') ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'
                }`}>{feePaidFeedback}</div>
              )}

              {/* Member summary */}
              <div className="rounded-[20px] border border-yellow-200 bg-yellow-50 p-4 text-sm space-y-1.5">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-700 mb-2">Member Details to Accountant</p>
                {[
                  ['Member ID', feePaidRow.registration_number || feePaidRow.registration_id || '—'],
                  ['Name', feePaidRow.name || '—'],
                  ['Place', feePaidRow.place || '—'],
                  ['District', feePaidRow.district || '—'],
                  ['Contact', feePaidRow.contact_number || feePaidRow.whatsapp_number || '—'],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-20 shrink-0 text-xs font-bold text-gray-500">{k}</span>
                    <span className="font-black text-gray-900">{v}</span>
                  </div>
                ))}
              </div>

              {/* Paid Amount input */}
              <label className="block text-xs font-black uppercase tracking-[0.2em] text-gray-700">
                Paid Amount (₹) *
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={feePaidAmount}
                  onChange={(e) => setFeePaidAmount(e.target.value)}
                  className="mt-1 w-full rounded-xl border-2 border-yellow-300 bg-yellow-50 px-4 py-2.5 text-lg font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  placeholder="e.g. 500"
                />
              </label>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  disabled={feePaidSaving || !feePaidAmount}
                  onClick={async () => {
                    setFeePaidSaving(true);
                    setFeePaidFeedback('');
                    const statusLabel = feePaidType === 'Registration Fee' ? 'Registration Fee Paid' : 'Prime Membership Fee Paid';
                    try {
                      // 1. Update communication status
                      await handleUpdateCommunicationStatus(feePaidRow.id, statusLabel);
                      // 2. Send payment request to accountant
                      const token = localStorage.getItem('token');
                      await axios.post(`${API_URL}/api/hr/payment-requests`, {
                        forward_id: feePaidRow.id,
                        member_id: feePaidRow.registration_number || feePaidRow.registration_id || '',
                        member_name: feePaidRow.name,
                        place: feePaidRow.place,
                        district: feePaidRow.district,
                        contact_number: feePaidRow.contact_number || feePaidRow.whatsapp_number,
                        paid_amount: parseFloat(feePaidAmount),
                        payment_type: feePaidType,
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      // Refresh badges
                      fetchHRPaymentRequests();
                      setFeePaidFeedback('✅ Status updated and sent to Accountant!');
                      setTimeout(() => {
                        setFeePaidModal(false);
                        setFeePaidRow(null);
                        setFeePaidFeedback('');
                      }, 1500);
                    } catch (err) {
                      setFeePaidFeedback(`❌ ${err?.response?.data?.error || 'Failed to send to accountant.'}`);
                    } finally {
                      setFeePaidSaving(false);
                    }
                  }}
                  className="flex-1 rounded-full bg-black px-5 py-2.5 font-black text-yellow-300 hover:bg-gray-900 disabled:opacity-60 text-sm"
                >
                  {feePaidSaving ? 'Sending…' : '📋 Confirm & Send to Accounts'}
                </button>
                <button
                  type="button"
                  onClick={() => { setFeePaidModal(false); setFeePaidRow(null); setFeePaidFeedback(''); }}
                  className="rounded-full border border-gray-300 bg-white px-4 py-2.5 font-bold text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2nd Payment Modal — Interest Plan, Job Day 1 */}
      {secondPayModal && secondPayRow && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-[28px] bg-white shadow-2xl overflow-hidden">
            <div className="bg-[linear-gradient(135deg,#1e3a8a_0%,#1d4ed8_100%)] px-6 py-4">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-200">💼 Job Placement — 2nd Payment</p>
              <h3 className="mt-0.5 text-lg font-black text-white">{secondPayRow.name || 'Member'}</h3>
              <p className="text-xs text-blue-200 mt-0.5">Regular Plan · Day 1 of Work — ₹2,999 due</p>
            </div>
            <div className="p-6 space-y-4">
              {secondPayFeedback && (
                <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${secondPayFeedback.includes('✅') ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>{secondPayFeedback}</div>
              )}
              <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm space-y-1">
                {[['Contact', secondPayRow.contact_number], ['District', secondPayRow.district], ['Department', secondPayRow.department], ['Expecting Job', secondPayRow.expecting_job]].filter(([, v]) => v).map(([k, v]) => (
                  <p key={k}><span className="font-bold text-gray-500">{k}:</span> {v}</p>
                ))}
              </div>
              <label className="block text-xs font-black uppercase tracking-[0.2em] text-gray-700">
                Amount Collected (₹) *
                <input type="number" min="0" step="0.01" value={secondPayAmount}
                  onChange={(e) => setSecondPayAmount(e.target.value)}
                  className="mt-1 w-full rounded-xl border-2 border-blue-300 bg-blue-50 px-4 py-2.5 text-lg font-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="2999" />
              </label>
              <div className="flex gap-3 pt-1">
                <button type="button" disabled={secondPaySaving || !secondPayAmount}
                  onClick={async () => {
                    setSecondPaySaving(true); setSecondPayFeedback('');
                    try {
                      const token = localStorage.getItem('token');
                      await axios.put(`${API_URL}/api/staff/hr/member-plan/${secondPayRow.plan_id}/second-payment`,
                        { amount: parseFloat(secondPayAmount) },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      setSecondPayFeedback('✅ Payment recorded — member marked as placed!');
                      fetchForwardedRegistrations({ silent: true });
                      setTimeout(() => { setSecondPayModal(false); setSecondPayRow(null); setSecondPayFeedback(''); }, 1500);
                    } catch (err) {
                      setSecondPayFeedback(`❌ ${err?.response?.data?.error || 'Failed to record payment'}`);
                    } finally { setSecondPaySaving(false); }
                  }}
                  className="flex-1 rounded-full bg-blue-600 px-5 py-2.5 font-black text-white hover:bg-blue-700 disabled:opacity-60 text-sm">
                  {secondPaySaving ? 'Saving…' : '✓ Confirm Job Placed + ₹2,999'}
                </button>
                <button type="button" onClick={() => { setSecondPayModal(false); setSecondPayRow(null); setSecondPayFeedback(''); }}
                  className="rounded-full border border-gray-300 bg-white px-4 py-2.5 font-bold text-gray-700 hover:bg-gray-50 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Interview Coordination Modal */}
      {interviewCoordModalOpen && interviewCoordEditRow && (() => {
        const row = interviewCoordEditRow;
        const isSelected = row.interview_status === 'Selected';
        const isRejected = row.interview_status === 'Rejected';
        const isResulted = isSelected || isRejected;
        const offerSentToHR = Boolean(row.offer_sent_to_hr);
        const stepADone = Boolean(row.dani_offer_sent);
        const stepBDone = Boolean(row.member_offer_response);
        const stepBAccepted = row.member_offer_response === 'Accepted';
        const stepCDone = Number(row.payment_received || 0) > 0;
        const stepDDone = Number(row.company_offer_sent || 0) > 0;
        const waDigits = String(row.whatsapp_number || row.contact_number || '').replace(/\D/g, '');
        const waPhone = waDigits.length === 10 ? `91${waDigits}` : waDigits;
        const scWaMsg = [
          `Dear ${row.name || 'Candidate'},`,
          ``,
          `Congratulations on your selection${row.selected_job_title ? ` for ${row.selected_job_title}${row.selected_job_company ? ` at ${row.selected_job_company}` : ''}` : ''}!`,
          ``,
          `To complete your placement, please pay the service charge:`,
          `💰 Service Charge: ${row.service_charge_amount}`,
          ``,
          `🔷 UPI: lwin123@fbl`,
          `🏦 Account Name: Dani Job Club | Account No: 1256955555`,
          `    IFSC: FDRL00025 | Bank: Federal Bank, Kulappully`,
          ``,
          `Please send a payment screenshot to confirm. – Dani Job Club`,
        ].join('\n');
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-[28px] bg-white shadow-2xl">
              {/* Header */}
              <div className={`flex items-center justify-between rounded-t-[28px] px-6 py-4 ${isSelected ? 'bg-gradient-to-r from-emerald-700 to-teal-600' : isRejected ? 'bg-gradient-to-r from-red-700 to-red-600' : 'bg-black'}`}>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-300">
                    {isSelected ? 'Placement Steps' : isRejected ? 'Interview Result' : 'Interview Coordination'}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    <p className="text-lg font-black text-white">{row.name}</p>
                    <PartnerTag row={row} />
                  </div>
                  <p className="text-xs text-white/60">{row.selected_job_title || 'No vacancy selected'}{row.selected_job_company ? ` · ${row.selected_job_company}` : ''}</p>
                </div>
                <button type="button" onClick={() => { setInterviewCoordModalOpen(false); setInterviewCoordEditRow(null); setInterviewCoordinationFeedback(''); setPlacementStepsFeedback(''); }} className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20">✕</button>
              </div>

              <div className="p-5 space-y-4">
                {/* Feedback */}
                {(interviewCoordinationFeedback || placementStepsFeedback) && (
                  <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${(interviewCoordinationFeedback || placementStepsFeedback).toLowerCase().includes('fail') ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                    {interviewCoordinationFeedback || placementStepsFeedback}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════
                    MODE A: PRE-RESULT — interview coordination form
                    ═══════════════════════════════════════════════════════ */}
                {!isResulted && (
                  <>
                    <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                      <span className="text-xl">📋</span>
                      <div>
                        <p className="text-sm font-extrabold text-amber-900">Interview Status: {row.interview_status || 'Not Scheduled'}</p>
                        <p className="text-xs text-amber-700 mt-0.5">Recruiter will update the result after the interview. Your task: confirm the candidate's availability below.</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <label className="text-sm font-semibold text-gray-700">Candidate Availability
                        <select name="candidate_availability" value={interviewCoordinationForm.candidate_availability} onChange={handleInterviewCoordinationFieldChange} className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400">
                          {availabilityStatusOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-gray-700">HR Confirmation
                        <select name="hr_assistant_confirmation" value={interviewCoordinationForm.hr_assistant_confirmation} onChange={handleInterviewCoordinationFieldChange} className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400">
                          {interviewConfirmationOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </label>
                      <label className="text-sm font-semibold text-gray-700 md:col-span-2">Availability Note
                        <textarea name="candidate_availability_note" value={interviewCoordinationForm.candidate_availability_note} onChange={handleInterviewCoordinationFieldChange} rows={2} className="mt-1 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                      </label>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {[['Company Availability', row.vacancy_availability], ['Company Confirmation', row.assistant_recruiter_confirmation], ['Interview Date', row.interview_date], ['Interview Time', row.interview_time], ['Location', row.interview_location], ['Reporting Officer', row.reporting_officer]].filter(([, v]) => v).map(([label, val]) => (
                        <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
                          <p className="font-bold text-gray-400">{label}</p>
                          <p className="font-semibold text-gray-800">{val}</p>
                        </div>
                      ))}
                    </div>

                    {row.is_shortlisted > 0 && (
                      <div className="rounded-[16px] border border-green-200 bg-green-50/60 p-3">
                        <p className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-green-800">WhatsApp Messages</p>
                        <div className="flex flex-col gap-2">
                          <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Dear ${row.name || 'Candidate'}, you have been shortlisted for ${row.selected_job_title || '[Vacancy]'} at ${row.selected_job_company || '[Company]'}. Kindly confirm your availability. – Dani Job Club`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl border border-green-300 bg-white px-3 py-2 text-xs font-bold text-green-800 hover:bg-green-100">📋 Send Shortlist Notification</a>
                          {row.interview_date && <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(`Dear ${row.name || 'Candidate'}, your interview is scheduled on ${row.interview_date} at ${row.interview_time || 'TBD'}. Venue: ${row.interview_location || 'TBD'}. Reporting Officer: ${row.reporting_officer || 'TBD'}. Please be on time. – Dani Job Club`)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl border border-green-300 bg-white px-3 py-2 text-xs font-bold text-green-800 hover:bg-green-100">📅 Send Interview Schedule</a>}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ═══════════════════════════════════════════════════════
                    MODE B: REJECTED
                    ═══════════════════════════════════════════════════════ */}
                {isRejected && (
                  <div className="rounded-[16px] border border-red-200 bg-red-50 p-6 text-center">
                    <p className="text-3xl mb-2">❌</p>
                    <p className="font-black text-red-800 text-base">Candidate was not selected</p>
                    {row.interview_note && <p className="text-sm text-red-600 mt-2">"{row.interview_note}"</p>}
                  </div>
                )}

                {/* ═══════════════════════════════════════════════════════
                    MODE C: SELECTED — clean step-by-step placement tracker
                    ═══════════════════════════════════════════════════════ */}
                {isSelected && (
                  <div className="space-y-2">

                    {/* Offer terms info panel */}
                    {row.offer_sent_to_hr ? (
                      <div className="rounded-[16px] border border-emerald-200 bg-emerald-50 p-4">
                        <p className="mb-2 text-sm font-extrabold text-emerald-900">📋 Offer Terms from Recruiter</p>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {row.offered_job_title && <p><span className="font-bold text-gray-500">Position:</span> {row.offered_job_title}</p>}
                          {row.offered_salary && <p><span className="font-bold text-gray-500">Salary:</span> <span className="font-bold text-emerald-700">{row.offered_salary}</span></p>}
                          {row.joining_date && <p><span className="font-bold text-gray-500">Joining:</span> {row.joining_date}</p>}
                          {row.work_location && <p><span className="font-bold text-gray-500">Location:</span> {row.work_location}</p>}
                          {row.probation_period && <p><span className="font-bold text-gray-500">Probation:</span> {row.probation_period}</p>}
                          {row.work_schedule && <p><span className="font-bold text-gray-500">Schedule:</span> {row.work_schedule}</p>}
                          {row.leave_days && <p><span className="font-bold text-gray-500">Leave Days:</span> {row.leave_days}</p>}
                          {row.other_terms && <p className="col-span-2"><span className="font-bold text-gray-500">Other:</span> {row.other_terms}</p>}
                          {row.service_charge_amount && <p className="col-span-2"><span className="font-bold text-gray-500">Service Charge:</span> <span className="font-bold text-amber-700">{row.service_charge_amount}</span></p>}
                        </div>
                        {waPhone && (
                          <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent([`Dear ${row.name || 'Candidate'},`, ``, `🎉 Congratulations! You have been selected.`, row.offered_job_title ? `📌 Position: ${row.offered_job_title}` : null, row.offered_salary ? `💰 Salary: ${row.offered_salary}` : null, row.joining_date ? `📅 Joining: ${row.joining_date}` : null, row.work_location ? `📍 Location: ${row.work_location}` : null, ``, `Please confirm your acceptance at the earliest. – Dani Job Club`].filter(Boolean).join('\n'))}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-xl border border-green-400 bg-white px-3 py-2 text-xs font-bold text-green-800 hover:bg-green-50">💬 Send Offer Details via WhatsApp</a>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-[16px] border border-amber-200 bg-amber-50 p-3">
                        <p className="text-xs font-extrabold text-amber-800">⏳ Waiting for Recruiter to send offer terms to HR</p>
                        <p className="text-xs text-amber-600 mt-0.5">Recruiter must enter offer details and click "Send to HR" before you can proceed.</p>
                      </div>
                    )}

                    {/* ── STEP 1: Send Offer Letter (locked until recruiter sends offer to HR) ── */}
                    {!offerSentToHR
                      ? <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 opacity-40 select-none">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300 text-xs font-black text-gray-500">🔒</span>
                          <div>
                            <p className="text-sm font-extrabold text-gray-400">Send Dani Offer Letter</p>
                            <p className="text-xs text-gray-400">Recruiter must send offer terms to HR first</p>
                          </div>
                        </div>
                      : <div className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition ${stepADone ? 'border-emerald-200 bg-emerald-50' : 'border-indigo-300 bg-white shadow-sm ring-2 ring-indigo-200'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${stepADone ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white'}`}>{stepADone ? '✓' : '1'}</span>
                            <div>
                              <p className={`text-sm font-extrabold ${stepADone ? 'text-emerald-800' : 'text-indigo-900'}`}>Send Dani Offer Letter</p>
                              <p className="text-xs text-gray-500">Send the Dani Job Club offer letter to the member</p>
                            </div>
                          </div>
                          {stepADone
                            ? <span className="shrink-0 rounded-full bg-emerald-100 px-3 py-1 text-xs font-extrabold text-emerald-700">✓ Sent</span>
                            : <button type="button" disabled={placementStepsSaving} onClick={() => handleSavePlacementSteps({ dani_offer_sent: true, member_offer_response: '', payment_received: 0, company_offer_sent: row.company_offer_sent || 0 })} className="shrink-0 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-extrabold text-white hover:bg-indigo-700 disabled:opacity-60">Mark Sent →</button>
                          }
                        </div>
                    }

                    {/* ── STEP 2: Member Response (locked until Step 1) ── */}
                    {!stepADone
                      ? <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 opacity-40 select-none"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300 text-xs font-black text-gray-500">🔒</span><div><p className="text-sm font-extrabold text-gray-400">Member Offer Response</p><p className="text-xs text-gray-400">Complete Step 1 first</p></div></div>
                      : <div className={`rounded-2xl border px-4 py-3 transition ${stepBDone ? 'border-emerald-200 bg-emerald-50' : 'border-indigo-300 bg-white shadow-sm ring-2 ring-indigo-200'}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${stepBDone ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white'}`}>{stepBDone ? '✓' : '2'}</span>
                            <div>
                              <p className={`text-sm font-extrabold ${stepBDone ? 'text-emerald-800' : 'text-indigo-900'}`}>Member Offer Response</p>
                              <p className="text-xs text-gray-500">Record the member's response after they receive the offer letter</p>
                            </div>
                          </div>
                          <div className="flex gap-3 pl-11">
                            {['Accepted', 'Rejected'].map((opt) => (
                              <button key={opt} type="button" disabled={placementStepsSaving}
                                onClick={() => handleSavePlacementSteps({ dani_offer_sent: true, member_offer_response: opt, payment_received: row.payment_received || 0, company_offer_sent: row.company_offer_sent || 0 })}
                                className={`rounded-xl px-5 py-2 text-sm font-extrabold transition disabled:opacity-60 ${row.member_offer_response === opt ? (opt === 'Accepted' ? 'bg-emerald-500 text-white shadow-md' : 'bg-red-500 text-white shadow-md') : 'border-2 border-gray-200 bg-white text-gray-600 hover:border-indigo-400 hover:text-indigo-700'}`}>
                                {opt === 'Accepted' ? '✅ Accepted' : '❌ Rejected'}
                              </button>
                            ))}
                          </div>
                        </div>
                    }

                    {/* ── STEP 3: Collect Service Charge (locked until Accepted) ── */}
                    {!stepBAccepted
                      ? <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 opacity-40 select-none"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300 text-xs font-black text-gray-500">🔒</span><div><p className="text-sm font-extrabold text-gray-400">Collect Service Charge</p><p className="text-xs text-gray-400">Available after member accepts</p></div></div>
                      : <div className={`rounded-2xl border px-4 py-3 transition ${stepCDone ? 'border-emerald-200 bg-emerald-50' : 'border-indigo-300 bg-white shadow-sm ring-2 ring-indigo-200'}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${stepCDone ? 'bg-emerald-500 text-white' : 'bg-indigo-600 text-white'}`}>{stepCDone ? '✓' : '3'}</span>
                            <div>
                              <p className={`text-sm font-extrabold ${stepCDone ? 'text-emerald-800' : 'text-indigo-900'}`}>Collect Service Charge</p>
                              <p className="text-xs text-gray-500">Request payment from member, then mark once received</p>
                            </div>
                          </div>
                          {!stepCDone && (
                            <div className="pl-11 space-y-2">
                              {row.service_charge_amount
                                ? <div className="flex items-center gap-3">
                                    <div className="rounded-xl border-2 border-blue-300 bg-blue-50 px-4 py-2"><p className="text-[10px] font-black uppercase text-blue-500">Amount</p><p className="text-lg font-black text-blue-900">{row.service_charge_amount}</p></div>
                                    {waPhone && <a href={`https://wa.me/${waPhone}?text=${encodeURIComponent(scWaMsg)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-xl border border-green-400 bg-white px-3 py-2 text-xs font-bold text-green-800 hover:bg-green-50">💬 Request Payment via WhatsApp</a>}
                                  </div>
                                : <p className="text-xs font-semibold text-amber-700">⚠ Recruiter has not set the service charge amount yet</p>
                              }
                            </div>
                          )}
                          <div className="flex justify-end mt-2">
                            {stepCDone
                              ? <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-extrabold text-emerald-700">✓ Payment Received</span>
                              : <button type="button" disabled={placementStepsSaving} onClick={() => handleSavePlacementSteps({ dani_offer_sent: true, member_offer_response: 'Accepted', payment_received: true, company_offer_sent: row.company_offer_sent || 0 })} className="rounded-xl bg-indigo-600 px-4 py-2 text-xs font-extrabold text-white hover:bg-indigo-700 disabled:opacity-60">✓ Mark Payment Received</button>
                            }
                          </div>
                        </div>
                    }

                    {/* ── STEP 4: Company Offer Letter (locked until payment) ── */}
                    {!stepCDone
                      ? <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 opacity-40 select-none"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-300 text-xs font-black text-gray-500">🔒</span><div><p className="text-sm font-extrabold text-gray-400">Send Company Offer Letter</p><p className="text-xs text-gray-400">Available after payment is received</p></div></div>
                      : <div className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 transition ${stepDDone ? 'border-emerald-300 bg-emerald-100' : 'border-indigo-300 bg-white shadow-sm ring-2 ring-indigo-200'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${stepDDone ? 'bg-emerald-600 text-white' : 'bg-indigo-600 text-white'}`}>{stepDDone ? '✓' : '4'}</span>
                            <div>
                              <p className={`text-sm font-extrabold ${stepDDone ? 'text-emerald-900' : 'text-indigo-900'}`}>Send Company Offer Letter</p>
                              <p className="text-xs text-gray-500">Send the original company offer letter to complete placement</p>
                            </div>
                          </div>
                          {stepDDone
                            ? <span className="shrink-0 rounded-full bg-emerald-600 px-3 py-1 text-xs font-extrabold text-white">🎉 Placement Complete</span>
                            : <button type="button" disabled={placementStepsSaving} onClick={() => handleSavePlacementSteps({ dani_offer_sent: true, member_offer_response: 'Accepted', payment_received: true, company_offer_sent: true })} className="shrink-0 rounded-xl bg-emerald-600 px-4 py-2 text-xs font-extrabold text-white hover:bg-emerald-700 disabled:opacity-60">Complete Placement →</button>
                          }
                        </div>
                    }

                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex gap-3 px-5 pb-5">
                {!isResulted && (
                  <button type="button" onClick={handleSaveInterviewCoordination} disabled={interviewCoordinationSaving} className="flex-1 rounded-full bg-black px-5 py-2.5 font-bold text-yellow-300 hover:bg-gray-900 disabled:cursor-not-allowed disabled:bg-gray-500">
                    {interviewCoordinationSaving ? 'Saving…' : 'Save Confirmation'}
                  </button>
                )}
                <button type="button" onClick={() => { setInterviewCoordModalOpen(false); setInterviewCoordEditRow(null); setInterviewCoordinationFeedback(''); setPlacementStepsFeedback(''); }} className={`rounded-full bg-gray-100 px-5 py-2.5 font-bold text-gray-700 hover:bg-gray-200 ${isResulted ? 'flex-1' : ''}`}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Call Later — Active Alarm Notification */}
      {recruiterModal && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-[28px] bg-white shadow-2xl overflow-hidden">
            <div className="bg-black px-6 py-5 flex items-center gap-3">
              <span className="text-2xl">👤</span>
              <div>
                <p className="font-black text-white text-base">Forward to Recruiter</p>
                <p className="text-yellow-300 text-xs mt-0.5 truncate max-w-xs">{recruiterModal.row.name}</p>
              </div>
            </div>
            <div className="p-6">
              {recruiterListLoading ? (
                <p className="text-center text-sm text-gray-500 py-4">Loading recruiters...</p>
              ) : recruiterList.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-4">No recruiters found. Create a Recruiter staff account first.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recruiterList.map((r) => (
                    <button
                      key={r.user_id}
                      type="button"
                      onClick={() => setSelectedRecruiterId(r.user_id)}
                      className={`w-full text-left rounded-2xl border px-4 py-3 transition ${
                        selectedRecruiterId === r.user_id
                          ? 'border-black bg-gray-900 text-white ring-2 ring-black'
                          : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                    >
                      <p className="font-black text-sm">{r.name}</p>
                      <p className={`text-xs mt-0.5 ${selectedRecruiterId === r.user_id ? 'text-gray-300' : 'text-gray-500'}`}>{r.user_id}</p>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={() => { setRecruiterModal(null); setSelectedRecruiterId(''); }}
                  disabled={recruiterForwarding}
                  className="flex-1 rounded-2xl border border-gray-300 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRecruiterModalConfirm}
                  disabled={!selectedRecruiterId || recruiterForwarding || recruiterListLoading}
                  className="flex-1 rounded-2xl bg-black px-4 py-2.5 text-sm font-extrabold text-yellow-400 hover:bg-gray-900 disabled:opacity-50"
                >
                  {recruiterForwarding ? 'Forwarding...' : 'Forward'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {partnerModal && (
        <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-[28px] bg-white shadow-2xl overflow-hidden">
            <div className="bg-black px-6 py-5 flex items-center gap-3">
              <span className="text-2xl">🤝</span>
              <div>
                <p className="font-black text-white text-base">Select Partner</p>
                <p className="text-yellow-300 text-xs mt-0.5">Tag this member as referred by a registered partner</p>
              </div>
            </div>
            <div className="p-6">
              {partnerListLoading ? (
                <p className="text-center text-sm text-gray-500 py-4">Loading partners...</p>
              ) : partnerList.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-4">No approved partners found.</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {partnerList.map((p) => (
                    <button
                      key={p.partner_user_id}
                      type="button"
                      onClick={() => setSelectedPartnerId(p.partner_user_id)}
                      className={`w-full text-left rounded-2xl border px-4 py-3 transition ${
                        selectedPartnerId === p.partner_user_id
                          ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-300'
                          : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50/50'
                      }`}
                    >
                      <p className="font-black text-sm text-gray-900">{p.name_of_organisation || p.partner_user_id}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Owner: {p.owner_of_organisation || '—'} · Commission: {p.commission_percentage || '—'}%</p>
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-5 flex gap-3">
                <button
                  type="button"
                  onClick={() => { setPartnerModal(null); setSelectedPartnerId(''); }}
                  className="flex-1 rounded-2xl border border-gray-300 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePartnerModalConfirm}
                  disabled={!selectedPartnerId || partnerListLoading}
                  className="flex-1 rounded-2xl bg-yellow-400 px-4 py-2.5 text-sm font-extrabold text-black hover:bg-yellow-300 disabled:opacity-50"
                >
                  Confirm Partner
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeAlarm && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center pt-16 bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-[24px] bg-white shadow-2xl overflow-hidden animate-bounce">
            <div className="bg-amber-500 px-6 py-4 flex items-center gap-3">
              <span className="text-2xl">🔔</span>
              <div>
                <p className="font-black text-white text-base">Call Later Reminder!</p>
                <p className="text-amber-100 text-xs mt-0.5">Scheduled call time reached</p>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-lg font-black text-gray-900">{activeAlarm.candidateName}</p>
              <p className="text-sm text-gray-500 mt-1">
                Scheduled: {fmtDateTime(activeAlarm.scheduledAt)}
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => { playAlarmSound(); }}
                  className="flex-1 rounded-full border border-amber-400 px-4 py-2 text-sm font-bold text-amber-700 hover:bg-amber-50"
                >
                  🔊 Replay Sound
                </button>
                <button
                  type="button"
                  onClick={() => dismissCallLaterAlarm(activeAlarm.forwardId)}
                  className="flex-1 rounded-full bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-600"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StaffDashboard = () => {
  const rawUser = localStorage.getItem('user');
  const cachedUser = rawUser ? JSON.parse(rawUser) : null;
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const [liveRole, setLiveRole] = React.useState(null);
  const [liveName, setLiveName] = React.useState(null);
  const [roleLoading, setRoleLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setRoleLoading(false); return; }
    axios.get(`${API_URL}/api/staff/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setLiveRole(res.data.role || 'Staff');
        setLiveName(res.data.name);
        const updated = { ...(cachedUser || {}), role: res.data.role, name: res.data.name };
        localStorage.setItem('user', JSON.stringify(updated));
      })
      .catch(() => {
        setLiveRole(cachedUser?.role || 'Staff');
        setLiveName(cachedUser?.name || 'Staff User');
      })
      .finally(() => setRoleLoading(false));
  }, [API_URL]); // cachedUser is intentionally excluded — it's a snapshot from mount

  if (roleLoading) {
    return <div className="flex min-h-screen items-center justify-center text-gray-400 text-sm">Loading...</div>;
  }

  const role = liveRole || cachedUser?.role || 'Staff';
  const name = liveName || cachedUser?.name || 'Staff User';
  const summary = roleSummaries[role] || roleSummaries.Staff;

  let dashboard;
  if (role === 'Data Analyst') {
    dashboard = <DataAnalystDashboard name={name} summary={summary} API_URL={API_URL} />;
  } else if (role === 'Partnership Executive') {
    dashboard = <PartnershipExecutiveDashboard name={name} API_URL={API_URL} />;
  } else if (role === 'Assistant Recruiter') {
    dashboard = <AssistantRecruiterDashboard name={name} summary={summary} API_URL={API_URL} />;
  } else if (role === 'Recruiter') {
    dashboard = <RecruiterDashboard name={name} summary={summary} API_URL={API_URL} />;
  } else if (role === 'Agent') {
    dashboard = <AgentDashboard name={name} API_URL={API_URL} />;
  } else if (role === 'Field Agent') {
    dashboard = <FieldAgentDashboard name={name} API_URL={API_URL} />;
  } else if (role === 'Telecaller') {
    dashboard = <TelecallerDashboard name={name} API_URL={API_URL} />;
  } else if (role === 'Accountant') {
    dashboard = <AccountantDashboard name={name} API_URL={API_URL} />;
  } else if (role?.toLowerCase() === 'marketing') {
    dashboard = <MarketingDashboard name={name} API_URL={API_URL} />;
  } else if (role === 'HR Executive') {
    dashboard = <HRExecutiveDashboard name={name} API_URL={API_URL} />;
  } else {
    dashboard = <LegacyStaffDashboard name={name} role={role} summary={summary} API_URL={API_URL} />;
  }

  return (
    <>
      {dashboard}
      <StaffChat />
    </>
  );
};

export default StaffDashboard;
